import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useContext, useMemo } from 'react';
import { FaLeaf, FaSeedling, FaChevronLeft, FaBars, FaMapMarkedAlt, FaDrawPolygon, FaLayerGroup, FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa';
import { TbDrone } from 'react-icons/tb';
import { GiCow } from 'react-icons/gi';
import { ThemeContext } from '../App';
import { useUserEvent } from '../hooks';
import {
  MapContainer, TileLayer, Polygon, Polyline, Circle, CircleMarker, Marker,
  useMapEvents, useMap, LayersControl, ImageOverlay, Tooltip, Popup,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const verticeIcon = L.divIcon({
  className: 'sat-vertice-icon',
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#ff6d00;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});
const verticeAderidoIcon = L.divIcon({
  className: 'sat-vertice-icon sat-vertice-icon--snapped',
  html: '<div></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});
const verticeSelecionadoIcon = L.divIcon({
  className: 'sat-vertice-icon sat-vertice-icon--selected',
  html: '<div></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});
const verticeIntermediarioIcon = L.divIcon({
  className: 'sat-midpoint-icon',
  html: '<div><span>+</span></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});
const sugestaoAderenciaIcon = L.divIcon({
  className: 'sat-snap-suggestion-icon',
  html: '<div><span></span></div>',
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});
const activityLabelAnchorIcon = L.divIcon({
  className: 'remote-activity-label-anchor',
  html: '',
  iconSize: [0, 0],
  iconAnchor: [0, 0],
});
import { supabase } from '../supabaseClient';
import {
  buscarCenas, getTileUrl, getStacSelfUrl, getPreviewImage,
  poligonoParaBbox, geojsonParaLeaflet, leafletParaGeoJson,
  calcularEstatisticasNDVI, calcularEvolucaoNdvi, encontrarCenaAnterior,
  gerarMapaNdviDemonstrativo, gerarMapaNdviRecortado,
  gerarMapaZonasManejo, CORES_ZONA_MANEJO,
} from './stacService';
import { analisarTerreno, FONTES_TERRENO, FONTE_TERRENO_PADRAO } from './terrainService';
import { obterNiveisCurvasMestras, selecionarRotulosCurvas } from './terrainContourLabels';
import {
  carregarLogoRelatorio, criarCabecalhoRelatorio, criarRelatorioNdvi,
  criarRelatorioTerreno, salvarRelatorioPdf,
} from './sensoriamentoReports';
import { calcularChuvaUltimas24Horas } from './sensorRainfall';
import {
  calcularAreaHectares,
  calcularCentroGeometria,
  formatarAreaHectares,
  normalizarTexto,
  normalizarUf,
} from './territorialGeometry';
import {
  criarProtecaoCliqueAposArraste,
  criarPontosIntermediarios,
  expandirPoligonoAderente,
  expandirTrechoAderente,
  encontrarAderenciaNoMapa,
  extrairAnelLatLng,
  inserirVertice,
  permiteAdicionarPontoNoMapa,
  pontoComAderencia,
  validarGeometriaTalhao,
} from './territorialSnapping';
import { useAddonSatelite } from '../hooks/useAddonSatelite';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { useMunicipiosAutocomplete } from '../hooks/useMunicipios';
import { fitMapBoundsSafely, setMapViewSafely, stopMapMotion } from '../utils/leafletMapSafety';
import { sensorWeatherStatus } from './sensorWeatherStatus';
import { activityLabelPosition, activityLabelScale } from './activityLabelPosition';
import useCompactMapLayout from './useCompactMapLayout';
import AddonSateliteModal from '../components/AddonSateliteModal';
import GestaoCultivoModal from '../components/GestaoCultivoModal';
import CameraStreamPlayer from '../components/CameraStreamPlayer';
import {
  aplicarContextoCultivoAoTalhao,
  buscarContextosCultivo,
  contextoCultivoLegado,
} from '../services/ciclosCultivoService';
import { loadVisibleAuthorizedProperties } from '../services/authorizedPropertiesService';
import { propertyActionAllowed } from '../utils/collaborationAccess';
import { operationalPhysicalPlotToCompatibleLegacyPlot } from '../services/operationalPropertiesService';
import { updateOperationalPlot, deleteOperationalPlot } from '../services/plotMigrationReviewService';
import { deleteEmptyLegacyPlot } from '../services/legacyPlotDeletionService';
import { motivoBloqueioExclusaoTalhao } from '../services/plotDeletionRules';
import './SensoriamentoRemoto.css';
import usePecuariaMap from '../pecuaria/usePecuariaMap';
import { formatNumber, PASTURE_STATES } from '../pecuaria/pecuariaDomain';
import { MapeamentoDroneLayer, MapeamentoDronePanel } from './MapeamentoDrone';
import { droneMappingAvailable } from '../services/droneMappingService';
import InmetStationsLayer from './InmetStations';

const CENTRO_BRASIL = [-15.78, -47.93];
const SNAP_DRAG_TOLERANCE_PX = 32;

function criarIconeRotuloCurva(rotulo) {
  const nivel = Number.isInteger(rotulo.nivel) ? rotulo.nivel : rotulo.nivel.toFixed(1);
  return L.divIcon({
    className: 'terrain-contour-label-wrapper',
    html: `<span style="transform:rotate(${rotulo.angle.toFixed(1)}deg)">${nivel} m</span>`,
    iconSize: [62, 24],
    iconAnchor: [31, 12],
  });
}
// A Central grava por padrão no fluxo autenticado. A variável continua existindo
// como chave de emergência: use "false" para bloquear novas gravações sem
// precisar publicar uma alteração de código. A prévia permanece sempre local.
const TERRITORIAL_WRITES_ENABLED = process.env.REACT_APP_TERRITORIAL_ENABLE_WRITES !== 'false';
const hoje = () => new Date().toISOString().split('T')[0];
const diasAtras = (n) => new Date(Date.now() - n * 86400000).toISOString().split('T')[0];

function formatarDataCena(feature) {
  const data = new Date(feature?.properties?.datetime || '');
  if (Number.isNaN(data.getTime())) return 'Data indisponível';
  return data.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatarNumeroComSinal(valor, casas = 1) {
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return '—';
  return `${numero >= 0 ? '+' : ''}${numero.toFixed(casas)}`;
}

function useIsMobile(breakpoint = 720) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [breakpoint]);
  return isMobile;
}

function geometriaParaPosicoes(geometria) {
  try {
    if (!geometria?.geometry?.coordinates?.[0]?.length) return null;
    return geojsonParaLeaflet(geometria);
  } catch {
    return null;
  }
}

function parseCoordinateSearch(value) {
  const match = String(value || '').trim().match(/^(-?\d{1,2}(?:[.,]\d+)?)\s*[,;]\s*(-?\d{1,3}(?:[.,]\d+)?)$/);
  if (!match) return null;
  const lat = Number(match[1].replace(',', '.'));
  const lng = Number(match[2].replace(',', '.'));
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

function calcularDistanciaMedicao(pontos = []) {
  return pontos.reduce((total, ponto, index) => {
    if (index === 0) return 0;
    const anterior = pontos[index - 1];
    return total + L.latLng(anterior.lat, anterior.lng).distanceTo(L.latLng(ponto.lat, ponto.lng));
  }, 0);
}

function formatarDistanciaMedicao(metros) {
  const valor = Number(metros);
  if (!Number.isFinite(valor) || valor <= 0) return '0 m';
  if (valor >= 1000) return `${(valor / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} km`;
  return `${valor.toLocaleString('pt-BR', { maximumFractionDigits: valor < 10 ? 1 : 0 })} m`;
}

function formatarAreaMedicao(hectares) {
  const valor = Number(hectares);
  if (!Number.isFinite(valor) || valor <= 0) return '0 m²';
  if (valor < 1) return `${(valor * 10000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} m²`;
  return `${valor.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha`;
}

const SENSOR_DATA_TABLES = {
  meteorologico: 'dados_sensores_meteorologicos',
  umidade_solo: 'dados_sensores_solo',
  chuva_presenca: 'dados_sensores_chuva',
};

const SENSOR_TYPE_INFO = {
  meteorologico: { label: 'Estação meteorológica', icon: '🌦️' },
  umidade_solo: { label: 'Sensor de solo', icon: '🌱' },
  chuva_presenca: { label: 'Chuva / irrigação', icon: '💧' },
  camera: { label: 'Câmera de campo', icon: '📷' },
};

function sensorCoordinates(sensor) {
  const lat = Number(sensor?.latitude ?? sensor?.lat);
  const lng = Number(sensor?.longitude ?? sensor?.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

function sensorReadingTimestamp(reading) {
  const parsed = new Date(reading?.timestamp || reading?.data_local || reading?.created_at || '');
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function sensorConnection(sensor) {
  const timestamp = sensor?.tipo_sensor === 'camera'
    ? sensorReadingTimestamp({ timestamp: sensor.camera_last_seen_at })
    : sensorReadingTimestamp(sensor?.latestReading);
  if (sensor?.tipo_sensor === 'camera' && !timestamp) {
    const configured = Boolean(sensor.stream_url || process.env.REACT_APP_CAMERA_STREAM_BASE_URL);
    return configured
      ? { tone: 'attention', label: 'Configurada', detail: 'Transmissão sob demanda' }
      : { tone: 'offline', label: 'Sem gateway', detail: 'Configure a URL HLS' };
  }
  if (!timestamp) return { tone: 'offline', label: 'Sem leitura recente', detail: 'Aguardando comunicação' };
  const minutes = Math.max(0, Math.round((Date.now() - timestamp.getTime()) / 60000));
  if (minutes <= 60) return { tone: 'online', label: 'Online', detail: minutes < 2 ? 'Atualizada agora' : `Atualizada há ${minutes} min` };
  if (minutes <= 360) return { tone: 'attention', label: 'Atenção', detail: `Última leitura há ${Math.round(minutes / 60)} h` };
  return { tone: 'offline', label: 'Sem comunicação', detail: timestamp.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) };
}

function makeSensorMapIcon(sensor, zoom = 15) {
  const type = SENSOR_TYPE_INFO[sensor?.tipo_sensor] || SENSOR_TYPE_INFO.meteorologico;
  const connection = sensorConnection(sensor);
  const weather = sensor?.tipo_sensor === 'meteorologico' ? sensorWeatherStatus(sensor) : null;
  const icon = weather?.icon || type.icon;
  const size = zoom <= 7 ? 24 : zoom <= 11 ? 32 : 44;
  const bodySize = size - 2;
  return L.divIcon({
    className: `remote-sensor-marker ${zoom <= 7 ? 'is-compact' : zoom <= 11 ? 'is-medium' : ''}`,
    html: `<span class="remote-sensor-marker__body" style="--sensor-marker-size:${bodySize}px"><b>${icon}</b><i class="is-${connection.tone}"></i></span>`,
    iconSize: [size, size + 6],
    iconAnchor: [size / 2, size + 2],
  });
}

function makeSensorClusterIcon(count, zoom = 5) {
  const size = zoom <= 7 ? 30 : 36;
  return L.divIcon({
    className: 'remote-sensor-cluster-marker',
    html: `<span>${count}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function applyMarkerAccessibleName(event, name) {
  const marker = event?.target;
  const updateElement = () => {
    const element = marker?.getElement?.() || marker?._icon;
    if (!element) return;
    element.setAttribute('aria-label', name);
    element.setAttribute('title', name);
  };
  updateElement();
  window.setTimeout(updateElement, 0);
}

function ActivityLabelContent({ indicator, clearance, compact }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const element = ref.current;
    setScale(activityLabelScale(element.offsetWidth, element.offsetHeight, clearance));
  }, [clearance, compact, indicator.label, indicator.detail, indicator.type]);
  const Icon = indicator.type === 'livestock' ? GiCow : indicator.type === 'pasture' ? FaLeaf : FaSeedling;
  const statusLabel = indicator.type === 'crop' ? null : PASTURE_STATES[indicator.state]?.label;
  return <div ref={ref} aria-label={`${indicator.label}. ${indicator.detail}`} style={{ transform: `scale(${scale})` }}>
    <Icon aria-hidden="true" />
    <span><strong>{indicator.label}</strong><small>{indicator.detail}</small></span>
    {statusLabel && <i className="remote-activity-status" role="img" aria-label={`Situação: ${statusLabel}`} title={statusLabel} style={{ backgroundColor: indicator.color }} />}
  </div>;
}

function OperationalPlotIndicator({ plot, geometry, pecuariaMap, zoom, interactive = true, onClick }) {
  const map = useMap();
  const position = useMemo(() => activityLabelPosition(geometry, map, zoom), [geometry, map, zoom]);
  const indicator = pecuariaMap.getIndicator(plot);
  if (!indicator || !position) return null;
  return <Marker
    key={plot._physicalPlotId ?? plot.id}
    position={position.center}
    icon={activityLabelAnchorIcon}
    interactive={interactive}
    keyboard={interactive}
    title={`${plot.nome}: ${indicator.label}. ${indicator.detail}`}
    eventHandlers={interactive && onClick ? { click: onClick } : undefined}
  >
    <Tooltip permanent interactive={interactive} direction="center" className={`remote-operational-map-label ${zoom <= 11 ? 'is-compact' : ''}`}>
      <ActivityLabelContent indicator={indicator} clearance={position.clearance} compact={zoom <= 11} />
    </Tooltip>
  </Marker>;
}

function groupSensorsByZoom(sensors, zoom) {
  const cellSize = 34 / (2 ** Math.max(1, zoom));
  const groups = new Map();

  sensors.forEach(sensor => {
    const coordinates = sensorCoordinates(sensor);
    if (!coordinates) return;
    const key = `${Math.floor((coordinates.lat + 90) / cellSize)}:${Math.floor((coordinates.lng + 180) / cellSize)}`;
    const group = groups.get(key) || { sensors: [], latTotal: 0, lngTotal: 0 };
    group.sensors.push(sensor);
    group.latTotal += coordinates.lat;
    group.lngTotal += coordinates.lng;
    groups.set(key, group);
  });

  return Array.from(groups.values()).map(group => ({
    sensors: group.sensors,
    lat: group.latTotal / group.sensors.length,
    lng: group.lngTotal / group.sensors.length,
  }));
}

function formatSensorValue(value, suffix = '', decimals = 1) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? `${parsed.toFixed(decimals)}${suffix}` : '—';
}

/**
 * Classifica a variabilidade espacial do NDVI dentro do talhão em 3 zonas
 * (baixo/médio/alto vigor) a partir do histograma retornado pelo TiTiler,
 * usando média ± desvio padrão como limiares.
 */
function classificarVariabilidadeNdvi(stats) {
  const { mean, std, histogram } = stats;
  const [contagens, limites] = histogram || [[], []];
  const total = contagens.reduce((a, b) => a + b, 0) || 1;
  const limiarBaixo = mean - std;
  const limiarAlto = mean + std;

  let baixo = 0, medio = 0, alto = 0;
  contagens.forEach((qtd, i) => {
    const centro = (limites[i] + limites[i + 1]) / 2;
    if (centro < limiarBaixo) baixo += qtd;
    else if (centro > limiarAlto) alto += qtd;
    else medio += qtd;
  });

  const cv = mean !== 0 ? Math.abs(std / mean) * 100 : 0;
  const nivel = cv < 10 ? 'baixa' : cv < 20 ? 'moderada' : 'alta';

  return {
    baixoPct: (baixo / total) * 100,
    medioPct: (medio / total) * 100,
    altoPct: (alto / total) * 100,
    cv,
    nivel,
  };
}

// ---------------------------------------------------------------------------
// Componentes internos do mapa (devem estar dentro de <MapContainer>)
// ---------------------------------------------------------------------------

function CentrarMapa({ lat, lng, zoom = 15 }) {
  const map = useMap();
  const prev = useRef(null);
  useEffect(() => {
    const key = `${lat},${lng},${zoom}`;
    if (!lat || !lng || prev.current === key) return;
    prev.current = key;
    setMapViewSafely(map, [lat, lng], zoom);
    return () => stopMapMotion(map);
  }, [lat, lng, map, zoom]);
  return null;
}

function FitBounds({ positions }) {
  const map = useMap();
  const prev = useRef(null);
  useEffect(() => {
    if (!positions || positions.length < 2) return;
    const key = JSON.stringify(positions[0]);
    if (prev.current === key) return;
    prev.current = key;
    fitMapBoundsSafely(map, positions, { padding: [50, 50] });
    return () => stopMapMotion(map);
  }, [positions, map]);
  return null;
}

function FitPortfolioBounds({ positions, enabled }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (!enabled || fitted.current || !positions?.length) return;
    fitted.current = true;
    if (positions.length === 1) {
      setMapViewSafely(map, positions[0], 11);
      return () => stopMapMotion(map);
    }
    fitMapBoundsSafely(map, positions, { padding: [70, 70], maxZoom: 12 });
    return () => stopMapMotion(map);
  }, [enabled, positions, map]);
  return null;
}

function ColetorPontos({ ativo, onAdicionar, onSugestao, limitesAderencia = [], ignorarClique }) {
  const map = useMapEvents({
    mousemove(e) {
      if (!ativo) return;
      onSugestao?.(encontrarAderenciaNoMapa(e.latlng, limitesAderencia, map));
    },
    mouseout() {
      onSugestao?.(null);
    },
    click(e) {
      if (!ativo || (e.originalEvent?.button ?? 0) !== 0) return;
      if (ignorarClique?.()) {
        onSugestao?.(null);
        return;
      }
      const candidate = encontrarAderenciaNoMapa(e.latlng, limitesAderencia, map);
      onAdicionar(pontoComAderencia(candidate, e.latlng));
      onSugestao?.(null);
    },
  });

  useEffect(() => {
    if (!ativo) onSugestao?.(null);
    return () => onSugestao?.(null);
  }, [ativo, onSugestao]);
  return null;
}

function ColetorMedicao({ ativo, onAdicionar, onMover }) {
  const map = useMapEvents({
    mousemove(event) {
      if (!ativo) return;
      onMover?.({ lat: event.latlng.lat, lng: event.latlng.lng });
    },
    mouseout() {
      if (ativo) onMover?.(null);
    },
    click(event) {
      if (!ativo || (event.originalEvent?.button ?? 0) !== 0) return;
      L.DomEvent.stopPropagation(event.originalEvent);
      onAdicionar?.({ lat: event.latlng.lat, lng: event.latlng.lng });
    },
  });

  useEffect(() => {
    const container = map.getContainer();
    if (ativo) {
      map.doubleClickZoom.disable();
      container.classList.add('is-measuring');
    } else {
      container.classList.remove('is-measuring');
    }
    return () => {
      container.classList.remove('is-measuring');
      if (map.doubleClickZoom) map.doubleClickZoom.enable();
    };
  }, [ativo, map]);

  return null;
}

function VerticeDesenho({
  index,
  position,
  selecionado,
  limitesAderencia,
  onMover,
  onRemover,
  onSelecionar,
  onArrasteStart,
  onArrasteEnd,
}) {
  const map = useMap();
  const snapCandidateRef = useRef(null);
  const handleDragEnd = event => {
    try {
      const rawPosition = event.target.getLatLng();
      const candidate = snapCandidateRef.current
        || encontrarAderenciaNoMapa(rawPosition, limitesAderencia, map, SNAP_DRAG_TOLERANCE_PX);
      const finalPosition = pontoComAderencia(candidate, rawPosition);
      event.target.setLatLng(finalPosition);
      onMover(index, finalPosition);
      onSelecionar?.(index);
    } finally {
      snapCandidateRef.current = null;
      onArrasteEnd?.();
    }
  };

  return (
    <Marker
      position={[position.lat, position.lng]}
      icon={selecionado ? verticeSelecionadoIcon : position.snapSourceId ? verticeAderidoIcon : verticeIcon}
      draggable
      eventHandlers={{
        dragstart: () => {
          snapCandidateRef.current = null;
          onArrasteStart?.();
        },
        drag: event => {
          snapCandidateRef.current = encontrarAderenciaNoMapa(
            event.target.getLatLng(), limitesAderencia, map, SNAP_DRAG_TOLERANCE_PX
          );
        },
        dragend: handleDragEnd,
        click: event => {
          L.DomEvent.stopPropagation(event.originalEvent);
          onSelecionar?.(index);
        },
        dblclick: event => {
          L.DomEvent.stopPropagation(event.originalEvent);
          onRemover(index);
        },
        contextmenu: event => {
          L.DomEvent.stopPropagation(event.originalEvent);
          onRemover(index);
        },
      }}
      title={position.snapSourceId
        ? `Aderido a ${position.snapSourceName || 'uma borda existente'} · clique para selecionar ou arraste para ajustar`
        : 'Clique para selecionar · arraste para mover · duplo clique para excluir · aproxime de uma borda para encaixar'}
    />
  );
}

function VerticeIntermediario({ position, insertIndex, limitesAderencia, onAdicionar, onArrasteStart, onArrasteEnd }) {
  const map = useMap();
  const dragged = useRef(false);
  const add = rawPosition => {
    const candidate = encontrarAderenciaNoMapa(rawPosition, limitesAderencia, map);
    onAdicionar(insertIndex, pontoComAderencia(candidate, rawPosition));
  };

  return (
    <Marker
      position={[position.lat, position.lng]}
      icon={verticeIntermediarioIcon}
      draggable
      zIndexOffset={1200}
      eventHandlers={{
        dragstart: () => {
          dragged.current = true;
          onArrasteStart?.();
        },
        dragend: event => {
          add(event.target.getLatLng());
          onArrasteEnd?.();
        },
        click: event => {
          L.DomEvent.stopPropagation(event.originalEvent);
          if (dragged.current) {
            dragged.current = false;
            return;
          }
          add(position);
        },
      }}
      title="Adicionar vértice nesta aresta"
    />
  );
}

function MapViewObserver({ onChange }) {
  const map = useMapEvents({
    moveend() {
      const center = map.getCenter();
      onChange?.({ center: { lat: center.lat, lng: center.lng }, zoom: map.getZoom() });
    },
    zoomend() {
      const center = map.getCenter();
      onChange?.({ center: { lat: center.lat, lng: center.lng }, zoom: map.getZoom() });
    },
  });

  useEffect(() => {
    const center = map.getCenter();
    onChange?.({ center: { lat: center.lat, lng: center.lng }, zoom: map.getZoom() });
  }, [map, onChange]);
  return null;
}

function RemoteSensingOverview({ talhao, scopeType = 'talhao', insights, onOpenAnalysis, loading = false, error = '', scene = null }) {
  const isPropertyScope = scopeType === 'propriedade';
  const defaultScopeName = isPropertyScope ? 'Propriedade selecionada' : 'Talhão selecionado';
  const defaultScopeDetail = isPropertyScope ? 'Propriedade completa' : 'Cultura monitorada';
  if (!insights) {
    return (
      <section className="remote-overview is-pending" aria-label="Preparação dos indicadores agronômicos">
        <div className="remote-overview-lead">
          <div className="remote-overview-kicker">
            <span className="remote-live-dot" aria-hidden="true" />
            Imagem selecionada
          </div>
          <div className="remote-overview-title-row">
            <div>
              <h2>{talhao?.nome || defaultScopeName}</h2>
              <p>{talhao?.cultura || defaultScopeDetail} · {talhao?.area ? `${talhao.area} ha` : 'Área cadastrada'}</p>
            </div>
            <span className="remote-health-badge">{loading ? 'Calculando indicadores' : 'Análise pendente'}</span>
          </div>
        </div>

        <div className="remote-overview-metrics" aria-label="Indicadores em processamento">
          {['NDVI médio', 'Evolução', 'Área de atenção', 'Imagem'].map((label, index) => (
            <div key={label}>
              <span>{label}</span>
              <strong className={loading ? 'remote-metric-placeholder' : ''}>
                {index === 3 && scene ? formatarDataCena(scene).replace(/ de /g, ' ') : '—'}
              </strong>
              <small>{loading ? 'Processando dados reais…' : 'Aguardando processamento'}</small>
            </div>
          ))}
        </div>

        <div className="remote-recommendation">
          <div className="remote-recommendation-icon" aria-hidden="true">◎</div>
          <div>
            <span>Recomendação</span>
            <strong>{loading ? `Analisando o vigor ${isPropertyScope ? 'da propriedade' : 'do talhão'}` : 'Indicadores ainda não disponíveis'}</strong>
            <p>{error || 'O sistema está comparando a imagem selecionada com a cena anterior usando o mesmo polígono.'}</p>
          </div>
          <button type="button" onClick={onOpenAnalysis} disabled={loading}>
            {loading ? 'Processando análise…' : 'Ver detalhes da análise'}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </section>
    );
  }
  const attention = Number(insights.attentionPct || 0);
  const hasTemporalComparison = insights.changePct !== null
    && insights.changePct !== undefined
    && Number.isFinite(Number(insights.changePct));
  const temporalChange = hasTemporalComparison ? Number(insights.changePct) : null;
  const statusClass = attention >= 20 ? 'is-critical' : attention >= 10 ? 'needs-attention' : 'is-healthy';
  const statusLabel = attention >= 20 ? 'Vistoria prioritária' : attention >= 10 ? 'Atenção localizada' : 'Desenvolvimento uniforme';

  return (
    <section className={`remote-overview ${statusClass}`} aria-label={`Resumo agronômico ${isPropertyScope ? 'da propriedade' : 'do talhão'}`}>
      <div className="remote-overview-lead">
        <div className="remote-overview-kicker">
          <span className="remote-live-dot" aria-hidden="true" />
          Leitura mais recente
        </div>
        <div className="remote-overview-title-row">
          <div>
            <h2>{talhao?.nome || defaultScopeName}</h2>
            <p>{insights.culture || defaultScopeDetail} · {insights.area || 'Área cadastrada'}</p>
          </div>
          <span className="remote-health-badge">{statusLabel}</span>
        </div>
      </div>

      <div className="remote-overview-metrics">
        <div>
          <span>NDVI médio</span>
          <strong>{Number(insights.ndvi).toFixed(2)}</strong>
          <small>Vigor vegetativo</small>
        </div>
        <div>
          <span>Evolução</span>
          <strong className={hasTemporalComparison ? (temporalChange >= 0 ? 'is-positive' : 'is-negative') : ''}>
            {hasTemporalComparison ? `${formatarNumeroComSinal(temporalChange)}%` : '—'}
          </strong>
          <small title={insights.comparisonLabel || ''}>
            {insights.comparisonLabel || 'Sem imagem anterior analisada'}
          </small>
        </div>
        <div>
          <span>Área de atenção</span>
          <strong>{attention.toFixed(1)}%</strong>
          <small>{insights.attentionArea || (isPropertyScope ? 'da propriedade' : 'do talhão')}</small>
        </div>
        <div>
          <span>Imagem</span>
          <strong>{insights.imageDate}</strong>
          <small>{insights.cloudPct}% de nuvens</small>
        </div>
      </div>

      <div className="remote-recommendation">
        <div className="remote-recommendation-icon" aria-hidden="true">◎</div>
        <div>
          <span>Recomendação</span>
          <strong>{insights.recommendation}</strong>
          <p>{insights.recommendationDetail}</p>
        </div>
        <button type="button" onClick={onOpenAnalysis}>
          Ver análise por zonas
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

function SensoriamentoRemotoInner({ onBack, onNavigate, user, previewMode = false, previewData = null, initialContext = null, pecuariaEnabled = false }) {
  const { darkMode } = useContext(ThemeContext);
  const s = makeStyles(darkMode);
  const { verificarLimite } = usePlanLimits(previewMode ? undefined : user?.id);
  useUserEvent({ userId: user?.id, tela: 'Central de Operações', evento: 'page_view', comDuração: true });
  const previewProperty = previewData?.propriedade || null;
  const previewTalhaoOriginal = previewData?.talhao || null;
  const previewTalhoesOriginais = previewData?.talhoes || (previewTalhaoOriginal ? [previewTalhaoOriginal] : []);
  const previewTalhoes = previewTalhoesOriginais.map(item => aplicarContextoCultivoAoTalhao(item, contextoCultivoLegado(item)));
  const previewTalhao = previewTalhoes.find(item => String(item.id) === String(previewTalhaoOriginal?.id)) || null;
  const previewProperties = previewData?.propriedades || (previewProperty ? [previewProperty] : []);
  const previewPolygon = previewTalhao?.geometria || null;
  const previewScenes = previewData?.cenas || [];
  const previewScene = previewScenes[0] || null;
  const previewSensors = previewData?.sensores || [];
  // Seleção
  const [propriedades, setPropriedades] = useState(() => previewProperties);
  const [propId, setPropId] = useState(() => previewProperty
    ? String(previewProperty.id)
    : (initialContext?.propertyId ? String(initialContext.propertyId) : ''));
  const [talhoes, setTalhoes] = useState(() => previewTalhoes);
  const [talhaoId, setTalhaoId] = useState(() => previewTalhao
    ? String(previewTalhao.id)
    : (initialContext?.plotId ? String(initialContext.plotId) : ''));
  const [talhao, setTalhao] = useState(() => previewTalhao);
  const [escopoSelecionado, setEscopoSelecionado] = useState(() => (
    previewTalhao
      ? { tipo: 'talhao', id: String(previewTalhao.id) }
      : initialContext?.scopeType === 'talhao' && initialContext?.plotId
        ? { tipo: 'talhao', id: String(initialContext.plotId) }
        : initialContext?.propertyId
          ? { tipo: 'propriedade', id: String(initialContext.propertyId) }
      : previewProperty
        ? { tipo: 'propriedade', id: String(previewProperty.id) }
        : null
  ));

  // Polígono
  const [poligono, setPoligono] = useState(() => previewPolygon);        // GeoJSON Feature (salvo/confirmado)
  const [pontosDesenho, setPontosDesenho] = useState([]); // [{lat,lng}] em construção
  const [modoDesenho, setModoDesenho] = useState(false);
  const [sugestaoAderencia, setSugestaoAderencia] = useState(null);
  const [erroGeometria, setErroGeometria] = useState('');
  const [verticeSelecionado, setVerticeSelecionado] = useState(null);

  // Imagens
  const [dataInicio, setDataInicio] = useState(diasAtras(60));
  const [dataFim, setDataFim] = useState(hoje());
  const [maxNuvens, setMaxNuvens] = useState(20);
  const [cenas, setCenas] = useState(() => previewScenes);
  const [cenaUrl, setCenaUrl] = useState(() => previewScene ? getStacSelfUrl(previewScene) : '');       // para marcar cena ativa na lista
  const [cenaFeature, setCenaFeature] = useState(() => previewScene); // feature completo para gerar tile
  const [tipoImagem, setTipoImagem] = useState(() => initialContext?.imageType || (previewMode ? 'ndvi' : 'rgb'));
  const [tileUrl, setTileUrl] = useState('');
  const [mostrarImagemSatelite, setMostrarImagemSatelite] = useState(true);
  const [mapaNdviRecortado, setMapaNdviRecortado] = useState(null);
  const [gerandoNdviRecortado, setGerandoNdviRecortado] = useState(false);
  const [erroNdviRecortado, setErroNdviRecortado] = useState('');

  // UI
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erroStac, setErroStac] = useState('');
  const [erroTile, setErroTile] = useState(false);
  const [msgSalvo, setMsgSalvo] = useState(() => initialContext?.notice || '');
  const [previewNotice, setPreviewNotice] = useState('');
  const [mostrarPoligono, setMostrarPoligono] = useState(true);
  const [medicaoAberta, setMedicaoAberta] = useState(false);
  const [modoMedicao, setModoMedicao] = useState(null);
  const [pontosMedicao, setPontosMedicao] = useState([]);
  const [cursorMedicao, setCursorMedicao] = useState(null);
  const [controlesPoligonoAbertos, setControlesPoligonoAbertos] = useState(() => !previewPolygon);
  const [gerandoRelatorio, setGerandoRelatorio] = useState(false);
  const [gerandoRelatorioNdvi, setGerandoRelatorioNdvi] = useState(false);
  const [gerandoRelatorioTerreno, setGerandoRelatorioTerreno] = useState(false);
  const [erroRelatorioNdvi, setErroRelatorioNdvi] = useState('');
  const [erroRelatorioTerreno, setErroRelatorioTerreno] = useState('');
  const [camadasRelatorio, setCamadasRelatorio] = useState({ rgb: true, cir: true, ndvi: false });
  const isMobile = useIsMobile();
  const mapAreaRef = useRef(null);
  const compactMapLayout = useCompactMapLayout(mapAreaRef, isMobile);
  const [moreMapActionsOpen, setMoreMapActionsOpen] = useState(false);
  const [plotDetailsOpen, setPlotDetailsOpen] = useState(false);
  const [painelAberto, setPainelAberto] = useState(false);
  const [painelTerritorialRecolhido, setPainelTerritorialRecolhido] = useState(true);
  const [abaResultados, setAbaResultados] = useState(() => initialContext?.analysisTab || 'imagens');
  const [renamingPlot, setRenamingPlot] = useState(false);
  const [salvandoNomeTalhao, setSalvandoNomeTalhao] = useState(false);
  const [excluindoTalhao, setExcluindoTalhao] = useState(false);
  const [alteracaoPoligonoPendente, setAlteracaoPoligonoPendente] = useState(false);
  const [gestaoCultivoAberta, setGestaoCultivoAberta] = useState(false);
  const [gestaoCultivoModo, setGestaoCultivoModo] = useState('auto');
  const [sensoresMapa, setSensoresMapa] = useState(() => previewSensors);
  const [mostrarInmet, setMostrarInmet] = useState(false);
  const [inmetStatus, setInmetStatus] = useState({ loading: false, count: 0, error: '' });
  const [sensorMapaSelecionado, setSensorMapaSelecionado] = useState(null);
  const [plotNameDraft, setPlotNameDraft] = useState(() => previewTalhao?.nome || '');
  const analysisSectionRef = useRef(null);
  const analysisRequestIdRef = useRef(0);
  const automaticAnalysisKeyRef = useRef('');
  const selectedPlotIdRef = useRef('');
  const [mostrarPastagens, setMostrarPastagens] = useState(Boolean(initialContext?.pastureLayer));
  const pecuariaMap = usePecuariaMap(propId, !previewMode && pecuariaEnabled && mostrarPastagens);

  // Mapeamento de drone (recurso dos planos pagos)
  const droneHabilitado = !previewMode && droneMappingAvailable(user);
  const [painelDroneAberto, setPainelDroneAberto] = useState(false);
  const [camadaDrone, setCamadaDrone] = useState(null); // { id, nome, url }
  const [opacidadeDrone, setOpacidadeDrone] = useState(0.85);
  const [statusCamadaDrone, setStatusCamadaDrone] = useState(null);
  const [mapToolsCollapsedChoice, setMapToolsCollapsedChoice] = useState(null);
  const ferramentasMapaRecolhidas = mapToolsCollapsedChoice ?? compactMapLayout;
  const plotDetailsRequired = modoDesenho || renamingPlot || alteracaoPoligonoPendente;
  const plotDetailsVisible = plotDetailsOpen || plotDetailsRequired;

  useEffect(() => {
    setCamadaDrone(null);
    setPainelDroneAberto(false);
    setStatusCamadaDrone(null);
  }, [propId]);
  const geometriaAntesEdicaoRef = useRef(null);
  const protecaoCliqueAposArrasteRef = useRef(null);
  if (!protecaoCliqueAposArrasteRef.current) {
    protecaoCliqueAposArrasteRef.current = criarProtecaoCliqueAposArraste();
  }
  const mapViewRef = useRef(initialContext?.mapView || null);
  const restoringMapViewRef = useRef(Boolean(initialContext?.mapView?.center));
  const [mapZoom, setMapZoom] = useState(initialContext?.mapView?.zoom || 5);
  const handleMapViewChange = useCallback(view => {
    mapViewRef.current = view;
    setMapZoom(view.zoom);
  }, []);
  const [voo, setVoo] = useState(null);
  const [fitPoly, setFitPoly] = useState(() => previewPolygon ? geojsonParaLeaflet(previewPolygon) : null);
  const [locationQuery, setLocationQuery] = useState('');
  const locationInputRef = useRef(null);
  const [locationSearchOpen, setLocationSearchOpen] = useState(false);
  const [locationSearchError, setLocationSearchError] = useState('');
  const [polygonContextMenu, setPolygonContextMenu] = useState(null);
  const [cadastroTerritorial, setCadastroTerritorial] = useState(null);
  const [cadastroTerritorialErro, setCadastroTerritorialErro] = useState('');
  const [salvandoCadastroTerritorial, setSalvandoCadastroTerritorial] = useState(false);
  const [municipioCadastroAberto, setMunicipioCadastroAberto] = useState(false);
  const { municipios: locationResults, loading: locationLoading } = useMunicipiosAutocomplete(locationQuery);
  const municipioCadastroTermo = cadastroTerritorial?.tipo === 'propriedade'
    && cadastroTerritorial?.modo !== 'atualizacao'
    && cadastroTerritorial?.etapa === 'dados'
    ? cadastroTerritorial.form.municipio
    : '';
  const {
    municipios: municipiosCadastro,
    loading: municipiosCadastroLoading,
    error: municipiosCadastroError,
  } = useMunicipiosAutocomplete(municipioCadastroTermo);
  const propriedadeParaAderencia = propriedades.find(item => String(item.id) === String(propId))
    || previewData?.propriedade
    || null;
  const desenhandoTalhaoAtual = cadastroTerritorial?.tipo === 'talhao'
    || (!cadastroTerritorial && escopoSelecionado?.tipo === 'talhao' && Boolean(talhaoId));
  const limitesAderencia = useMemo(() => (desenhandoTalhaoAtual ? [
    propriedadeParaAderencia?.geometria ? {
      id: `propriedade-${propriedadeParaAderencia.id}`,
      nome: `limite de ${propriedadeParaAderencia.nome || 'propriedade'}`,
      tipo: 'propriedade',
      prioridade: 1,
      geometria: propriedadeParaAderencia.geometria,
    } : null,
    ...talhoes
      .filter(item => {
        if (!item?.geometria) return false;
        const selectedIds = [talhaoId, talhao?.id, talhao?._physicalPlotId].filter(value => value != null).map(String);
        const itemIds = [item.id, item._physicalPlotId].filter(value => value != null).map(String);
        return !itemIds.some(id => selectedIds.includes(id));
      })
      .map(item => ({
        id: `talhao-${item.id}`,
        nome: item.nome || 'talhão vizinho',
        tipo: 'talhao',
        prioridade: 2,
        geometria: item.geometria,
      })),
  ].filter(item => item && extrairAnelLatLng(item.geometria).length >= 2) : []), [
    desenhandoTalhaoAtual,
    propriedadeParaAderencia,
    talhao,
    talhaoId,
    talhoes,
  ]);

  // Variabilidade NDVI (zonas de manejo)
  const [statsNdvi, setStatsNdvi] = useState(() => previewData?.statsNdvi || null);
  const [comparacaoNdvi, setComparacaoNdvi] = useState(() => previewData?.temporalComparison || null);
  const [calculandoStats, setCalculandoStats] = useState(false);
  const [erroStats, setErroStats] = useState('');

  // Mapa espacial de zonas de manejo
  const [zonasManejo, setZonasManejo] = useState(null);
  const [gerandoZonas, setGerandoZonas] = useState(false);
  const [erroZonas, setErroZonas] = useState('');
  const [mostrarZonas, setMostrarZonas] = useState(true);

  // MDE, declividade e curvas de nível
  const [dadosTerreno, setDadosTerreno] = useState(null);
  const [fonteTerreno, setFonteTerreno] = useState(FONTE_TERRENO_PADRAO);
  const terrenoRequestIdRef = useRef(0);
  const [analisandoTerreno, setAnalisandoTerreno] = useState(false);
  const [erroTerreno, setErroTerreno] = useState('');
  const [camadaTerreno, setCamadaTerreno] = useState('altitude');
  const [mostrarCurvasNivel, setMostrarCurvasNivel] = useState(true);
  const [mostrarRotulosCurvas, setMostrarRotulosCurvas] = useState(true);
  const [intervaloCurvasSelecionado, setIntervaloCurvasSelecionado] = useState(10);

  const curvasNivelAtivas = useMemo(
    () => dadosTerreno?.curvasPorIntervalo?.[intervaloCurvasSelecionado] || [],
    [dadosTerreno, intervaloCurvasSelecionado],
  );
  const rotulosCurvasNivel = useMemo(
    () => selecionarRotulosCurvas(curvasNivelAtivas, intervaloCurvasSelecionado, mapZoom),
    [curvasNivelAtivas, intervaloCurvasSelecionado, mapZoom],
  );
  const niveisCurvasMestras = useMemo(
    () => new Set(obterNiveisCurvasMestras(curvasNivelAtivas, intervaloCurvasSelecionado)),
    [curvasNivelAtivas, intervaloCurvasSelecionado],
  );

  const showPreviewNotice = (message = 'Ação demonstrativa: nenhum dado real foi alterado.') => {
    setPreviewNotice(message);
    window.setTimeout(() => setPreviewNotice(''), 3200);
  };

  const abrirSensorNoModulo = useCallback((sensor) => {
    if (!sensor || typeof onNavigate !== 'function') return;
    onNavigate('sensores', {
      sensorId: sensor.id,
      deviceId: sensor.device_id,
      from: 'central-operacoes',
    });
  }, [onNavigate]);

  useEffect(() => {
    terrenoRequestIdRef.current++;
    setAnalisandoTerreno(false);
    setDadosTerreno(null);
    setErroTerreno('');
    setMapaNdviRecortado(null);
    setErroNdviRecortado('');
  }, [poligono]);

  // ── Carrega o mesmo escopo operacional usado nos demais módulos ──────────
  const [propriedadesCarregadas, setPropriedadesCarregadas] = useState(previewMode);
  useEffect(() => {
    if (previewMode) return;
    if (!user?.id) return;
    async function carregarPropriedades() {
      try {
        const { properties } = await loadVisibleAuthorizedProperties(user.id, '*', 'sensoriamento');
        const todas = [...properties].sort((a, b) => {
          const prioridadeA = a._accessRole === 'owner' ? 0 : 1;
          const prioridadeB = b._accessRole === 'owner' ? 0 : 1;
          return prioridadeA - prioridadeB || a.nome.localeCompare(b.nome);
        });
        setPropriedades(todas);
      } catch (error) {
        console.error('Não foi possível carregar o escopo operacional das propriedades:', error);
        setPropriedades([]);
      } finally {
        setPropriedadesCarregadas(true);
      }
    }
    carregarPropriedades();
  }, [user, previewMode]);


  // ── Carrega estações georreferenciadas para a operação pelo mapa ───────────
  useEffect(() => {
    if (previewMode) {
      setSensoresMapa(previewSensors);
      return;
    }
    if (!user?.id) {
      setSensoresMapa([]);
      return;
    }

    let cancelled = false;
    async function carregarSensoresMapa() {
      const { data, error } = await supabase
        .from('sensores_usuarios')
        .select('id, user_id, device_id, nome_sensor, tipo_sensor, profundidade_cm, latitude, longitude, localizacao_atualizada_em, data_cadastro, stream_url, camera_last_seen_at')
        .eq('user_id', user.id)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .order('data_cadastro', { ascending: false });

      if (error || cancelled) {
        if (!cancelled) setSensoresMapa([]);
        return;
      }

      const enriched = await Promise.all((data || []).map(async sensor => {
        if (sensor.tipo_sensor === 'camera') return { ...sensor, latestReading: null, rainLast24h: null };
        const table = SENSOR_DATA_TABLES[sensor.tipo_sensor] || SENSOR_DATA_TABLES.meteorologico;
        const deviceId = String(sensor.device_id || '').trim().toLowerCase();
        if (table === SENSOR_DATA_TABLES.meteorologico) {
          const fimJanela = new Date();
          const inicioJanela = new Date(fimJanela.getTime() - 24 * 60 * 60 * 1000);
          const { data: recentReadings, error: recentError } = await supabase
            .from(table)
            .select('timestamp, temp_c, rh_perc, rain_mm')
            .eq('device_id', deviceId)
            .gte('timestamp', inicioJanela.toISOString())
            .lte('timestamp', fimJanela.toISOString())
            .order('timestamp', { ascending: false })
            .limit(2000);
          if (!recentError && recentReadings?.length) {
            return {
              ...sensor,
              latestReading: recentReadings[0],
              rainLast24h: calcularChuvaUltimas24Horas(recentReadings, fimJanela),
            };
          }
        }
        const { data: readings } = await supabase
          .from(table)
          .select('*')
          .eq('device_id', deviceId)
          .order('timestamp', { ascending: false })
          .limit(1);
        return { ...sensor, latestReading: readings?.[0] || null, rainLast24h: null };
      }));

      if (!cancelled) setSensoresMapa(enriched);
    }

    carregarSensoresMapa();
    return () => { cancelled = true; };
  }, [user?.id, previewMode, previewData]);

  // ── Carrega talhões ao selecionar propriedade ─────────────────────────────
  useEffect(() => {
    if (previewMode) {
      const nextPreviewTalhoes = previewData?.talhoesPorPropriedade?.[String(propId)]
        || (String(propId) === String(previewProperty?.id) ? previewTalhoes : []);
      setTalhoes(nextPreviewTalhoes);
      if (talhaoId && !nextPreviewTalhoes.some(item => String(item.id) === String(talhaoId))) {
        setTalhaoId('');
        setTalhao(null);
      }
      return;
    }
    if (!propId) {
      setTalhoes([]);
      setTalhaoId('');
      setTalhao(null);
      return;
    }
    // Sem a lista de propriedades ainda não dá para saber se este cadastro usa
    // talhões físicos; aguardar evita mostrar dados legados desatualizados.
    if (!propriedadesCarregadas) return;
    async function carregarTalhoesPermanentes() {
      const propriedadeSelecionada = propriedades.find(item => String(item.id) === String(propId));
      if (propriedadeSelecionada?._usesPhysicalPlots) {
        const talhoesFisicos = (propriedadeSelecionada._operationalPlots || []).map(item => (
          operationalPhysicalPlotToCompatibleLegacyPlot(item, [], propriedadeSelecionada)
        ));
        setTalhoes(talhoesFisicos);
        return;
      }

      const { data, error } = await supabase
        .from('talhoes')
        .select('id, nome, area, cultura, coordenada_x, coordenada_y, geometria, data_plantio, data_colheita, propriedade_id')
        .eq('propriedade_id', propId)
        .order('nome');
      if (error) return setTalhoes([]);

      let contextos = new Map();
      try {
        contextos = await buscarContextosCultivo((data || []).map(item => item.id));
      } catch {
        // Compatibilidade enquanto a migration ainda não estiver aplicada.
      }
      setTalhoes((data || []).map(item => aplicarContextoCultivoAoTalhao(
        item,
        contextos.get(String(item.id)) || contextoCultivoLegado(item)
      )));
    }
    carregarTalhoesPermanentes();
  }, [propId, previewMode, previewData, propriedades, propriedadesCarregadas]);

  // ── Ao selecionar talhão ───────────────────────────────────────────────────
  useEffect(() => {
    analysisRequestIdRef.current += 1;
    automaticAnalysisKeyRef.current = '';
    setCalculandoStats(false);
    if (!talhaoId) {
      selectedPlotIdRef.current = '';
      setTalhao(null);
      setPontosDesenho([]);
      setSugestaoAderencia(null);
      setErroGeometria('');
      setControlesPoligonoAbertos(false);
      if (escopoSelecionado?.tipo === 'propriedade') return;
      setPoligono(null);
      setVoo(null);
      setFitPoly(null);
      setCenas([]);
      setTileUrl('');
      setCenaUrl('');
      setStatsNdvi(null);
      setComparacaoNdvi(null);
      return;
    }
    if (String(propId).startsWith('territorial-property-')) {
      setTalhoes([]);
      setTalhaoId('');
      setTalhao(null);
      return;
    }
    const t = talhoes.find(t => String(t.id) === talhaoId);
    if (!t) return;
    const selectionChanged = selectedPlotIdRef.current !== talhaoId;
    selectedPlotIdRef.current = talhaoId;
    setEscopoSelecionado(current => (
      current?.tipo === 'talhao' && String(current.id) === String(talhaoId)
        ? current
        : { tipo: 'talhao', id: String(talhaoId) }
    ));
    setTalhao(t || null);
    setPlotNameDraft(t?.nome || '');
    setRenamingPlot(Boolean(previewMode && t?.needsName));

    if (!selectionChanged) return;

    setAlteracaoPoligonoPendente(false);

    if (previewMode) {
      const previewGeometry = t?.geometria || null;
      setPoligono(previewGeometry);
      setPontosDesenho([]);
      setModoDesenho(Boolean(t?.isDraft && !previewGeometry));
      setFitPoly(geometriaParaPosicoes(previewGeometry));
      setVoo(!previewGeometry && t?.coordenada_y && t?.coordenada_x
        ? { lat: Number(t.coordenada_y), lng: Number(t.coordenada_x) }
        : null);
      setControlesPoligonoAbertos(!previewGeometry);
      return;
    }

    setCenas([]);
    setTileUrl('');
    setCenaUrl('');
    setCenaFeature(null);
    setStatsNdvi(null);
    setComparacaoNdvi(null);
    setErroStats('');
    if (!initialContext?.notice) setMsgSalvo('');

    if (t?.geometria) {
      setPoligono(t.geometria);
      setControlesPoligonoAbertos(false);
      setFitPoly(restoringMapViewRef.current ? null : geojsonParaLeaflet(t.geometria));
      setVoo(null);
    } else {
      setPoligono(null);
      setControlesPoligonoAbertos(true);
      setFitPoly(null);
      if (t?.coordenada_y && t?.coordenada_x) {
        setVoo({ lat: parseFloat(t.coordenada_y), lng: parseFloat(t.coordenada_x) });
      }
    }
    restoringMapViewRef.current = false;
  }, [talhaoId, talhoes, previewMode, escopoSelecionado?.tipo]);

  // ── Ao selecionar o perímetro completo da propriedade ────────────────────
  useEffect(() => {
    if (escopoSelecionado?.tipo !== 'propriedade' || !propId) return;
    const propriedade = propriedades.find(item => String(item.id) === String(propId));
    if (!propriedade) return;
    const geometria = propriedade?.geometria || null;

    analysisRequestIdRef.current += 1;
    automaticAnalysisKeyRef.current = '';
    selectedPlotIdRef.current = '';
    setTalhao(null);
    setPoligono(geometria);
    setPontosDesenho([]);
    setSugestaoAderencia(null);
    setErroGeometria('');
    setModoDesenho(false);
    setFitPoly(restoringMapViewRef.current ? null : geometriaParaPosicoes(geometria));
    setVoo(!geometria && propriedade?.coordenada_y && propriedade?.coordenada_x
      ? { lat: Number(propriedade.coordenada_y), lng: Number(propriedade.coordenada_x) }
      : null);
    setCenas([]);
    setTileUrl('');
    setCenaUrl('');
    setCenaFeature(null);
    setStatsNdvi(null);
    setComparacaoNdvi(null);
    setDadosTerreno(null);
    setErroStats('');
    setErroTerreno('');
    if (!initialContext?.notice) setMsgSalvo('');
    setControlesPoligonoAbertos(false);
    restoringMapViewRef.current = false;
  }, [escopoSelecionado?.tipo, escopoSelecionado?.id, propId, propriedades]);

  // ── Atualiza tile quando cena ou tipo muda ────────────────────────────────
  useEffect(() => {
    if (previewMode) return;
    if (cenaFeature && tipoImagem) {
      setErroTile(false);
      setTileUrl(tipoImagem === 'ndvi' ? '' : getTileUrl(cenaFeature, tipoImagem));
      setMostrarImagemSatelite(true);
    }
  }, [cenaFeature, tipoImagem, previewMode]);

  // NDVI é rasterizado e mascarado pelo polígono, evitando colorir o mapa inteiro.
  useEffect(() => {
    if (previewMode) return undefined;
    if (tipoImagem !== 'ndvi' || !cenaFeature || !poligono) {
      setMapaNdviRecortado(null);
      setGerandoNdviRecortado(false);
      setErroNdviRecortado('');
      return undefined;
    }

    let cancelled = false;
    setGerandoNdviRecortado(true);
    setErroNdviRecortado('');
    setMapaNdviRecortado(null);
    gerarMapaNdviRecortado(cenaFeature, poligono)
      .then(resultado => {
        if (!cancelled) setMapaNdviRecortado(resultado);
      })
      .catch(error => {
        if (!cancelled) {
          setErroNdviRecortado(error?.message || 'Não foi possível recortar o NDVI para esta área.');
        }
      })
      .finally(() => {
        if (!cancelled) setGerandoNdviRecortado(false);
      });

    return () => { cancelled = true; };
  }, [cenaFeature, tipoImagem, poligono, previewMode]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const selecionarPropriedadeNoMapa = propriedadeId => {
    const nextId = propriedadeId ? String(propriedadeId) : '';
    setPropId(nextId);
    setTalhaoId('');
    setEscopoSelecionado(nextId ? { tipo: 'propriedade', id: nextId } : null);
    setCadastroTerritorialErro('');
  };

  const selecionarTalhaoNoMapa = id => {
    const nextId = id ? String(id) : '';
    setTalhaoId(nextId);
    setEscopoSelecionado(nextId
      ? { tipo: 'talhao', id: nextId }
      : (propId ? { tipo: 'propriedade', id: String(propId) } : null));
  };

  const localizarCoordenadas = () => {
    const coordenadas = parseCoordinateSearch(locationQuery);
    if (!coordenadas) {
      setLocationSearchError('Informe no formato latitude, longitude ou escolha uma cidade da lista.');
      setLocationSearchOpen(true);
      return;
    }
    setLocationSearchError('');
    setLocationSearchOpen(false);
    setVoo(coordenadas);
    setFitPoly(null);
  };

  const localizarMunicipio = async municipio => {
    const nome = municipio?.name_municipio || municipio?.name_muni;
    if (!nome) return;
    const estado = normalizarUf(municipio.sigla_estado || municipio.state || municipio.code_state);
    setLocationQuery(`${nome} · ${estado}`.replace(/ · $/, ''));
    setLocationSearchOpen(false);
    setLocationSearchError('');
    setCadastroTerritorial(current => current?.tipo === 'propriedade' ? {
      ...current,
      form: { ...current.form, municipio: nome, estado },
    } : current);

    if (previewMode && previewTalhao?.coordenada_y && previewTalhao?.coordenada_x) {
      setVoo({ lat: Number(previewTalhao.coordenada_y), lng: Number(previewTalhao.coordenada_x) });
      setFitPoly(null);
      showPreviewNotice(`Mapa centralizado próximo a ${nome}.`);
      return;
    }

    const { data, error } = await supabase
      .from('municipios_brazil')
      .select('coordenada_x, coordenada_y')
      .eq('name_muni', nome)
      .limit(1)
      .maybeSingle();

    if (error || !data) {
      setLocationSearchError('Não foi possível localizar esta cidade. Tente pelas coordenadas.');
      return;
    }

    setVoo({ lat: Number(data.coordenada_y), lng: Number(data.coordenada_x) });
    setFitPoly(null);
  };

  const abrirAcaoTerritorial = (action, message) => {
    if (!propId) {
      showPreviewNotice('Selecione uma propriedade no mapa antes de continuar.');
      return;
    }

    if ((action === 'property' || action === 'plot-info') && !talhaoId) {
      showPreviewNotice('Selecione um talhão para abrir o manejo do plantio.');
      return;
    }

    const operationReturnContext = {
      propertyId: propId,
      plotId: talhaoId || null,
      scopeType: escopoSelecionado?.tipo || (talhaoId ? 'talhao' : 'propriedade'),
      mapView: mapViewRef.current,
      analysisTab: abaResultados,
      imageType: tipoImagem,
    };
    const shouldReturnToMap = action === 'new-expense' || action === 'new-order';

    if (action === 'property' || action === 'plot-info') {
      onNavigate?.('propriedades', {
        propertyId: propId,
        propertyName: propriedadeSelecionada?.nome || '',
        plotId: talhaoId,
        plotName: talhao?.nome || '',
        section: action === 'plot-info' ? 'info' : 'plantio',
        // Mantém o contexto autorizado já carregado na Central. Esse retrato
        // permite abrir diretamente propriedades territoriais (inclusive
        // compartilhadas) sem depender da lista legada terminar de recarregar.
        propertySnapshot: propriedadeSelecionada || null,
        plotSnapshot: talhao || null,
        propertyPlots: talhoes.filter(item => (
          !item.propriedade_id
          || String(item.propriedade_id) === String(propId)
        )),
        // Quem entrou pela Central de Operações deve voltar direto para lá,
        // com a mesma fazenda e visão de mapa, em vez de ficar preso na
        // aba Propriedades e Talhões.
        returnTarget: 'sensoriamento',
        returnContext: operationReturnContext,
      });
      return;
    }

    if (previewMode && shouldReturnToMap) {
      onNavigate?.('caderno', {
        action,
        propertyId: propId,
        plotId: talhaoId || null,
        returnTarget: 'sensoriamento',
        returnContext: operationReturnContext,
      });
      return;
    }

    if (previewMode) {
      showPreviewNotice(message);
      return;
    }

    onNavigate?.('caderno', {
      action,
      propertyId: propId,
      plotId: talhaoId || null,
      returnTarget: shouldReturnToMap ? 'sensoriamento' : null,
      returnContext: shouldReturnToMap ? operationReturnContext : null,
    });
  };

  const iniciarCadastroTerritorial = tipo => {
    const propriedadeAtual = propriedades.find(item => String(item.id) === String(propId));
    if (tipo === 'talhao' && !propriedadeAtual) {
      setCadastroTerritorialErro('Selecione a propriedade antes de desenhar o novo talhão.');
      return;
    }

    if (tipo === 'talhao' && !propriedadeAtual?.geometria) {
      setCadastroTerritorialErro('Adicione primeiro o perímetro da propriedade. O novo talhão precisa ficar dentro dessa área.');
      return;
    }

    if (tipo === 'talhao' && !propertyActionAllowed(propriedadeAtual, 'propriedades', 'create', propriedadeAtual?._accessRole !== 'viewer')) {
      setCadastroTerritorialErro('Seu acesso a esta propriedade é somente para visualização.');
      return;
    }

    const cadastro = {
      id: `territorial-${tipo}-${Date.now()}`,
      tipo,
      etapa: 'desenho',
      previousPropId: propId,
      previousTalhaoId: talhaoId,
      form: tipo === 'propriedade'
        ? { nome: '', municipio: '', estado: '', area: null }
        : { nome: '', area: null, propriedadeId: propId },
    };

    setCadastroTerritorial(cadastro);
    setMedicaoAberta(false);
    setModoMedicao(null);
    setPontosMedicao([]);
    setCursorMedicao(null);
    setMunicipioCadastroAberto(false);
    setCadastroTerritorialErro('');
    setEscopoSelecionado(null);
    setTalhaoId('');
    if (tipo === 'propriedade') setPropId('');
    setTalhao(null);
    setPoligono(null);
    setPontosDesenho([]);
    setSugestaoAderencia(null);
    setErroGeometria('');
    setFitPoly(null);
    setModoDesenho(true);
    setControlesPoligonoAbertos(true);
    setMsgSalvo('');
  };

  const iniciarAtualizacaoPerimetroPropriedade = () => {
    const propriedadeAtual = propriedades.find(item => String(item.id) === String(propId));
    if (!propriedadeAtual) {
      setCadastroTerritorialErro('Selecione a propriedade que receberá o perímetro.');
      return;
    }
    if (!propertyActionAllowed(propriedadeAtual, 'propriedades', 'edit', propriedadeAtual._accessRole !== 'viewer')) {
      setCadastroTerritorialErro('Seu acesso a esta propriedade é somente para visualização.');
      return;
    }

    const pontosExistentes = propriedadeAtual.geometria?.geometry?.coordinates?.[0]
      ?.slice(0, -1)
      .map(([lng, lat]) => ({ lat, lng })) || [];

    setCadastroTerritorial({
      id: `territorial-property-update-${Date.now()}`,
      tipo: 'propriedade',
      modo: 'atualizacao',
      propertyId: String(propriedadeAtual.id),
      etapa: 'desenho',
      previousPropId: String(propriedadeAtual.id),
      previousTalhaoId: '',
      form: {
        nome: propriedadeAtual.nome || '',
        municipio: propriedadeAtual.municipio || '',
        estado: propriedadeAtual.estado || '',
        area: null,
      },
    });
    setMedicaoAberta(false);
    setModoMedicao(null);
    setPontosMedicao([]);
    setCursorMedicao(null);
    setCadastroTerritorialErro('');
    setEscopoSelecionado({ tipo: 'propriedade', id: String(propriedadeAtual.id) });
    setTalhaoId('');
    setTalhao(null);
    setPoligono(propriedadeAtual.geometria || null);
    setPontosDesenho(pontosExistentes);
    setSugestaoAderencia(null);
    setErroGeometria('');
    setFitPoly(geometriaParaPosicoes(propriedadeAtual.geometria));
    setModoDesenho(true);
    setControlesPoligonoAbertos(true);
    setMsgSalvo('');
  };

  const editarPerimetroPeloMapa = event => {
    event?.preventDefault();
    event?.stopPropagation();
    setPolygonContextMenu(null);
    if (escopoSelecionado?.tipo === 'propriedade') {
      iniciarAtualizacaoPerimetroPropriedade();
      return;
    }
    iniciarEdicaoPoligono();
  };

  const atualizarCadastroTerritorial = (campo, valor) => {
    setCadastroTerritorial(current => current ? {
      ...current,
      form: { ...current.form, [campo]: valor },
    } : current);
    setCadastroTerritorialErro('');
  };

  const abrirGestaoCultivo = modo => {
    if (!propertyActionAllowed(propriedadeSelecionada, 'propriedades', modo === 'create' ? 'create' : 'edit', true)) { setMsgSalvo('Você não tem permissão para alterar plantios desta propriedade.'); return; }
    setGestaoCultivoModo(modo);
    setGestaoCultivoAberta(true);
  };

  const selecionarMunicipioCadastro = municipio => {
    const nome = municipio?.name_municipio || municipio?.name_muni;
    if (!nome) return;
    const estado = normalizarUf(municipio.sigla_estado || municipio.state || municipio.code_state);
    setCadastroTerritorial(current => current ? {
      ...current,
      form: { ...current.form, municipio: nome, estado },
    } : current);
    setMunicipioCadastroAberto(false);
    setCadastroTerritorialErro('');
  };

  const cancelarCadastroTerritorial = () => {
    const previousPropId = cadastroTerritorial?.previousPropId || '';
    const previousTalhaoId = cadastroTerritorial?.previousTalhaoId || '';
    const geometriaAnterior = cadastroTerritorial?.modo === 'atualizacao'
      ? propriedades.find(item => String(item.id) === String(previousPropId))?.geometria || null
      : null;
    setCadastroTerritorial(null);
    setMunicipioCadastroAberto(false);
    setCadastroTerritorialErro('');
    setPoligono(geometriaAnterior);
    setPontosDesenho([]);
    setSugestaoAderencia(null);
    setErroGeometria('');
    setModoDesenho(false);
    setFitPoly(geometriaParaPosicoes(geometriaAnterior));
    setPropId(previousPropId);
    setTalhaoId(previousTalhaoId);
    setEscopoSelecionado(previousTalhaoId
      ? { tipo: 'talhao', id: String(previousTalhaoId) }
      : (previousPropId ? { tipo: 'propriedade', id: String(previousPropId) } : null));
  };

  const concluirCadastroTerritorial = (registro, simulated = false) => {
    if (cadastroTerritorial.modo === 'atualizacao') {
      const propertyId = String(cadastroTerritorial.propertyId);
      const propriedadeAtualizada = {
        ...propriedades.find(item => String(item.id) === propertyId),
        ...registro,
        geometria: poligono,
      };
      setPropriedades(current => current.map(item => (
        String(item.id) === propertyId ? propriedadeAtualizada : item
      )));
      setPropId(propertyId);
      setTalhoes([]);
      setTalhaoId('');
      setTalhao(null);
      setEscopoSelecionado({ tipo: 'propriedade', id: propertyId });
    } else if (cadastroTerritorial.tipo === 'propriedade') {
      const propriedadeCriada = {
        ...registro,
        geometria: poligono,
        area: cadastroTerritorial.form.area,
        _accessRole: 'owner',
        _localPreview: simulated,
      };
      setPropriedades(current => [...current, propriedadeCriada].sort((a, b) => a.nome.localeCompare(b.nome)));
      setPropId(String(propriedadeCriada.id));
      setTalhoes([]);
      setTalhaoId('');
      setTalhao(null);
      setEscopoSelecionado({ tipo: 'propriedade', id: String(propriedadeCriada.id) });
    } else {
      const talhaoCriado = {
        ...registro,
        geometria: poligono,
        area: cadastroTerritorial.form.area,
        propriedade_id: cadastroTerritorial.form.propriedadeId,
        ciclo_atual: null,
        ultimo_ciclo: null,
        cultura: null,
        data_plantio: null,
        _localPreview: simulated,
      };
      setTalhoes(current => [...current, talhaoCriado].sort((a, b) => a.nome.localeCompare(b.nome)));
      setTalhaoId(String(talhaoCriado.id));
      setTalhao(talhaoCriado);
      setEscopoSelecionado({ tipo: 'talhao', id: String(talhaoCriado.id) });
    }

    setCadastroTerritorial(null);
    setModoDesenho(false);
    setPontosDesenho([]);
    setControlesPoligonoAbertos(false);
    setMsgSalvo(cadastroTerritorial.modo === 'atualizacao'
      ? `Perímetro adicionado à propriedade${simulated ? ' na prévia local' : ''} ✓`
      : `${cadastroTerritorial.tipo === 'propriedade' ? 'Propriedade' : 'Talhão'} salvo${simulated ? ' na prévia local' : ''} ✓`);
    if (simulated) {
      showPreviewNotice('Cadastro salvo somente nesta prévia local. Nenhum dado do banco de produção foi alterado.');
    }
  };

  const salvarCadastroTerritorial = async () => {
    if (!cadastroTerritorial || !poligono || salvandoCadastroTerritorial) return;
    const nome = cadastroTerritorial.form.nome.trim();
    if (nome.length < 2) {
      setCadastroTerritorialErro('Informe um nome com pelo menos 2 caracteres.');
      return;
    }

    if (cadastroTerritorial.tipo === 'talhao') {
      const validation = validarTalhaoDesenhado(poligono);
      if (!validation.valido) {
        setErroGeometria(validation.mensagem);
        setCadastroTerritorialErro(validation.mensagem);
        return;
      }
    }

    const area = calcularAreaHectares(poligono);
    if (!area || area <= 0) {
      setCadastroTerritorialErro('Não foi possível medir este desenho. Ajuste o perímetro e tente novamente.');
      return;
    }

    const centro = calcularCentroGeometria(poligono);
    const baseRecord = {
      id: cadastroTerritorial.modo === 'atualizacao' ? cadastroTerritorial.propertyId : cadastroTerritorial.id,
      nome,
      area,
      geometria: poligono,
    };

    if (previewMode) {
      if (cadastroTerritorial.modo === 'atualizacao') {
        const propriedadeAtual = propriedades.find(item => String(item.id) === String(cadastroTerritorial.propertyId));
        concluirCadastroTerritorial({ ...propriedadeAtual, geometria: poligono }, true);
        return;
      }
      concluirCadastroTerritorial({
        ...baseRecord,
        ...(cadastroTerritorial.tipo === 'propriedade'
          ? {
              municipio: normalizarTexto(cadastroTerritorial.form.municipio),
              estado: normalizarUf(cadastroTerritorial.form.estado),
              coordenada_x: centro?.lng,
              coordenada_y: centro?.lat,
            }
          : {}),
      }, true);
      return;
    }

    if (!TERRITORIAL_WRITES_ENABLED) {
      setCadastroTerritorialErro('Novos cadastros estão temporariamente bloqueados pela chave de segurança.');
      return;
    }

    if (!user?.id) {
      setCadastroTerritorialErro('Sua sessão não está disponível. Entre novamente antes de salvar.');
      return;
    }

    setSalvandoCadastroTerritorial(true);
    setCadastroTerritorialErro('');

    try {
      if (cadastroTerritorial.modo === 'atualizacao') {
        const propriedadeAtual = propriedades.find(item => String(item.id) === String(cadastroTerritorial.propertyId));
        if (!propriedadeAtual || !propertyActionAllowed(propriedadeAtual, 'propriedades', 'edit', propriedadeAtual._accessRole !== 'viewer')) {
          setCadastroTerritorialErro('Você não possui permissão para atualizar esta propriedade.');
          return;
        }

        const { data, error } = await supabase
          .from('propriedades')
          .update({ geometria: poligono })
          .eq('id', propriedadeAtual.id)
          .select('*');
        if (error) {
          setCadastroTerritorialErro(`Não foi possível salvar o perímetro: ${error.message}`);
          return;
        }
        if (!data?.length) {
          setCadastroTerritorialErro(
            propriedadeAtual._accessRole === 'editor'
              ? 'O banco ainda não autorizou editores a atualizar o perímetro compartilhado. Aplique a migration de permissão e tente novamente.'
              : 'A propriedade não foi encontrada ou sua sessão não possui permissão para atualizá-la.'
          );
          return;
        }
        concluirCadastroTerritorial({ ...data[0], _accessRole: propriedadeAtual._accessRole || 'owner' });
        return;
      }

      const recurso = cadastroTerritorial.tipo === 'propriedade' ? 'propriedades' : 'talhoes';
      const limite = await verificarLimite(recurso);
      if (!limite?.permitido) {
        const detalhe = limite?.error
          ? ` Não foi possível validar o plano: ${limite.error}`
          : ` Seu plano permite ${limite?.limite ?? 0} e já possui ${limite?.usado ?? limite?.limite ?? 0}.`;
        setCadastroTerritorialErro(`Não é possível criar ${cadastroTerritorial.tipo === 'propriedade' ? 'outra propriedade.' : 'outro talhão.'}${detalhe}`);
        return;
      }

      if (cadastroTerritorial.tipo === 'propriedade') {
        const payload = {
          user_id: user.id,
          nome,
          municipio: normalizarTexto(cadastroTerritorial.form.municipio) || null,
          estado: normalizarUf(cadastroTerritorial.form.estado) || null,
          area,
          geometria: poligono,
          coordenada_x: centro?.lng || null,
          coordenada_y: centro?.lat || null,
        };
        const { data, error } = await supabase.from('propriedades').insert(payload).select('*').single();
        if (error) {
          setCadastroTerritorialErro(`Não foi possível salvar a propriedade: ${error.message}`);
          return;
        }
        concluirCadastroTerritorial(data);
        return;
      }

      const propriedadeAtual = propriedades.find(item => String(item.id) === String(cadastroTerritorial.form.propriedadeId));
      if (!propriedadeAtual || !propertyActionAllowed(propriedadeAtual, 'propriedades', 'create', propriedadeAtual._accessRole !== 'viewer')) {
        setCadastroTerritorialErro('Você não possui permissão para cadastrar talhões nesta propriedade.');
        return;
      }

      const areaOcupada = talhoes.reduce((total, item) => total + (Number(item.area) || 0), 0);
      const areaPropriedade = Number(propriedadeAtual.area) || 0;
      if (areaPropriedade > 0 && areaOcupada + area > areaPropriedade + 0.01) {
        setCadastroTerritorialErro(
          `A área dos talhões (${(areaOcupada + area).toFixed(2)} ha) ultrapassaria a área da propriedade (${areaPropriedade.toFixed(2)} ha).`
        );
        return;
      }

      const payload = {
      propriedade_id: cadastroTerritorial.form.propriedadeId,
      user_id: user.id,
      nome,
      area,
        geometria: poligono,
        coordenada_x: centro?.lng || null,
        coordenada_y: centro?.lat || null,
      };
      const { data, error } = await supabase.from('talhoes').insert(payload).select('*').single();
      if (error) {
        setCadastroTerritorialErro(`Não foi possível salvar o talhão: ${error.message}`);
        return;
      }
      concluirCadastroTerritorial(data);
    } catch (error) {
      setCadastroTerritorialErro(`Não foi possível concluir o cadastro: ${error?.message || 'erro inesperado'}`);
    } finally {
      setSalvandoCadastroTerritorial(false);
    }
  };

  const salvarNomeTalhao = async () => {
    if (!propertyActionAllowed(propriedadeSelecionada, 'propriedades', 'edit', true)) { setMsgSalvo('Alteração não autorizada nesta propriedade.'); return; }
    const nextName = plotNameDraft.trim();
    if (!nextName || !talhaoId || salvandoNomeTalhao) return;

    if (previewMode) {
      setTalhao(current => current ? { ...current, nome: nextName } : current);
      setTalhoes(current => current.map(item => (
        String(item.id) === String(talhaoId) ? { ...item, nome: nextName, needsName: false } : item
      )));
      setRenamingPlot(false);
      showPreviewNotice(`Nome atualizado para “${nextName}” nesta prévia.`);
      return;
    }

    setSalvandoNomeTalhao(true);
    if (talhao?._operationalPhysical) {
      const physicalPlotId = talhao._physicalPlotId ?? talhao.id;
      try {
        const result = await updateOperationalPlot({
          plotId: physicalPlotId,
          propertyId: propId,
          name: nextName,
        });
        const savedName = result.nome || nextName;
        setTalhao(current => {
          const currentPhysicalId = current?._physicalPlotId ?? current?.id;
          return current && String(currentPhysicalId) === String(physicalPlotId)
            ? { ...current, nome: savedName }
            : current;
        });
        setTalhoes(current => current.map(item => (
          String(item.id) === String(talhaoId) ? { ...item, nome: savedName } : item
        )));
        setPropriedades(current => current.map(property => (
          String(property.id) !== String(propId) ? property : {
            ...property,
            _operationalPlots: (property._operationalPlots || []).map(plot => (
              String(plot.id) === String(physicalPlotId) ? { ...plot, nome: savedName } : plot
            )),
          }
        )));
        setRenamingPlot(false);
        setMsgSalvo('Nome do talhão atualizado ✓');
      } catch (error) {
        setMsgSalvo(`Não foi possível renomear: ${error?.message || 'erro inesperado'}`);
      } finally {
        setSalvandoNomeTalhao(false);
      }
      return;
    }

    const { error } = await supabase
      .from('talhoes')
      .update({ nome: nextName })
      .eq('id', talhaoId);
    setSalvandoNomeTalhao(false);
    if (error) {
      setMsgSalvo(`Não foi possível renomear: ${error.message}`);
      return;
    }

    setTalhao(current => current ? { ...current, nome: nextName } : current);
    setTalhoes(current => current.map(item => (
      String(item.id) === String(talhaoId) ? { ...item, nome: nextName } : item
    )));
    setRenamingPlot(false);
    setMsgSalvo('Nome do talhão atualizado ✓');
  };

  const excluirTalhaoAtual = async () => {
    if (!talhao || excluindoTalhao) return;
    if (propriedadeSelecionada?._accessRole !== 'owner') {
      setMsgSalvo('Somente o proprietário pode excluir talhões.');
      return;
    }
    const bloqueio = motivoBloqueioExclusaoTalhao(talhao);
    if (bloqueio) { setMsgSalvo(bloqueio); return; }

    if (previewMode) {
      showPreviewNotice('Exclusão não disponível nesta prévia.');
      return;
    }
    if (!window.confirm(`Excluir o talhão vazio "${talhao.nome}"? Essa ação não pode ser desfeita.`)) return;

    setExcluindoTalhao(true);
    try {
      if (talhao._operationalPhysical) {
        await deleteOperationalPlot(talhao._physicalPlotId ?? talhao.id, propId);
      } else {
        await deleteEmptyLegacyPlot(talhao.id);
      }
      const talhaoExcluidoId = talhao.id;
      setTalhoes(current => current.filter(item => String(item.id) !== String(talhaoExcluidoId)));
      setTalhaoId('');
      setTalhao(null);
      setPoligono(null);
      setEscopoSelecionado(propId ? { tipo: 'propriedade', id: String(propId) } : null);
      setMsgSalvo('Talhão excluído com segurança ✓');
    } catch (error) {
      setMsgSalvo(`Não foi possível excluir: ${error?.message || 'erro inesperado'}`);
    } finally {
      setExcluindoTalhao(false);
    }
  };

  const atualizarTalhaoComCiclo = ({ type, ciclo, contexto, talhaoId: cicloTalhaoId }) => {
    const talhaoAlvoId = String(cicloTalhaoId || ciclo?.talhao_id || talhaoId);
    const construirContexto = () => {
      if (type === 'deleted') return contexto || { cicloAtual: null, ultimoCiclo: null };
      if (type === 'harvested') return { cicloAtual: null, ultimoCiclo: ciclo };
      return { cicloAtual: ciclo, ultimoCiclo: ciclo };
    };
    setTalhoes(current => current.map(item => (
      String(item.id) === talhaoAlvoId
        ? aplicarContextoCultivoAoTalhao(item, construirContexto())
        : item
    )));
    setTalhao(current => current && String(current.id) === talhaoAlvoId
      ? aplicarContextoCultivoAoTalhao(current, construirContexto())
      : current);
    setMsgSalvo(type === 'deleted'
      ? 'Plantio excluído. O talhão foi mantido e está disponível ✓'
      : type === 'harvested'
        ? 'Colheita registrada. O talhão está disponível para um novo plantio ✓'
        : type === 'updated'
          ? 'Informações do plantio atualizadas em todo o sistema ✓'
          : 'Novo plantio cadastrado no talhão ✓');
    if (previewMode) showPreviewNotice('Ciclo atualizado somente nesta prévia local.');
  };

  const atualizarSugestaoAderencia = useCallback(candidate => {
    setSugestaoAderencia(current => {
      if (!candidate) return current ? null : current;
      if (current
        && current.limiteId === candidate.limiteId
        && current.tipo === candidate.tipo
        && Math.abs(current.lat - candidate.lat) < 1e-8
        && Math.abs(current.lng - candidate.lng) < 1e-8) return current;
      return candidate;
    });
  }, []);

  const adicionarPonto = useCallback((latlng) => {
    setPontosDesenho(prev => {
      const previous = prev[prev.length - 1];
      if (previous && Math.abs(previous.lat - latlng.lat) < 1e-10 && Math.abs(previous.lng - latlng.lng) < 1e-10) {
        return prev;
      }
      return [...prev, ...expandirTrechoAderente(previous, latlng, limitesAderencia)];
    });
    setVerticeSelecionado(null);
    setErroGeometria('');
    setCadastroTerritorialErro('');
  }, [limitesAderencia]);

  const moverPontoDesenho = useCallback((index, latlng) => {
    setPontosDesenho(prev => prev.map((p, i) => (i === index ? latlng : p)));
    setVerticeSelecionado(index);
    setErroGeometria('');
    setCadastroTerritorialErro('');
  }, []);

  const removerPontoDesenho = useCallback((index) => {
    if (pontosDesenho.length <= 3) {
      setErroGeometria('Um polígono precisa manter pelo menos três vértices.');
      return;
    }
    setPontosDesenho(prev => prev.filter((_, i) => i !== index));
    setVerticeSelecionado(current => {
      if (current === null || current === index) return null;
      return current > index ? current - 1 : current;
    });
    setErroGeometria('');
    setCadastroTerritorialErro('');
  }, [pontosDesenho.length]);

  const adicionarVerticeNaAresta = useCallback((insertIndex, point) => {
    setPontosDesenho(current => inserirVertice(current, insertIndex, point));
    setVerticeSelecionado(insertIndex);
    setErroGeometria('');
    setCadastroTerritorialErro('');
  }, []);

  const desfazerUltimoPonto = useCallback(() => {
    setPontosDesenho(prev => prev.length ? prev.slice(0, -1) : prev);
    setVerticeSelecionado(null);
    setErroGeometria('');
    setCadastroTerritorialErro('');
  }, []);

  const validarTalhaoDesenhado = useCallback(geometria => {
    const isPlotGeometry = cadastroTerritorial?.tipo === 'talhao'
      || (!cadastroTerritorial && escopoSelecionado?.tipo === 'talhao' && talhaoId);
    if (!isPlotGeometry) return { valido: true, codigo: 'nao_aplicavel', mensagem: '' };

    const propertyId = cadastroTerritorial?.form?.propriedadeId || propId;
    const property = propriedades.find(item => String(item.id) === String(propertyId));
    const existingPlots = talhoes.filter(item => (
      item?.geometria && String(item.id) !== String(talhaoId)
    ));
    return validarGeometriaTalhao({
      geometria,
      propriedade: property?.geometria || null,
      talhoesExistentes: existingPlots,
    });
  }, [cadastroTerritorial, escopoSelecionado?.tipo, propId, propriedades, talhaoId, talhoes]);

  const validacaoDesenhoEmTempoReal = useMemo(() => {
    if (!modoDesenho || pontosDesenho.length < 3) {
      return { valido: false, codigo: 'incompleto', mensagem: '' };
    }
    const pontosNormalizados = expandirPoligonoAderente(pontosDesenho, limitesAderencia);
    return validarTalhaoDesenhado(leafletParaGeoJson(pontosNormalizados));
  }, [limitesAderencia, modoDesenho, pontosDesenho, validarTalhaoDesenhado]);

  const desenhoPodeConcluir = pontosDesenho.length >= 3 && validacaoDesenhoEmTempoReal.valido;
  const erroGeometriaVisivel = erroGeometria
    || (modoDesenho && pontosDesenho.length >= 3 && !validacaoDesenhoEmTempoReal.valido
      ? validacaoDesenhoEmTempoReal.mensagem
      : '');

  useEffect(() => {
    if (!modoDesenho) return undefined;

    const handleDrawingKeyboard = event => {
      const target = event.target;
      const isEditingText = typeof HTMLElement !== 'undefined' && target instanceof HTMLElement
        && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
      if (isEditingText) return;

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        desfazerUltimoPonto();
        return;
      }

      if ((event.key === 'Delete' || event.key === 'Backspace') && verticeSelecionado !== null) {
        event.preventDefault();
        removerPontoDesenho(verticeSelecionado);
      }
    };

    window.addEventListener('keydown', handleDrawingKeyboard);
    return () => window.removeEventListener('keydown', handleDrawingKeyboard);
  }, [desfazerUltimoPonto, modoDesenho, removerPontoDesenho, verticeSelecionado]);

  const selecionarModoMedicao = useCallback(tipo => {
    setModoMedicao(tipo);
    setPontosMedicao([]);
    setCursorMedicao(null);
  }, []);

  const adicionarPontoMedicao = useCallback(ponto => {
    setPontosMedicao(atuais => {
      if (modoMedicao === 'circulo' && atuais.length >= 2) return [ponto];
      return [...atuais, ponto];
    });
    setCursorMedicao(null);
  }, [modoMedicao]);

  const limparMedicao = useCallback(() => {
    setPontosMedicao([]);
    setCursorMedicao(null);
  }, []);

  const fecharMedicao = useCallback(() => {
    setMedicaoAberta(false);
    setModoMedicao(null);
    setPontosMedicao([]);
    setCursorMedicao(null);
  }, []);

  useEffect(() => {
    if (!modoMedicao) return undefined;

    const handleMeasurementKeyboard = event => {
      const target = event.target;
      const isEditingText = typeof HTMLElement !== 'undefined' && target instanceof HTMLElement
        && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
      if (isEditingText) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        fecharMedicao();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        setPontosMedicao(atuais => atuais.slice(0, -1));
      }
    };

    window.addEventListener('keydown', handleMeasurementKeyboard);
    return () => window.removeEventListener('keydown', handleMeasurementKeyboard);
  }, [fecharMedicao, modoMedicao]);

  const iniciarEdicaoPoligono = () => {
    if (!propertyActionAllowed(propriedadeSelecionada, 'propriedades', 'edit', true)) { setMsgSalvo('Seu acesso permite consultar o mapa, mas não alterar os talhões.'); return; }
    if (!poligono) return;
    fecharMedicao();
    geometriaAntesEdicaoRef.current = poligono;
    const coords = poligono.geometry.coordinates[0];
    const pontos = coords.slice(0, -1).map(([lng, lat]) => ({ lat, lng }));
    setPontosDesenho(pontos);
    setVerticeSelecionado(null);
    setSugestaoAderencia(null);
    setErroGeometria('');
    setAlteracaoPoligonoPendente(false);
    setSugestaoAderencia(null);
    setErroGeometria('');
    setControlesPoligonoAbertos(true);
    setModoDesenho(true);
  };

  const cancelarEdicaoPoligono = () => {
    const geometriaOriginal = geometriaAntesEdicaoRef.current || poligono;
    setPoligono(geometriaOriginal || null);
    setPontosDesenho([]);
    setVerticeSelecionado(null);
    setSugestaoAderencia(null);
    setErroGeometria('');
    setCadastroTerritorialErro('');
    setModoDesenho(false);
    setFitPoly(geometriaOriginal ? geojsonParaLeaflet(geometriaOriginal) : null);
    setControlesPoligonoAbertos(false);
    setMsgSalvo(geometriaOriginal ? 'Edição cancelada. O perímetro salvo foi restaurado.' : 'Desenho cancelado.');
    geometriaAntesEdicaoRef.current = null;
  };

  const encerrarPoligono = () => {
    if (!desenhoPodeConcluir) {
      if (pontosDesenho.length >= 3 && validacaoDesenhoEmTempoReal.mensagem) {
        setErroGeometria(validacaoDesenhoEmTempoReal.mensagem);
        if (cadastroTerritorial) setCadastroTerritorialErro(validacaoDesenhoEmTempoReal.mensagem);
      }
      return;
    }
    const pontosFinais = expandirPoligonoAderente(pontosDesenho, limitesAderencia);
    setPontosDesenho(pontosFinais);
    const geo = leafletParaGeoJson(pontosFinais);
    const validation = validarTalhaoDesenhado(geo);
    if (!validation.valido) {
      setErroGeometria(validation.mensagem);
      if (cadastroTerritorial) setCadastroTerritorialErro(validation.mensagem);
      setMsgSalvo('');
      return;
    }
    const calculatedArea = calcularAreaHectares(geo);
    setPoligono(geo);
    setFitPoly(geojsonParaLeaflet(geo));
    setPontosDesenho([]);
    setVerticeSelecionado(null);
    setSugestaoAderencia(null);
    setErroGeometria('');
    setCadastroTerritorialErro('');
    setModoDesenho(false);
    geometriaAntesEdicaoRef.current = null;
    analysisRequestIdRef.current += 1;
    automaticAnalysisKeyRef.current = '';
    setCalculandoStats(false);
    setStatsNdvi(null);
    setComparacaoNdvi(null);
    setZonasManejo(null);

    if (cadastroTerritorial) {
      setCadastroTerritorial(current => current ? {
        ...current,
        etapa: 'dados',
        form: { ...current.form, area: calculatedArea },
      } : current);
      setMsgSalvo(`Perímetro medido: ${formatarAreaHectares(calculatedArea)}.`);
      return;
    }

    setTalhao(current => current ? { ...current, geometria: geo, area: calculatedArea || current.area } : current);
    setTalhoes(current => current.map(item => (
      String(item.id) === String(talhaoId) ? { ...item, geometria: geo, area: calculatedArea || item.area } : item
    )));
    setAlteracaoPoligonoPendente(true);
    setMsgSalvo('Desenho ajustado. Salve as alterações para confirmar.');
  };

  const limparPoligono = () => {
    if (previewMode && !cadastroTerritorial) {
      showPreviewNotice();
      return;
    }
    analysisRequestIdRef.current += 1;
    automaticAnalysisKeyRef.current = '';
    setCalculandoStats(false);
    setPoligono(null);
    setPontosDesenho([]);
    setSugestaoAderencia(null);
    setErroGeometria('');
    setModoDesenho(false);
    setFitPoly(null);
    setCenas([]);
    setTileUrl('');
    setCenaUrl('');
    setCenaFeature(null);
    setStatsNdvi(null);
    setComparacaoNdvi(null);
    setZonasManejo(null);
    setMsgSalvo('');
    setAlteracaoPoligonoPendente(false);
    if (cadastroTerritorial) {
      setCadastroTerritorial(current => current ? {
        ...current,
        etapa: 'desenho',
        form: { ...current.form, area: null },
      } : current);
      setModoDesenho(true);
    }
  };

  const salvarPoligono = async () => {
    if (!propertyActionAllowed(propriedadeSelecionada, 'propriedades', 'edit', true)) { setMsgSalvo('Alteração não autorizada nesta propriedade.'); return; }
    if (!poligono || !talhaoId) return;
    const validation = validarTalhaoDesenhado(poligono);
    if (!validation.valido) {
      setErroGeometria(validation.mensagem);
      setMsgSalvo('');
      return;
    }
    if (previewMode) {
      setTalhao(current => current ? { ...current, geometria: poligono, isDraft: false } : current);
      setTalhoes(current => current.map(item => (
        String(item.id) === String(talhaoId) ? { ...item, geometria: poligono, isDraft: false } : item
      )));
      setMsgSalvo(`Talhão ${talhao?.nome || ''} salvo na prévia ✓`);
      setAlteracaoPoligonoPendente(false);
      setControlesPoligonoAbertos(false);
      showPreviewNotice('Talhão salvo nesta simulação local. Nenhum dado do banco foi alterado.');
      return;
    }
    setSalvando(true);
    const calculatedArea = calcularAreaHectares(poligono);
    if (talhao?._operationalPhysical) {
      const physicalPlotId = talhao._physicalPlotId ?? talhao.id;
      const center = calcularCentroGeometria(poligono);
      try {
        await updateOperationalPlot({
          plotId: physicalPlotId,
          propertyId: propId,
          geometry: poligono,
          area: calculatedArea,
          coordinateX: center?.lng ?? null,
          coordinateY: center?.lat ?? null,
        });
        const updates = {
          geometria: poligono,
          area: calculatedArea,
          area_ha: calculatedArea,
          coordenada_x: center?.lng ?? null,
          coordenada_y: center?.lat ?? null,
        };
        setTalhoes(current => current.map(item => (
          String(item.id) === String(talhaoId) ? { ...item, ...updates } : item
        )));
        setTalhao(current => {
          const currentPhysicalId = current?._physicalPlotId ?? current?.id;
          return current && String(currentPhysicalId) === String(physicalPlotId)
            ? { ...current, ...updates }
            : current;
        });
        setPropriedades(current => current.map(property => (
          String(property.id) !== String(propId) ? property : {
            ...property,
            _operationalPlots: (property._operationalPlots || []).map(plot => (
              String(plot.id) === String(physicalPlotId) ? { ...plot, ...updates } : plot
            )),
          }
        )));
        setMsgSalvo('Polígono salvo ✓');
        setAlteracaoPoligonoPendente(false);
        setControlesPoligonoAbertos(false);
        setTimeout(() => setMsgSalvo(''), 3000);
      } catch (error) {
        setMsgSalvo(`Não foi possível salvar o polígono: ${error?.message || 'erro inesperado'}`);
      } finally {
        setSalvando(false);
      }
      return;
    }

    const { error } = await supabase
      .from('talhoes')
      .update({ geometria: poligono, area: calculatedArea })
      .eq('id', talhaoId);
    setSalvando(false);
    if (!error) {
      setMsgSalvo('Polígono salvo ✓');
      setAlteracaoPoligonoPendente(false);
      setControlesPoligonoAbertos(false);
      // Atualiza cache local dos talhões
      setTalhoes(prev => prev.map(t =>
        String(t.id) === talhaoId ? { ...t, geometria: poligono, area: calculatedArea } : t
      ));
      setTalhao(current => current ? { ...current, geometria: poligono, area: calculatedArea } : current);
      setTimeout(() => setMsgSalvo(''), 3000);
    }
  };

  const buscarImagens = async () => {
    if (!poligono) return;
    if (previewMode) {
      setCenas(previewScenes);
      setCenaFeature(previewScene);
      setCenaUrl(previewScene ? getStacSelfUrl(previewScene) : '');
      setStatsNdvi(previewData?.statsNdvi || null);
      setComparacaoNdvi(previewData?.temporalComparison || null);
      showPreviewNotice('Busca simulada concluída com dados fictícios.');
      return;
    }
    setBuscando(true);
    analysisRequestIdRef.current += 1;
    automaticAnalysisKeyRef.current = '';
    setCalculandoStats(false);
    setErroStac('');
    setCenas([]);
    setTileUrl('');
    setCenaUrl('');
    setCenaFeature(null);
    setStatsNdvi(null);
    setComparacaoNdvi(null);
    setZonasManejo(null);
    setErroTile(false);
    try {
      const pts = poligono.geometry.coordinates[0].map(c => ({ lng: c[0], lat: c[1] }));
      const bbox = poligonoParaBbox(pts);
      const resultado = await buscarCenas(bbox, dataInicio, dataFim, maxNuvens);
      setCenas(resultado);
      if (resultado.length === 0) {
        setErroStac('Nenhuma cena encontrada. Tente ampliar o período ou aumentar o % de nuvens permitido.');
      }
    } catch {
      setErroStac('Erro ao buscar imagens. Verifique sua conexão.');
    }
    setBuscando(false);
  };

  const selecionarCena = async feature => {
    const manterNdviAtivo = abaResultados === 'analise';
    analysisRequestIdRef.current += 1;
    automaticAnalysisKeyRef.current = '';
    setCenaUrl(getStacSelfUrl(feature));
    setCenaFeature(feature);
    setAbaResultados(manterNdviAtivo ? 'analise' : 'imagens');
    setTipoImagem(manterNdviAtivo ? 'ndvi' : 'rgb');
    setErroTile(false);
    if (previewMode) {
      setStatsNdvi(previewData?.statsNdvi || null);
      setComparacaoNdvi(previewData?.temporalComparison || null);
      showPreviewNotice('Cena selecionada na demonstração local.');
      return;
    }
    setStatsNdvi(null);
    setComparacaoNdvi(null);
    setErroStats('');
    setZonasManejo(null);
    setErroZonas('');
    if (manterNdviAtivo && poligono) {
      const polygonKey = JSON.stringify(poligono?.geometry?.coordinates?.[0] || []);
      automaticAnalysisKeyRef.current = `${feature.id}:${polygonKey}`;
      await executarAnaliseReal(feature, poligono, cenas);
    }
  };

  const gerarZonasManejo = async () => {
    if (!cenaFeature || !poligono) return;
    if (previewMode) {
      setAbaResultados('analise');
      showPreviewNotice('Mapa de zonas demonstrado sem processar dados externos.');
      return;
    }
    setGerandoZonas(true);
    setErroZonas('');
    setZonasManejo(null);
    try {
      const zonas = await gerarMapaZonasManejo(cenaFeature, poligono);
      setZonasManejo(zonas);
      setMostrarZonas(true);
    } catch (err) {
      setErroZonas('Não foi possível gerar o mapa de zonas. Tente novamente.');
      console.warn('[Sensoriamento] erro mapa de zonas', err?.message);
    }
    setGerandoZonas(false);
  };

  const gerarAnaliseTerreno = async (fonte = fonteTerreno, preservarVisualizacao = false) => {
    if (!poligono || analisandoTerreno) return;
    const requestId = ++terrenoRequestIdRef.current;
    setAnalisandoTerreno(true);
    setErroTerreno('');
    try {
      const resultado = await analisarTerreno(poligono, fonte);
      if (requestId !== terrenoRequestIdRef.current) return;
      setDadosTerreno(resultado);
      if (!preservarVisualizacao) {
        setCamadaTerreno('altitude');
        setMostrarCurvasNivel(true);
        setIntervaloCurvasSelecionado(resultado.intervaloCurvas);
      }
    } catch (err) {
      if (requestId !== terrenoRequestIdRef.current) return;
      setErroTerreno(err?.message || 'Não foi possível analisar o relevo da área selecionada.');
      console.warn('[Sensoriamento] erro análise de terreno', err?.message);
    } finally {
      if (requestId === terrenoRequestIdRef.current) setAnalisandoTerreno(false);
    }
  };

  const trocarFonteTerreno = async (fonte) => {
    setFonteTerreno(fonte);
    setDadosTerreno(null);
    setErroRelatorioTerreno('');
    await gerarAnaliseTerreno(fonte, true);
  };

  const gerarNdviDoEscopoSelecionado = async () => {
    if (!poligono) {
      showPreviewNotice('O perímetro selecionado ainda não possui um desenho georreferenciado.');
      return;
    }
    if (!cenaFeature) {
      setAbaResultados('imagens');
      if (!isMobile) setPainelTerritorialRecolhido(false);
      if (isMobile) setPainelAberto(true);
      return;
    }
    setTipoImagem('ndvi');
    setAbaResultados('imagens');
    setMostrarImagemSatelite(true);
    if (!isMobile) setPainelTerritorialRecolhido(false);
    if (isMobile) setPainelAberto(true);
    if (previewMode) {
      setMapaNdviRecortado(gerarMapaNdviDemonstrativo(poligono));
      setErroNdviRecortado('');
      showPreviewNotice('NDVI demonstrado somente dentro do perímetro selecionado.');
    }
  };

  const gerarCurvasDoEscopoSelecionado = async () => {
    if (!poligono) {
      showPreviewNotice('O perímetro selecionado ainda não possui um desenho georreferenciado.');
      return;
    }
    setAbaResultados('terreno');
    setMostrarCurvasNivel(true);
    if (!isMobile) setPainelTerritorialRecolhido(false);
    if (isMobile) setPainelAberto(true);
    await gerarAnaliseTerreno();
    setCamadaTerreno('curvas');
  };

  const executarAnaliseReal = useCallback(async (feature, polygon, sceneList) => {
    if (!feature || !polygon) return;
    const requestId = ++analysisRequestIdRef.current;
    setCalculandoStats(true);
    setErroStats('');
    setStatsNdvi(null);
    setComparacaoNdvi(null);
    try {
      const cenaAnterior = encontrarCenaAnterior(sceneList, feature);
      const resultados = await Promise.allSettled([
        calcularEstatisticasNDVI(feature, polygon),
        cenaAnterior ? calcularEstatisticasNDVI(cenaAnterior, polygon) : Promise.resolve(null),
      ]);

      if (requestId !== analysisRequestIdRef.current) return;
      const [resultadoAtual, resultadoAnterior] = resultados;
      if (resultadoAtual.status === 'rejected') throw resultadoAtual.reason;

      const statsAtual = resultadoAtual.value;
      setStatsNdvi(statsAtual);

      if (!cenaAnterior) {
        setComparacaoNdvi({
          status: 'sem_cena_anterior',
          dataAtual: formatarDataCena(feature),
        });
      } else if (resultadoAnterior.status === 'fulfilled' && resultadoAnterior.value) {
        const evolucao = calcularEvolucaoNdvi(statsAtual.mean, resultadoAnterior.value.mean);
        setComparacaoNdvi({
          status: 'sucesso',
          ...evolucao,
          dataAtual: formatarDataCena(feature),
          dataAnterior: formatarDataCena(cenaAnterior),
          cenaAtualId: feature.id,
          cenaAnteriorId: cenaAnterior.id,
        });
      } else {
        setComparacaoNdvi({
          status: 'erro_cena_anterior',
          dataAtual: formatarDataCena(feature),
          dataAnterior: formatarDataCena(cenaAnterior),
        });
        console.warn('[Sensoriamento] erro comparação temporal NDVI', resultadoAnterior.reason?.message);
      }
    } catch (err) {
      if (requestId !== analysisRequestIdRef.current) return;
      setErroStats('Não foi possível calcular os indicadores desta imagem. Tente novamente.');
      console.warn('[Sensoriamento] erro estatísticas NDVI', err?.message);
    } finally {
      if (requestId === analysisRequestIdRef.current) setCalculandoStats(false);
    }
  }, []);

  const analisarVariabilidadeNdvi = async () => {
    if (!cenaFeature || !poligono) return;
    if (previewMode) {
      setStatsNdvi(previewData?.statsNdvi || null);
      setComparacaoNdvi(previewData?.temporalComparison || null);
      showPreviewNotice('Análise NDVI demonstrada com dados fictícios.');
      return;
    }
    const polygonKey = JSON.stringify(poligono?.geometry?.coordinates?.[0] || []);
    automaticAnalysisKeyRef.current = `${cenaFeature.id}:${polygonKey}`;
    await executarAnaliseReal(cenaFeature, poligono, cenas);
  };

  const abrirNdvi = async () => {
    if (!cenaFeature || !poligono) return;
    setAbaResultados('analise');
    setTipoImagem('ndvi');
    if (isMobile) setPainelAberto(true);
    if (!isMobile) setPainelTerritorialRecolhido(false);
    await analisarVariabilidadeNdvi();
  };

  const excluirCena = (id) => {
    const novas = cenas.filter(f => f.id !== id);
    setCenas(novas);
    if (cenaFeature?.id === id) {
      if (novas.length > 0) {
        selecionarCena(novas[0]);
      } else {
        setTileUrl('');
        setCenaUrl('');
        setCenaFeature(null);
        setStatsNdvi(null);
        setComparacaoNdvi(null);
      }
    }
  };

  const obterContextoRelatorio = () => ({
    propriedade: propriedades.find(item => String(item.id) === String(propId))?.nome || 'Propriedade não informada',
    area: escopoSelecionado?.tipo === 'propriedade' ? 'Propriedade completa' : talhao?.nome || 'Área desenhada',
    areaHa: calcularAreaHectares(poligono),
  });

  const nomeRelatorio = (tipo, contexto, referencia) => (
    `${tipo}_${contexto.propriedade}_${contexto.area}_${referencia}`.replace(/[^a-zA-Z0-9_-]/g, '_') + '.pdf'
  );

  const gerarRelatorioNdvi = async () => {
    if (previewMode) {
      showPreviewNotice('O relatório será habilitado após a aprovação da experiência.');
      return;
    }
    setGerandoRelatorioNdvi(true);
    setErroRelatorioNdvi('');
    try {
      if (!cenaFeature || !poligono) throw new Error('Selecione uma cena e uma área para gerar o relatório.');
      const contexto = obterContextoRelatorio();
      const [stats, mapaNdvi, zonas, logo] = await Promise.all([
        calcularEstatisticasNDVI(cenaFeature, poligono),
        gerarMapaNdviRecortado(cenaFeature, poligono),
        gerarMapaZonasManejo(cenaFeature, poligono),
        carregarLogoRelatorio(),
      ]);
      const documento = await criarRelatorioNdvi({ contexto, poligono, cena: cenaFeature, stats, mapaNdvi, zonas, logo });
      const dataCena = cenaFeature.properties?.datetime?.split('T')[0] || 'sem_data';
      await salvarRelatorioPdf(documento, nomeRelatorio('ndvi_zonas', contexto, dataCena));
    } catch (error) {
      setErroRelatorioNdvi(`Não foi possível gerar o relatório de NDVI e zonas: ${error.message}`);
      console.error('[Sensoriamento] erro relatório NDVI e zonas', error);
    } finally {
      setGerandoRelatorioNdvi(false);
    }
  };

  const gerarRelatorioTerreno = async () => {
    if (previewMode) {
      showPreviewNotice('O relatório será habilitado após a aprovação da experiência.');
      return;
    }
    setGerandoRelatorioTerreno(true);
    setErroRelatorioTerreno('');
    try {
      if (!dadosTerreno || !poligono) throw new Error('Analise o terreno antes de gerar o relatório.');
      const contexto = obterContextoRelatorio();
      const logo = await carregarLogoRelatorio();
      const documento = await criarRelatorioTerreno({
        contexto, poligono, terreno: dadosTerreno, intervalo: intervaloCurvasSelecionado, logo,
      });
      await salvarRelatorioPdf(documento, nomeRelatorio('terreno', contexto, `${dadosTerreno.fonteId}_${intervaloCurvasSelecionado}m`));
    } catch (error) {
      setErroRelatorioTerreno(`Não foi possível gerar o relatório de terreno: ${error.message}`);
      console.error('[Sensoriamento] erro relatório terreno', error);
    } finally {
      setGerandoRelatorioTerreno(false);
    }
  };

  const gerarRelatorio = async () => {
    if (!cenas.length || !poligono) return;
    if (previewMode) {
      showPreviewNotice('O relatório será habilitado após a aprovação da experiência.');
      return;
    }
    setGerandoRelatorio(true);
    try {
      const logoBase64 = await carregarLogoRelatorio();

      const pts = poligono.geometry.coordinates[0].map(c => ({ lng: c[0], lat: c[1] }));
      const bbox = poligonoParaBbox(pts);
      const propNome = propriedades.find(p => String(p.id) === propId)?.nome ?? '-';
      const talhaoNome = talhao?.nome ?? '-';

      // Helpers de design — padrão relatório de talhão
      const divider = { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 0.5, lineColor: '#e8e8e8' }], margin: [0, 4, 0, 6] };
      const sectionTitle = (label, color) => ({
        columns: [
          { canvas: [{ type: 'rect', x: 0, y: 0, w: 3, h: 11, color, lineWidth: 0 }], width: 7 },
          { text: label, color: '#333', fontSize: 9, bold: true, margin: [0, 1, 0, 0] },
        ],
        margin: [0, 8, 0, 4],
      });
      const tblLayout = { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 };

      const content = [];

      // ── Cabeçalho branco — padrão dos demais relatórios ────────────────
      content.push(criarCabecalhoRelatorio('Relatório de Imagens de Satélite', {
        propriedade: propNome, area: talhaoNome,
      }, logoBase64));
      content.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 1, lineColor: '#e0e0e0' }], margin: [0, 0, 0, 10] });

      // ── Cards de parâmetros com fundo ─────────────────────────────────────
      content.push(sectionTitle('PARÂMETROS DA ANÁLISE', '#1976d2'));
      content.push({
        table: {
          widths: ['*', '*', 78, 72],
          body: [[
            { stack: [{ text: 'SATÉLITE', fontSize: 6, color: '#78909C', bold: true }, { text: 'Sentinel-2 L2A (ESA)', fontSize: 8, bold: true, color: '#003B73', margin: [0, 3, 0, 0] }], fillColor: '#F5F9FF', border: [false, false, true, false], borderColor: ['','','#BBDEFB',''], margin: [8, 7, 8, 7] },
            { stack: [{ text: 'PERÍODO', fontSize: 6, color: '#78909C', bold: true }, { text: `${dataInicio} a ${dataFim}`, fontSize: 8, bold: true, color: '#003B73', margin: [0, 3, 0, 0] }], fillColor: '#F5F9FF', border: [false, false, true, false], borderColor: ['','','#BBDEFB',''], margin: [8, 7, 8, 7] },
            { stack: [{ text: 'NUVENS MÁX.', fontSize: 6, color: '#78909C', bold: true }, { text: `${maxNuvens}%`, fontSize: 13, bold: true, color: '#546e7a', margin: [0, 2, 0, 0] }], fillColor: '#F5F9FF', border: [false, false, true, false], borderColor: ['','','#BBDEFB',''], margin: [8, 6, 8, 6], alignment: 'center' },
            { stack: [{ text: 'CENAS', fontSize: 6, color: '#1565C0', bold: true }, { text: String(cenas.length), fontSize: 20, bold: true, color: '#1976d2', margin: [0, 0, 0, 0] }], fillColor: '#E3F2FD', border: [false, false, false, false], margin: [8, 4, 8, 4], alignment: 'center' },
          ]],
        },
        layout: { hLineWidth: () => 0, vLineWidth: (i, n) => (i === 0 || i === n.table.widths.length) ? 0 : 0.5, vLineColor: () => '#BBDEFB', paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
        margin: [0, 0, 0, 8],
      });

      content.push(divider);
      content.push(sectionTitle('IMAGENS POR DATA', '#2e7d32'));

      // Legenda em caixa — apenas produtos selecionados
      const legendaCols = [];
      if (camadasRelatorio.rgb) {
        legendaCols.push({ canvas: [{ type: 'rect', x: 0, y: 3, w: 9, h: 9, color: '#4CAF50', lineWidth: 0 }], width: 13 });
        legendaCols.push({ text: 'RGB - cor natural (R, G, B)', fontSize: 7, color: '#333', margin: [0, 2, 12, 0] });
      }
      if (camadasRelatorio.cir) {
        legendaCols.push({ canvas: [{ type: 'rect', x: 0, y: 3, w: 9, h: 9, color: '#880E4F', lineWidth: 0 }], width: 13 });
        legendaCols.push({ text: 'CIR - infravermelho cor (NIR, R, G)', fontSize: 7, color: '#333', margin: [0, 2, 12, 0] });
      }
      if (camadasRelatorio.ndvi) {
        legendaCols.push({ canvas: [{ type: 'rect', x: 0, y: 3, w: 9, h: 9, color: '#1B5E20', lineWidth: 0 }], width: 13 });
        legendaCols.push({ text: 'NDVI - (NIR-Red)/(NIR+Red) | vermelho = solo/agua  >  verde = alta vegetacao', fontSize: 7, color: '#333', margin: [0, 2, 0, 0] });
      }
      if (legendaCols.length > 0) {
        content.push({
          table: {
            widths: ['*'],
            body: [[{
              columns: legendaCols,
              fillColor: '#F9FBE7', border: [false, false, false, true], borderColor: ['','','','#C5E1A5'], margin: [8, 6, 8, 6],
            }]],
          },
          layout: tblLayout,
          margin: [0, 0, 0, 10],
        });
      }

      // ── Uma seção por cena ────────────────────────────────────────────────
      let cenaIdx = 0;
      for (let i = 0; i < cenas.length; i++) {
        const feature = cenas[i];
        const dataCena = feature.properties?.datetime?.split('T')[0] ?? '?';
        const nuvensPct = typeof feature.properties?.['eo:cloud_cover'] === 'number' ? feature.properties['eo:cloud_cover'] : null;
        const nuvens = nuvensPct !== null ? nuvensPct.toFixed(1) : '?';
        const nuvensCor = nuvensPct === null ? '#78909C' : nuvensPct < 10 ? '#2e7d32' : nuvensPct < 20 ? '#f57f17' : '#b71c1c';

        // 1 request por camada via /cog/bbox/ — apenas camadas habilitadas
        const imgRgb  = camadasRelatorio.rgb  ? await getPreviewImage(feature, 'rgb',  bbox).catch(() => null) : null;
        const imgCir  = camadasRelatorio.cir  ? await getPreviewImage(feature, 'cir',  bbox).catch(() => null) : null;
        const imgNdvi = camadasRelatorio.ndvi ? await getPreviewImage(feature, 'ndvi', bbox).catch(() => null) : null;

        if (!imgRgb && !imgCir && !imgNdvi) continue;

        // Largura dinâmica por coluna conforme quantas imagens existem
        const nImgs = [imgRgb, imgCir, imgNdvi].filter(Boolean).length;
        const PAGE_W = 535; // largura útil A4 com margens 28pt
        const colWidth = nImgs === 1 ? PAGE_W : nImgs === 2 ? Math.floor((PAGE_W - 6) / 2) : Math.floor((PAGE_W - 12) / 3);

        // Header + imagens agrupados num table com dontBreakRows para evitar corte entre páginas
        const makeImgCol = (img, label, labelCor, labelBg) => img
          ? { stack: [
              { table: { widths: [colWidth], body: [[{ text: label, fontSize: 6, bold: true, color: labelCor, fillColor: labelBg, border: [false, false, false, false], margin: [4, 2, 4, 2] }]] }, layout: tblLayout, margin: [0, 0, 0, 2] },
              { image: img, width: colWidth },
            ]}
          : null;

        const sceneHeader = {
          table: {
            widths: ['*', 100],
            body: [[
              {
                columns: [
                  { canvas: [{ type: 'rect', x: 0, y: 1, w: 3, h: 10, color: '#2e7d32', lineWidth: 0 }], width: 7 },
                  { text: `Cena ${cenaIdx + 1}  •  ${dataCena}`, bold: true, fontSize: 9, color: '#1B5E20', margin: [0, 1, 0, 0] },
                ],
                fillColor: '#F1F8E9', border: [true, true, false, true], borderColor: ['#C8E6C9','#C8E6C9','','#C8E6C9'], margin: [6, 5, 0, 5],
              },
              {
                text: [{ text: 'Nuvens: ', fontSize: 7, color: '#78909C' }, { text: `${nuvens}%`, bold: true, fontSize: 9, color: nuvensCor }],
                alignment: 'right',
                fillColor: '#F1F8E9', border: [false, true, true, true], borderColor: ['','#C8E6C9','#C8E6C9','#C8E6C9'], margin: [0, 6, 8, 5],
              },
            ]],
          },
          layout: tblLayout,
          margin: [0, 0, 0, 4],
        };

        const sceneImages = {
          columns: [
            makeImgCol(imgRgb,  'RGB — COR NATURAL',         '#1565C0', '#E3F2FD'),
            makeImgCol(imgCir,  'CIR — INFRAVERMELHO COR',   '#880E4F', '#FCE4EC'),
            makeImgCol(imgNdvi, 'NDVI — VIGOR VEGETATIVO',   '#1B5E20', '#E8F5E9'),
          ].filter(Boolean),
          columnGap: 6,
        };

        content.push({
          table: {
            dontBreakRows: true,
            widths: ['*'],
            body: [[{ stack: [sceneHeader, sceneImages], border: [false, false, false, false], margin: [0, cenaIdx === 0 ? 0 : 8, 0, 4] }]],
          },
          layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
        });

        cenaIdx++;
      }

      content.push(divider);
      content.push({ text: 'Imagens: Sentinel-2 L2A © ESA  •  Processamento: TiTiler.xyz  •  Catálogo: Element84 Earth Search', fontSize: 7, color: '#aaa', italics: true });

      const docDefinition = {
        pageSize: 'A4',
        pageMargins: [28, 14, 28, 22],
        content,
        footer: (currentPage, pageCount) => ({
          columns: [
            { text: `Página ${currentPage} de ${pageCount}`, fontSize: 7, color: '#aaa', margin: [28, 8, 0, 0] },
            { text: `ClimoraX • ${new Date().toLocaleDateString('pt-BR')}`, fontSize: 7, color: '#1976d2', bold: true, alignment: 'right', margin: [0, 8, 28, 0] },
          ],
        }),
      };

      const nomeArq = `sensoriamento_${talhaoNome.replace(/[^a-zA-Z0-9_-]/g, '_')}_${dataInicio}_${dataFim}.pdf`;

      await salvarRelatorioPdf(docDefinition, nomeArq);
    } catch (err) {
      console.error('Erro ao gerar relatório:', err);
      alert('Erro ao gerar relatório. Verifique sua conexão e tente novamente.');
    } finally {
      setGerandoRelatorio(false);
    }
  };

  // Posições para o Leaflet
  const polyPositions = geometriaParaPosicoes(poligono);
  const propriedadeSelecionada = propriedadeParaAderencia;
  const propertyPositions = geometriaParaPosicoes(propriedadeSelecionada?.geometria);
  const limitesTerritoriaisVisiveis = mostrarPoligono || modoDesenho || Boolean(cadastroTerritorial);
  const contextoSelecionado = escopoSelecionado?.tipo === 'propriedade' ? propriedadeSelecionada : talhao;
  const contextoTipoLabel = escopoSelecionado?.tipo === 'propriedade' ? 'Propriedade' : 'Talhão';
  const contextoSelecaoLabel = escopoSelecionado?.tipo === 'propriedade' ? 'Propriedade selecionada' : 'Talhão selecionado';
  const portfolioPolygons = talhoes
    .map(item => ({ item, positions: geometriaParaPosicoes(item.geometria) }))
    .filter(entry => entry.positions && String(entry.item.id) !== String(talhaoId));
  const desenhoPositions = pontosDesenho.map(p => [p.lat, p.lng]);
  const pontosIntermediarios = modoDesenho && pontosDesenho.length >= 3
    ? criarPontosIntermediarios(pontosDesenho)
    : [];
  const areaEmDesenho = pontosDesenho.length >= 3 ? calcularAreaHectares(pontosDesenho) : null;
  const pontosMedicaoPreview = modoMedicao && cursorMedicao && pontosMedicao.length > 0
    && !(modoMedicao === 'circulo' && pontosMedicao.length >= 2)
    ? [...pontosMedicao, cursorMedicao]
    : pontosMedicao;
  const posicoesMedicao = pontosMedicaoPreview.map(ponto => [ponto.lat, ponto.lng]);
  const distanciaMedicao = modoMedicao === 'distancia' ? calcularDistanciaMedicao(pontosMedicaoPreview) : 0;
  const areaMedicao = modoMedicao === 'area' && pontosMedicaoPreview.length >= 3
    ? calcularAreaHectares(pontosMedicaoPreview)
    : 0;
  const raioMedicao = modoMedicao === 'circulo' && pontosMedicaoPreview.length >= 2
    ? calcularDistanciaMedicao(pontosMedicaoPreview.slice(0, 2))
    : 0;
  const areaCirculoMedicao = raioMedicao > 0 ? (Math.PI * raioMedicao * raioMedicao) / 10000 : 0;
  const resultadoMedicao = modoMedicao === 'distancia'
    ? formatarDistanciaMedicao(distanciaMedicao)
    : modoMedicao === 'area'
      ? formatarAreaMedicao(areaMedicao)
      : modoMedicao === 'circulo'
        ? formatarAreaMedicao(areaCirculoMedicao)
        : '';
  const areaTerritorialAtual = cadastroTerritorial?.form?.area || calcularAreaHectares(poligono) || areaEmDesenho;
  const totalPontosAderidos = pontosDesenho.filter(point => point.snapSourceId).length;
  const selectedSensorType = sensorMapaSelecionado
    ? (SENSOR_TYPE_INFO[sensorMapaSelecionado.tipo_sensor] || SENSOR_TYPE_INFO.meteorologico)
    : null;
  const selectedSensorConnection = sensorMapaSelecionado ? sensorConnection(sensorMapaSelecionado) : null;
  const selectedSensorWeather = sensorMapaSelecionado?.tipo_sensor === 'meteorologico'
    ? sensorWeatherStatus(sensorMapaSelecionado)
    : null;
  const selectedSensorReading = sensorMapaSelecionado?.latestReading || null;
  const selectedSensorMetrics = sensorMapaSelecionado?.tipo_sensor === 'umidade_solo'
    ? [
        { label: 'Umidade do solo', value: formatSensorValue(selectedSensorReading?.umidade_solo_perc, '%') },
        { label: 'Temp. do solo', value: formatSensorValue(selectedSensorReading?.temp_solo, '°C') },
      ]
    : sensorMapaSelecionado?.tipo_sensor === 'chuva_presenca'
      ? [
          { label: 'Estado atual', value: Number(selectedSensorReading?.molhado) === 1 ? 'Molhado' : Number(selectedSensorReading?.molhado) === 0 ? 'Seco' : '—' },
          { label: 'Última leitura', value: selectedSensorConnection?.detail || '—' },
        ]
      : sensorMapaSelecionado?.tipo_sensor === 'camera'
        ? [
            { label: 'Transmissão', value: sensorMapaSelecionado.stream_url || process.env.REACT_APP_CAMERA_STREAM_BASE_URL ? 'HLS' : 'Não configurada' },
            { label: 'Estado', value: selectedSensorConnection?.label || '—' },
          ]
        : [
          { label: 'Temperatura', value: formatSensorValue(selectedSensorReading?.temp_c, '°C') },
          { label: 'Umidade', value: formatSensorValue(selectedSensorReading?.rh_perc, '%') },
          { label: 'Chuva 24 h', value: formatSensorValue(sensorMapaSelecionado?.rainLast24h, ' mm') },
        ];
  const ndviDistribution = statsNdvi ? classificarVariabilidadeNdvi(statsNdvi) : null;
  const termoBuscaMapa = normalizarTexto(locationQuery.trim());
  const propriedadesEncontradas = (termoBuscaMapa
    ? propriedades.filter(item => normalizarTexto(`${item.nome} ${item.municipio || ''} ${item.estado || ''}`).includes(termoBuscaMapa))
    : propriedades
  ).slice(0, 6);
  const talhoesEncontrados = termoBuscaMapa.length >= 2
    ? talhoes.filter(item => normalizarTexto(`${item.nome} ${item.ciclo_atual?.cultura || item.cultura || ''}`).includes(termoBuscaMapa)).slice(0, 5)
    : [];
  const possuiResultadosTerritoriais = propriedadesEncontradas.length > 0 || talhoesEncontrados.length > 0;
  const sensorGroups = groupSensorsByZoom(sensoresMapa, mapZoom);
  const portfolioPositions = propriedades.flatMap(item => {
    const geometryPositions = geometriaParaPosicoes(item.geometria);
    if (geometryPositions?.length) return geometryPositions;
    const lat = Number(item.coordenada_y);
    const lng = Number(item.coordenada_x);
    return Number.isFinite(lat) && Number.isFinite(lng) ? [[lat, lng]] : [];
  });
  const shouldFitPortfolio = !previewMode
    && !initialContext?.mapView?.center
    && !propId
    && !talhaoId
    && !voo
    && !cadastroTerritorial;
  const sceneDateValue = cenaFeature?.properties?.datetime
    ? new Date(cenaFeature.properties.datetime).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
    : 'Sem data';
  const hasTemporalComparison = comparacaoNdvi?.status === 'sucesso'
    && Number.isFinite(comparacaoNdvi?.changePct);
  const temporalDecline = hasTemporalComparison && comparacaoNdvi.changePct <= -5;
  const comparisonLabel = hasTemporalComparison
    ? `${comparacaoNdvi.dataAnterior} → ${comparacaoNdvi.dataAtual}`
    : comparacaoNdvi?.status === 'erro_cena_anterior'
      ? 'Não foi possível analisar a cena anterior'
      : 'Sem cena anterior analisada';
  const operationalInsights = previewData?.insights || (statsNdvi ? {
    ndvi: statsNdvi.mean,
    changePct: hasTemporalComparison ? comparacaoNdvi.changePct : null,
    comparisonLabel,
    attentionPct: ndviDistribution?.baixoPct || 0,
    attentionArea: contextoSelecionado?.area ? `${((contextoSelecionado.area * (ndviDistribution?.baixoPct || 0)) / 100).toFixed(1)} ha` : 'da área selecionada',
    imageDate: sceneDateValue,
    cloudPct: Number(cenaFeature?.properties?.['eo:cloud_cover'] || 0).toFixed(1),
    culture: contextoSelecionado?.cultura || (escopoSelecionado?.tipo === 'propriedade' ? 'Propriedade completa' : 'Cultura monitorada'),
    area: contextoSelecionado?.area ? `${contextoSelecionado.area} ha` : 'Área cadastrada',
    recommendation: temporalDecline
      ? 'Investigar a redução de vigor entre imagens'
      : (ndviDistribution?.baixoPct || 0) >= 10
        ? 'Inspecionar a zona de menor vigor'
        : 'Manter o acompanhamento periódico',
    recommendationDetail: temporalDecline
      ? `O NDVI médio caiu ${Math.abs(comparacaoNdvi.changePct).toFixed(1)}% entre ${comparacaoNdvi.dataAnterior} e ${comparacaoNdvi.dataAtual}. Confirme as condições em campo antes de intervir.`
      : (ndviDistribution?.baixoPct || 0) >= 10
        ? 'Compare a área indicada com solo, pragas, falhas e disponibilidade hídrica em campo.'
        : 'O talhão apresenta boa uniformidade na leitura mais recente.',
  } : null);

  const abrirPainelAnalise = () => {
    setAbaResultados('analise');
    if (isMobile) {
      setPainelAberto(true);
      window.setTimeout(() => {
        analysisSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  };

  // ── Blocos de conteúdo reutilizados entre sidebar (desktop) e bottom sheet (mobile) ──

  const renderControlePoligono = () => (
    <div style={s.polyControlStack}>
      {!modoDesenho && !poligono && (
        <button style={s.btnPrimary} onClick={() => setModoDesenho(true)}>
          ✏️ Desenhar área do talhão
        </button>
      )}

      {modoDesenho && (
        <>
          <div style={s.hintBox}>
            {poligono
              ? `✏️ Arraste os pontos para ajustar ou use os botões + nas arestas para acrescentar vértices (${pontosDesenho.length} pontos)`
              : `📍 Toque no mapa para marcar pontos e use Ctrl+Z para desfazer (${pontosDesenho.length} marcados)`}
          </div>
          {erroGeometriaVisivel && <p className="remote-geometry-error" role="alert">{erroGeometriaVisivel}</p>}
          <div style={s.polyControlRow}>
            <button style={s.btnToggleOff} onClick={desfazerUltimoPonto} disabled={!pontosDesenho.length} title="Atalho: Ctrl+Z">
              ↶ Desfazer
            </button>
            {verticeSelecionado !== null && (
              <button style={s.btnDanger} onClick={() => removerPontoDesenho(verticeSelecionado)} disabled={pontosDesenho.length <= 3}>
                🗑 Excluir ponto {verticeSelecionado + 1}
              </button>
            )}
            <button style={s.btnSuccess} onClick={encerrarPoligono} disabled={!desenhoPodeConcluir}>
              {poligono ? '💾 Concluir edição' : '✔ Encerrar'}
            </button>
            <button style={s.btnDanger} onClick={cancelarEdicaoPoligono}>✕ Cancelar</button>
          </div>
        </>
      )}

      {poligono && !modoDesenho && (
        <>
          <div style={s.polySummary}>
            <span style={s.polySummaryIcon}>✓</span>
            <div style={s.polySummaryCopy}>
              <strong style={s.polySummaryTitle}>{talhao?.geometria ? 'Área configurada' : 'Área pronta para salvar'}</strong>
              <small style={s.polySummaryMeta}>{mostrarPoligono ? 'Limite visível no mapa' : 'Limite oculto no mapa'}</small>
            </div>
            <button
              type="button"
              style={s.polySummaryButton}
              onClick={() => setControlesPoligonoAbertos(valor => !valor)}
              aria-expanded={controlesPoligonoAbertos}
            >
              {controlesPoligonoAbertos ? 'Fechar' : 'Configurar'}
            </button>
          </div>

          {(!talhao?.geometria || controlesPoligonoAbertos) && (
            <div style={s.polyTools}>
              {!talhao?.geometria && (
                <button style={s.btnSave} onClick={salvarPoligono} disabled={salvando}>
                  {salvando ? 'Salvando...' : '💾 Salvar área'}
                </button>
              )}
              <button style={s.btnToggleOn} onClick={iniciarEdicaoPoligono}>✏️ Editar área</button>
              <div style={s.polyControlRow}>
                <button style={mostrarPoligono ? s.btnToggleOn : s.btnToggleOff} onClick={() => setMostrarPoligono(v => !v)}>
                  {mostrarPoligono ? '👁 Ocultar' : '👁 Mostrar'}
                </button>
                {(tileUrl || mapaNdviRecortado) && (
                  <button
                    style={mostrarImagemSatelite ? s.btnToggleOff : s.btnToggleOn}
                    onClick={() => setMostrarImagemSatelite(v => !v)}
                    title={mostrarImagemSatelite ? 'Ocultar imagem Sentinel-2 e ver o mapa base' : 'Mostrar imagem Sentinel-2 novamente'}
                  >
                    {mostrarImagemSatelite ? '🗺️ Base' : '🛰️ Satélite'}
                  </button>
                )}
              </div>
              <button style={s.btnDangerGhost} onClick={limparPoligono}>🗑 Remover área desta análise</button>
            </div>
          )}
          {msgSalvo && <span style={s.msgOk}>{msgSalvo}</span>}
        </>
      )}
    </div>
  );

  const renderTerreno = () => {
    const classes = dadosTerreno?.declividade?.classes || [];
    const areaInclinada = classes.slice(3).reduce((total, valor) => total + valor, 0);
    const escopoTerreno = escopoSelecionado?.tipo === 'propriedade' ? 'propriedade' : 'talhão';
    const artigoEscopoTerreno = escopoSelecionado?.tipo === 'propriedade' ? 'da' : 'do';
    const diagnostico = !dadosTerreno ? null : areaInclinada >= 20
      ? { nivel: 'Atenção ao escoamento', texto: `${areaInclinada.toFixed(1)}% da área apresenta declividade acima de 13%. Priorize validação de campo em operações sensíveis à erosão.`, cor: '#c86b20' }
      : dadosTerreno.altitude.amplitude >= 50
        ? { nivel: 'Relevo com amplitude relevante', texto: `A diferença de ${dadosTerreno.altitude.amplitude.toFixed(0)} m entre as cotas pode influenciar drenagem, irrigação e logística na área selecionada.`, cor: '#0f7ec8' }
        : { nivel: 'Relevo pouco acidentado', texto: 'A maior parte da área está em classes suaves. Confirme em campo baixadas e caminhos preferenciais de água.', cor: '#16866f' };

    return (
      <div className="terrain-panel">
        <div className="terrain-panel-heading">
          <div><span className="terrain-kicker">MDE {artigoEscopoTerreno} {escopoTerreno}</span><h3>Relevo e drenagem</h3><p>Altitude, declividade e curvas de nível derivadas do modelo digital de elevação.</p></div>
          <button type="button" style={s.btnMini} onClick={() => gerarAnaliseTerreno()} disabled={!poligono || analisandoTerreno}>{analisandoTerreno ? 'Processando…' : dadosTerreno ? 'Recalcular' : 'Analisar terreno'}</button>
        </div>
        <div className="terrain-contour-controls">
          <label><span>Modelo de elevação</span><select aria-label="Modelo de elevação" value={fonteTerreno} onChange={evento => trocarFonteTerreno(evento.target.value)} disabled={analisandoTerreno}>{Object.entries(FONTES_TERRENO).map(([id, nome]) => <option key={id} value={id}>{nome}{id === FONTE_TERRENO_PADRAO ? ' (padrão)' : ' — comparação'}</option>)}</select></label>
        </div>
        <p className="terrain-source">Alterne o modelo para comparar a mesma área. A grade de cálculo não representa a precisão vertical do levantamento.</p>
        {erroTerreno && <div style={s.erro}>{erroTerreno}</div>}
        {analisandoTerreno && <div className="terrain-loading" role="status"><span /><div><strong>Construindo o modelo {artigoEscopoTerreno} {escopoTerreno}</strong><small>Baixando o MDE e calculando o relevo local…</small></div></div>}
        {!dadosTerreno && !analisandoTerreno && !erroTerreno && <div className="terrain-empty"><div className="terrain-empty-icon" aria-hidden="true">⌁</div><strong>Conheça a forma do terreno</strong><p>A análise é feita uma vez para o polígono atual e não depende de novas imagens de satélite.</p><button type="button" onClick={() => gerarAnaliseTerreno()} disabled={!poligono}>Gerar análise de relevo</button></div>}
        {dadosTerreno && <>
          <div className="terrain-metrics">
            <div><span>Altitude mínima</span><strong>{dadosTerreno.altitude.min.toFixed(0)} m</strong></div><div><span>Altitude média</span><strong>{dadosTerreno.altitude.mean.toFixed(0)} m</strong></div>
            <div><span>Altitude máxima</span><strong>{dadosTerreno.altitude.max.toFixed(0)} m</strong></div><div><span>Amplitude</span><strong>{dadosTerreno.altitude.amplitude.toFixed(0)} m</strong></div>
          </div>
          <div className="terrain-map-heading"><span>Visualização no mapa</span><small>Escolha uma camada principal</small></div>
          <div className="terrain-layer-controls terrain-layer-controls-three" aria-label="Visualização do terreno">
            <button type="button" className={camadaTerreno === 'altitude' ? 'is-active' : ''} onClick={() => setCamadaTerreno('altitude')}><span className="terrain-layer-swatch" /> Altitude</button>
            <button type="button" className={camadaTerreno === 'declividade' ? 'is-active' : ''} onClick={() => setCamadaTerreno('declividade')}><span className="terrain-slope-swatch" /> Declividade</button>
            <button type="button" className={camadaTerreno === 'curvas' ? 'is-active is-contour-only' : ''} onClick={() => { setCamadaTerreno('curvas'); setMostrarCurvasNivel(true); }}><span className="terrain-contour-swatch" /> Somente curvas</button>
          </div>
          {camadaTerreno === 'altitude' && <div className="terrain-altitude-legend"><div className="terrain-altitude-gradient" /><div><span>{dadosTerreno.altitude.min.toFixed(0)} m</span><span>{dadosTerreno.altitude.max.toFixed(0)} m</span></div></div>}
          <div className="terrain-contour-controls">
            <button type="button" className={mostrarCurvasNivel ? 'is-active' : ''} onClick={() => setMostrarCurvasNivel(valor => !valor)} aria-pressed={mostrarCurvasNivel}><span className="terrain-contour-swatch" /> Sobrepor curvas</button>
            <button type="button" className={mostrarCurvasNivel && mostrarRotulosCurvas ? 'is-active' : ''} onClick={() => setMostrarRotulosCurvas(valor => !valor)} disabled={!mostrarCurvasNivel} aria-pressed={mostrarRotulosCurvas}><span className="terrain-contour-altitude-icon">640 m</span> Altitudes</button>
            <label><span>Intervalo</span><select value={intervaloCurvasSelecionado} onChange={evento => setIntervaloCurvasSelecionado(Number(evento.target.value))} disabled={!mostrarCurvasNivel} aria-label="Intervalo das curvas de nível">{dadosTerreno.intervalosCurvas.map(intervalo => <option key={intervalo} value={intervalo}>{intervalo} m</option>)}</select></label>
          </div>
          <section className="terrain-slope-section">
            <div className="terrain-section-title"><div><span>Declividade</span><strong>{dadosTerreno.declividade.mean.toFixed(1)}% média</strong></div><small>Máxima estimada: {dadosTerreno.declividade.max.toFixed(1)}%</small></div>
            <div className="terrain-slope-bar">{['#3c9d83', '#7eb77f', '#d0b95d', '#df8b45', '#c95745', '#8d3d55'].map((cor, indice) => classes[indice] > 0 && <span key={cor} style={{ width: `${classes[indice]}%`, background: cor }} />)}</div>
            <div className="terrain-slope-list">{['0–3% Plano', '3–8% Suave', '8–13% Moderado', '13–20% Ondulado', '20–45% Forte', '>45% Muito forte'].map((label, indice) => <div key={label}><i style={{ background: ['#3c9d83', '#7eb77f', '#d0b95d', '#df8b45', '#c95745', '#8d3d55'][indice] }} /><span>{label}</span><strong>{(classes[indice] || 0).toFixed(1)}%</strong></div>)}</div>
          </section>
          <div className="terrain-insight" style={{ '--terrain-accent': diagnostico.cor }}><span>Leitura topográfica</span><strong>{diagnostico.nivel}</strong><p>{diagnostico.texto}</p></div>
          <p className="terrain-source">Fonte: {dadosTerreno.fonte}{dadosTerreno.resolucaoNominal ? ` · resolução nominal ~${dadosTerreno.resolucaoNominal} m (modelo de superfície; inclui vegetação e construções)` : ''} · grade de cálculo ~{dadosTerreno.resolucao} m. A precisão depende do MDE original; não substitui levantamento topográfico.</p>
          {dadosTerreno.atribuicao && <p className="terrain-source">{dadosTerreno.atribuicao}</p>}
          <button type="button" style={s.btnRelatorio} onClick={gerarRelatorioTerreno} disabled={gerandoRelatorioTerreno || analisandoTerreno} aria-busy={gerandoRelatorioTerreno}>
            {gerandoRelatorioTerreno ? 'Gerando PDF de terreno…' : 'Relatório PDF — Altitude, declividade e curvas'}
          </button>
          <p className="terrain-source">Inclui os três mapas, a fonte atual e curvas com intervalo de {intervaloCurvasSelecionado} m, mesmo que as camadas estejam ocultas na tela.</p>
        </>}
        {erroRelatorioTerreno && <div style={s.erro} role="alert">{erroRelatorioTerreno}</div>}
      </div>
    );
  };

  const renderBuscaEResultados = (mostrarTabbarInline = true) => (
    <>
      <div style={s.filtroCard}>
        <div style={s.filtroGridRow}>
          <div style={s.campo}>
            <label style={s.label}>De</label>
            <input type="date" style={s.input} value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Até</label>
            <input type="date" style={s.input} value={dataFim} onChange={e => setDataFim(e.target.value)} />
          </div>
        </div>
        {talhao?.data_plantio && (
          <button
            onClick={() => setDataInicio(talhao.data_plantio.slice(0, 10))}
            style={s.btnPlantio}
            title={`Desde o plantio: ${talhao.data_plantio.slice(0, 10)}`}
          >
            🌱 Desde o plantio
          </button>
        )}
        <div style={s.campo}>
          <label style={s.label}>Nebulosidade máxima: {maxNuvens}%</label>
          <input
            type="range"
            min={0}
            max={100}
            value={maxNuvens}
            onChange={e => setMaxNuvens(Number(e.target.value))}
            style={s.slider}
          />
        </div>
        <button style={s.btnBuscar} onClick={buscarImagens} disabled={buscando}>
          {buscando ? '⏳ Buscando...' : '🔍 Buscar cenas'}
        </button>
      </div>

      {erroStac && <p style={s.erro}>{erroStac}</p>}
      {erroTile && (
        <p style={s.erroTile}>
          ⚠️ Os tiles não carregaram. O servidor pode estar lento — aguarde alguns segundos e troque a camada ou selecione outra cena.
        </p>
      )}
      {erroNdviRecortado && <p style={s.erro}>⚠️ {erroNdviRecortado}</p>}
      {gerandoNdviRecortado && <p style={s.mutedHint}>Recortando o NDVI exatamente no perímetro selecionado…</p>}

      {cenas.length > 0 && (
        <>
          <div style={s.resultHeader}>
            <span style={s.secLabel}>{cenas.length} cena{cenas.length > 1 ? 's' : ''} encontrada{cenas.length > 1 ? 's' : ''}</span>
          </div>
          <div style={s.listaCenas}>
            {cenas.map(f => {
              const url = getStacSelfUrl(f);
              const ativo = url === cenaUrl;
              const data = f.properties?.datetime?.split('T')[0] ?? '?';
              const nuvens = typeof f.properties?.['eo:cloud_cover'] === 'number'
                ? f.properties['eo:cloud_cover'].toFixed(1)
                : '?';
              return (
                <div key={f.id} style={s.cenaRowWrap}>
                  <button style={ativo ? s.cenaAtiva : s.cenaItem} onClick={() => selecionarCena(f)}>
                    <span>📅 {data}</span>
                    <span style={s.cenaNuvem}>☁ {nuvens}%</span>
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); excluirCena(f.id); }}
                    title="Remover cena"
                    style={{ ...s.cenaRemoveBtn, color: ativo ? '#fff' : (darkMode ? '#9ca3af' : '#666'), background: ativo ? 'rgba(255,255,255,0.22)' : (darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)') }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>

          {mostrarTabbarInline && cenaUrl && (
            <div style={s.sidebarTabbar}>
              <button
                style={abaResultados === 'imagens' ? s.sidebarTabBtnActive : s.sidebarTabBtn}
                onClick={() => setAbaResultados('imagens')}
              >
                🛰️ Imagens
              </button>
              <button
                style={abaResultados === 'analise' ? s.sidebarTabBtnActive : s.sidebarTabBtn}
                onClick={abrirNdvi}
              >
                🌿 NDVI
              </button>
              <button style={abaResultados === 'terreno' ? s.sidebarTabBtnActive : s.sidebarTabBtn} onClick={() => setAbaResultados('terreno')}>⛰ Terreno</button>
            </div>
          )}

          {abaResultados === 'imagens' && cenaUrl && (
            <div style={s.camadas}>
              {[
                { id: 'rgb',  icon: '🌍', label: 'RGB',  cor: '#1565C0', bgInativo: darkMode ? '#1b2b3d' : '#E3F2FD', bordaInativa: darkMode ? '#2a4c6b' : '#90CAF9', bgAtivo: 'linear-gradient(135deg, #1976D2, #1565C0)', sombra: 'rgba(25,118,210,0.35)' },
                { id: 'cir',  icon: '🌡️', label: 'CIR',  cor: '#E53935', bgInativo: darkMode ? '#331515' : '#FFEBEE', bordaInativa: darkMode ? '#5a2424' : '#EF9A9A', bgAtivo: 'linear-gradient(135deg, #E53935, #B71C1C)', sombra: 'rgba(183,28,28,0.35)' },
              ].map(({ id, icon, label, cor, bgInativo, bordaInativa, bgAtivo, sombra }) => {
                const ativo = tipoImagem === id;
                const noRelatorio = camadasRelatorio[id];
                return (
                  <div key={id} style={s.camadaWrap}>
                    <button
                      style={{
                        ...s.camadaBtnBase,
                        background: ativo ? bgAtivo : bgInativo,
                        border: ativo ? 'none' : `1.5px solid ${bordaInativa}`,
                        color: ativo ? '#fff' : cor,
                        boxShadow: ativo ? `0 3px 10px ${sombra}` : 'none',
                        fontWeight: ativo ? 700 : 600,
                        opacity: noRelatorio ? 1 : 0.45,
                        textDecoration: noRelatorio ? 'none' : 'line-through',
                      }}
                      onClick={() => setTipoImagem(id)}
                    >
                      <span style={{ fontSize: 15, lineHeight: 1 }}>{icon}</span>
                      <span>{label}</span>
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setCamadasRelatorio(prev => ({ ...prev, [id]: !prev[id] })); }}
                      title={noRelatorio ? `Remover ${label} do relatório` : `Adicionar ${label} ao relatório`}
                      style={{ ...s.camadaToggle, background: ativo ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.08)', color: ativo ? '#fff' : '#666' }}
                    >
                      {noRelatorio ? '×' : '+'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {abaResultados === 'imagens' && (
            <button
              style={{ ...s.btnRelatorio, opacity: Object.values(camadasRelatorio).some(Boolean) ? 1 : 0.5 }}
              onClick={gerarRelatorio}
              disabled={gerandoRelatorio || !Object.values(camadasRelatorio).some(Boolean)}
            >
              {gerandoRelatorio
                ? `⏳ Gerando PDF... (${cenas.length} cena${cenas.length > 1 ? 's' : ''})`
                : (() => {
                    const ativas = [camadasRelatorio.rgb && 'RGB', camadasRelatorio.cir && 'CIR'].filter(Boolean);
                    return `📄 Relatório PDF (${cenas.length} data${cenas.length > 1 ? 's' : ''} · ${ativas.join(', ')})`;
                  })()}
            </button>
          )}

          {abaResultados === 'analise' && cenaUrl && (
            <div style={s.varBox}>
              <div style={s.varBoxHeader}>
                <span style={s.secLabel}>🌿 NDVI da imagem selecionada</span>
                <button style={s.btnMini} onClick={() => setTipoImagem('ndvi')} disabled={gerandoNdviRecortado}>
                  {gerandoNdviRecortado ? '⏳ Processando' : tipoImagem === 'ndvi' ? '✓ Visível no mapa' : 'Mostrar no mapa'}
                </button>
              </div>
              <p style={s.mutedHint}>Índice de vigor calculado exclusivamente para {formatarDataCena(cenaFeature)}.</p>
            </div>
          )}

          {abaResultados === 'analise' && cenaUrl && tipoImagem === 'ndvi' && (
            <div style={s.legendaBox}>
              <span style={s.legendaLabel}>NDVI</span>
              <div style={s.legendaBar} />
              <div style={s.legendaTextos}>
                <span>−1 (Solo/Água)</span>
                <span>0</span>
                <span>+1 (Alta vegetação)</span>
              </div>
            </div>
          )}

          {abaResultados === 'analise' && cenaUrl && poligono && (
            <div ref={analysisSectionRef} style={s.varBox}>
              <div style={s.varBoxHeader}>
                <span style={s.secLabel}>📊 Variabilidade (NDVI)</span>
                <button style={s.btnMini} onClick={analisarVariabilidadeNdvi} disabled={calculandoStats}>
                  {calculandoStats ? '⏳ Comparando' : (statsNdvi ? '🔄 Recalcular' : '▶ Analisar')}
                </button>
              </div>

              {erroStats && <div style={{ ...s.erro, marginTop: 8 }}>{erroStats}</div>}

              {calculandoStats && (
                <p style={s.mutedHint}>
                  Calculando o NDVI da imagem selecionada e da cena imediatamente anterior no mesmo polígono…
                </p>
              )}

              {!statsNdvi && !calculandoStats && !erroStats && (
                <p style={s.mutedHint}>
                  Calcula média, desvio padrão e zonas de vigor (baixo/médio/alto) do NDVI recortado
                  exatamente pelo polígono do talhão selecionado.
                </p>
              )}

              {statsNdvi && (() => {
                const { baixoPct, medioPct, altoPct, cv, nivel } = classificarVariabilidadeNdvi(statsNdvi);
                const corNivel = nivel === 'baixa' ? '#2E7D32' : nivel === 'moderada' ? '#F57F17' : '#B71C1C';
                const textoNivel = nivel === 'baixa'
                  ? 'Talhão uniforme — manejo homogêneo é suficiente.'
                  : nivel === 'moderada'
                    ? 'Variabilidade moderada — considere ajustar insumos por zona.'
                    : 'Alta variabilidade — recomenda-se manejo por zonas (agricultura de precisão).';
                return (
                  <div style={{ marginTop: 10 }}>
                    <div style={s.statGrid}>
                      {[
                        { label: 'NDVI médio', valor: statsNdvi.mean.toFixed(3) },
                        { label: 'Desvio padrão', valor: statsNdvi.std.toFixed(3) },
                        { label: 'Coef. variação', valor: `${cv.toFixed(1)}%` },
                        { label: 'Mín / Máx', valor: `${statsNdvi.min.toFixed(2)} / ${statsNdvi.max.toFixed(2)}` },
                      ].map(item => (
                        <div key={item.label} style={s.statCard}>
                          <div style={s.statLabel}>{item.label}</div>
                          <div style={s.statValor}>{item.valor}</div>
                        </div>
                      ))}
                    </div>

                    {comparacaoNdvi && (
                      <div style={s.temporalBox}>
                        <div style={s.temporalHeader}>
                          <div>
                            <div style={s.statLabel}>Evolução temporal</div>
                            <strong style={s.temporalTitle}>Comparação entre imagens reais</strong>
                          </div>
                          {comparacaoNdvi.status === 'sucesso' && (
                            <span style={{
                              ...s.temporalBadge,
                              color: comparacaoNdvi.delta >= 0 ? '#13795b' : '#c53c3c',
                              background: comparacaoNdvi.delta >= 0 ? 'rgba(19,121,91,0.1)' : 'rgba(197,60,60,0.1)',
                            }}>
                              {Number.isFinite(comparacaoNdvi.changePct)
                                ? `${formatarNumeroComSinal(comparacaoNdvi.changePct)}%`
                                : `${formatarNumeroComSinal(comparacaoNdvi.delta, 3)} ponto`}
                            </span>
                          )}
                        </div>

                        {comparacaoNdvi.status === 'sucesso' ? (
                          <>
                            <div style={s.temporalPeriod}>
                              {comparacaoNdvi.dataAnterior} → {comparacaoNdvi.dataAtual}
                            </div>
                            <div style={s.temporalGrid}>
                              <div style={s.temporalMetric}>
                                <span style={s.statLabel}>NDVI anterior</span>
                                <strong style={s.statValor}>{comparacaoNdvi.anterior.toFixed(3)}</strong>
                              </div>
                              <div style={s.temporalMetric}>
                                <span style={s.statLabel}>NDVI atual</span>
                                <strong style={s.statValor}>{comparacaoNdvi.atual.toFixed(3)}</strong>
                              </div>
                              <div style={s.temporalMetric}>
                                <span style={s.statLabel}>Diferença</span>
                                <strong style={s.statValor}>{formatarNumeroComSinal(comparacaoNdvi.delta, 3)}</strong>
                                <small style={{ ...s.temporalNote, margin: 0 }}>pontos de NDVI</small>
                              </div>
                              <div style={s.temporalMetric}>
                                <span style={s.statLabel}>Variação</span>
                                <strong style={s.statValor}>
                                  {Number.isFinite(comparacaoNdvi.changePct)
                                    ? `${formatarNumeroComSinal(comparacaoNdvi.changePct)}%`
                                    : 'Base zero'}
                                </strong>
                                <small style={{ ...s.temporalNote, margin: 0 }}>sobre a cena anterior</small>
                              </div>
                            </div>
                            <p style={s.temporalNote}>
                              A comparação usa exatamente o mesmo polígono nas duas datas. Variações também podem refletir estágio da cultura, solo exposto, sombra ou condições atmosféricas.
                            </p>
                          </>
                        ) : (
                          <p style={s.temporalNote}>
                            {comparacaoNdvi.status === 'sem_cena_anterior'
                              ? 'Não existe uma cena mais antiga disponível no período pesquisado. Amplie a data inicial para habilitar a comparação.'
                              : 'A leitura atual foi calculada, mas a cena anterior não pôde ser processada. Tente novamente ou escolha outra data.'}
                          </p>
                        )}
                      </div>
                    )}

                    <div style={s.mutedHint}>Distribuição da área por zona de vigor</div>
                    <div style={s.vigorBar}>
                      {baixoPct > 0 && <div style={{ width: `${baixoPct}%`, background: '#d73027' }} title={`Baixo vigor: ${baixoPct.toFixed(1)}%`} />}
                      {medioPct > 0 && <div style={{ width: `${medioPct}%`, background: '#fee08b' }} title={`Vigor médio: ${medioPct.toFixed(1)}%`} />}
                      {altoPct > 0 && <div style={{ width: `${altoPct}%`, background: '#1a9850' }} title={`Alto vigor: ${altoPct.toFixed(1)}%`} />}
                    </div>
                    <div style={s.vigorLegend}>
                      <span>🔴 Baixo {baixoPct.toFixed(1)}%</span>
                      <span>🟡 Médio {medioPct.toFixed(1)}%</span>
                      <span>🟢 Alto {altoPct.toFixed(1)}%</span>
                    </div>

                    <div style={{ ...s.interpretacaoBox, border: `1px solid ${corNivel}33` }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: corNivel }}>Variabilidade {nivel}</span>
                      <p style={s.interpretacaoTexto}>{textoNivel}</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {abaResultados === 'analise' && cenaUrl && poligono && (
            <div style={s.varBox}>
              <div style={s.varBoxHeader}>
                <span style={s.secLabel}>🗺️ Zonas de manejo</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {zonasManejo && (
                    <button style={s.btnMini} onClick={() => setMostrarZonas(v => !v)}>
                      {mostrarZonas ? '👁️ Ocultar' : '👁️ Mostrar'}
                    </button>
                  )}
                  <button style={s.btnMini} onClick={gerarZonasManejo} disabled={gerandoZonas}>
                    {gerandoZonas ? '⏳' : (zonasManejo ? '🔄 Recalcular' : '▶ Gerar mapa')}
                  </button>
                </div>
              </div>

              {erroZonas && <div style={{ ...s.erro, marginTop: 8 }}>{erroZonas}</div>}

              {!zonasManejo && !gerandoZonas && !erroZonas && (
                <p style={s.mutedHint}>
                  Desenha no mapa as zonas de baixo, médio e alto vigor dentro do talhão, com base no
                  NDVI — útil para aplicação a taxa variável.
                </p>
              )}

              {zonasManejo && (
                <div style={{ marginTop: 10 }}>
                  <div style={s.mutedHint}>Zonas desenhadas no mapa. Toque em uma cor para identificar a área.</div>
                  <div style={s.vigorLegend}>
                    <span><span style={{ ...s.dotSwatch, background: CORES_ZONA_MANEJO.baixo }} />Baixo ({zonasManejo.baixoPct.toFixed(1)}%)</span>
                    <span><span style={{ ...s.dotSwatch, background: CORES_ZONA_MANEJO.medio }} />Médio ({zonasManejo.medioPct.toFixed(1)}%)</span>
                    <span><span style={{ ...s.dotSwatch, background: CORES_ZONA_MANEJO.alto }} />Alto ({zonasManejo.altoPct.toFixed(1)}%)</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {abaResultados === 'analise' && cenaFeature && poligono && (
            <>
              <button type="button" style={s.btnRelatorio} onClick={gerarRelatorioNdvi} disabled={gerandoRelatorioNdvi || calculandoStats || gerandoZonas || gerandoNdviRecortado} aria-busy={gerandoRelatorioNdvi}>
                {gerandoRelatorioNdvi ? 'Processando mapas e gerando PDF…' : 'Relatório PDF — NDVI e zonas de manejo'}
              </button>
              <p style={s.mutedHint}>Gera os mapas e as estatísticas da cena selecionada para o perímetro atual, mesmo sem análise prévia.</p>
              {erroRelatorioNdvi && <div style={s.erro} role="alert">{erroRelatorioNdvi}</div>}
            </>
          )}
        </>
      )}
    </>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={`remote-sensing-page ${darkMode ? 'is-dark' : ''} ${cadastroTerritorial ? 'is-territorial-registering' : ''}`} style={s.page}>

      {/* Header */}
      <div className="remote-sensing-header" style={s.header}>
        <div className="remote-sensing-header-main" style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14 }}>
          <button
            onClick={onBack}
            className="remote-back-button"
            aria-label="Voltar ao painel principal"
            style={s.btnVoltar}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            {!isMobile && <span>Voltar</span>}
          </button>

          <div className="remote-sensing-header-separator" style={{ width: 1, height: isMobile ? 28 : 32, background: darkMode ? '#2a3a52' : '#e2e8f0', flexShrink: 0 }} />

          <div className="remote-sensing-header-identity" style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12, minWidth: 0 }}>
            <div className="remote-sensing-header-icon" style={s.headerIcon}>▧</div>
            <div className="remote-sensing-header-copy" style={{ minWidth: 0 }}>
              <h1 style={s.headerTitle}>Central de Operações</h1>
              <p style={s.headerSubtitle}>Gestão territorial, imagens e execução agrícola direto no mapa</p>
            </div>
          </div>

          {contextoSelecionado && !isMobile && (
            <div style={s.headerBreadcrumb}>
              <span style={s.headerBreadcrumbDot} />
              {contextoTipoLabel}: {contextoSelecionado.nome}
            </div>
          )}
        </div>
      </div>

      {contextoSelecionado && cenaUrl && abaResultados === 'analise' && !isMobile && (
        <RemoteSensingOverview
          talhao={contextoSelecionado}
          scopeType={escopoSelecionado?.tipo}
          insights={operationalInsights}
          onOpenAnalysis={abrirPainelAnalise}
          loading={calculandoStats}
          error={erroStats}
          scene={cenaFeature}
        />
      )}

      {previewNotice && (
        <div className="remote-preview-notice" role="status">
          <span aria-hidden="true">✓</span>
          {previewNotice}
        </div>
      )}

      {/* Corpo: sidebar (desktop) + área de mapa */}
      <div style={s.body}>

        {!isMobile && !painelTerritorialRecolhido && (
          <aside className="remote-territorial-sidebar" style={s.sidebar}>
            <div style={s.sidebarScroll}>
              <div className="remote-territorial-intro">
                <div><span>Área selecionada no mapa</span><strong>Ferramentas de análise</strong></div>
                <button type="button" onClick={() => setPainelTerritorialRecolhido(true)} aria-label="Recolher ferramentas de análise">‹</button>
              </div>

              {abaResultados !== 'terreno' && (
                <div className="remote-analysis-module-tabs" aria-label="Ferramentas de imagens de satélite">
                  <button
                    type="button"
                    className={abaResultados === 'imagens' ? 'is-active' : ''}
                    onClick={() => { setAbaResultados('imagens'); if (tipoImagem === 'ndvi') setTipoImagem('rgb'); }}
                  >
                    <span aria-hidden="true">🛰️</span>
                    <strong>Imagens</strong>
                    <small>RGB e CIR</small>
                  </button>
                  <button
                    type="button"
                    className={abaResultados === 'analise' ? 'is-active' : ''}
                    onClick={abrirNdvi}
                    disabled={!cenaFeature}
                    title={cenaFeature ? 'Abrir análise NDVI da imagem selecionada' : 'Selecione uma imagem antes de abrir o NDVI'}
                  >
                    <span aria-hidden="true">🌿</span>
                    <strong>NDVI</strong>
                    <small>{cenaFeature ? formatarDataCena(cenaFeature) : 'Selecione uma cena'}</small>
                  </button>
                </div>
              )}

              {talhaoId && (
                <>
                  <div style={s.sidebarBlock}>
                    <div style={s.sidebarLabel}>Área de análise</div>
                    {renderControlePoligono()}
                  </div>
                </>
              )}

              {poligono && (
                <>
                  <div style={s.sidebarDivider} />
                  <div style={s.sidebarBlock}>
                    <div style={s.sidebarLabel}>{abaResultados === 'terreno' ? 'Modelo digital de elevação' : abaResultados === 'analise' ? 'Análise NDVI' : 'Cenas Sentinel-2'}</div>
                    {abaResultados === 'terreno' ? renderTerreno() : renderBuscaEResultados(false)}
                  </div>
                </>
              )}
            </div>
          </aside>
        )}

        <div ref={mapAreaRef} className={`remote-map-area${compactMapLayout ? ' is-compact' : ''}`} style={s.mapWrap}>
          <div className="remote-location-search">
            <form className={locationQuery ? 'has-query' : undefined} onSubmit={event => { event.preventDefault(); localizarCoordenadas(); }}>
              <span aria-hidden="true">⌖</span>
              <input
                ref={locationInputRef}
                value={locationQuery}
                onChange={event => {
                  setLocationQuery(event.target.value);
                  setLocationSearchOpen(true);
                  setLocationSearchError('');
                }}
                onFocus={() => setLocationSearchOpen(propriedades.length > 0 || locationQuery.trim().length >= 2)}
                placeholder={isMobile ? 'Buscar local ou coordenadas' : 'Buscar propriedade, talhão, cidade ou coordenadas'}
                aria-label="Buscar propriedade, talhão, cidade ou coordenadas"
              />
              {locationQuery && (
                <button
                  type="button"
                  className="remote-location-clear"
                  aria-label="Limpar busca de localização"
                  title="Limpar busca"
                  onClick={() => {
                    setLocationQuery('');
                    setLocationSearchError('');
                    setLocationSearchOpen(true);
                    locationInputRef.current?.focus();
                  }}
                >
                  <FaTimes aria-hidden="true" />
                </button>
              )}
              <button type="submit">{isMobile ? 'Ir' : 'Localizar'}</button>
            </form>
            {locationSearchOpen && (
              <div className="remote-location-results">
                {propriedadesEncontradas.length > 0 && <span className="remote-location-result-group">{termoBuscaMapa ? 'Propriedades' : 'Propriedades cadastradas'}</span>}
                {propriedadesEncontradas.map(propriedade => (
                  <button type="button" key={`propriedade-${propriedade.id}`} onClick={() => {
                    selecionarPropriedadeNoMapa(String(propriedade.id));
                    setLocationQuery(propriedade.nome);
                    setLocationSearchOpen(false);
                  }}>
                    <strong>{propriedade.nome}</strong>
                    <small>
                      {propriedade._accessRole === 'owner'
                        ? 'Sua propriedade'
                        : propriedade._accessRole === 'editor' ? 'Compartilhada · Editor' : 'Compartilhada · Visualização'}
                      {' · '}{[propriedade.municipio, propriedade.estado].filter(Boolean).join('/')}
                    </small>
                  </button>
                ))}
                {talhoesEncontrados.length > 0 && <span className="remote-location-result-group">Talhões da propriedade</span>}
                {talhoesEncontrados.map(item => (
                  <button type="button" key={`talhao-${item.id}`} onClick={() => {
                    selecionarTalhaoNoMapa(String(item.id));
                    setLocationQuery(item.nome);
                    setLocationSearchOpen(false);
                  }}>
                    <strong>{item.nome}</strong><small>Talhão · {item.ciclo_atual?.cultura || item.cultura || 'Sem cultivo ativo'}</small>
                  </button>
                ))}
                {locationResults.length > 0 && <span className="remote-location-result-group">Municípios</span>}
                {locationLoading && <span>Buscando municípios…</span>}
                {!locationLoading && locationResults.map((municipio, index) => (
                  <button type="button" key={`${municipio.name_muni}-${municipio.code_state}-${index}`} onClick={() => localizarMunicipio(municipio)}>
                    <strong>{municipio.name_muni}</strong><small>Município · {normalizarUf(municipio.code_state) || municipio.code_state}</small>
                  </button>
                ))}
                {!locationLoading && !possuiResultadosTerritoriais && locationResults.length === 0 && parseCoordinateSearch(locationQuery) && (
                  <button type="button" onClick={localizarCoordenadas}><strong>Ir para as coordenadas</strong><small>{locationQuery}</small></button>
                )}
                {locationSearchError && <p>{locationSearchError}</p>}
              </div>
            )}
          </div>

          {!cadastroTerritorial && (
            <div className={`remote-map-create-tools${pecuariaEnabled && propriedadeSelecionada ? ' has-pecuaria' : ''}${ferramentasMapaRecolhidas ? ' is-collapsed' : ''}`} aria-label="Ferramentas de cadastro territorial">
              <button
                type="button"
                className="remote-map-tools-toggle"
                onClick={() => setMapToolsCollapsedChoice(!ferramentasMapaRecolhidas)}
                aria-expanded={!ferramentasMapaRecolhidas}
                aria-label={ferramentasMapaRecolhidas ? 'Mostrar ferramentas do mapa' : 'Recolher ferramentas do mapa'}
                title={ferramentasMapaRecolhidas ? 'Mostrar ferramentas do mapa' : 'Recolher ferramentas do mapa'}
              >
                <span aria-hidden="true">{isMobile ? (ferramentasMapaRecolhidas ? <FaBars /> : <FaChevronLeft />) : (ferramentasMapaRecolhidas ? '☰' : '‹')}</span>
                {(ferramentasMapaRecolhidas || compactMapLayout) && <strong>Ferramentas</strong>}
              </button>
              <button type="button" onClick={() => iniciarCadastroTerritorial('propriedade')}>
                <span aria-hidden="true">{isMobile ? <FaMapMarkedAlt /> : '⌂'}</span><strong>Nova propriedade</strong><small>Desenhar perímetro</small>
              </button>
              <button type="button" onClick={() => iniciarCadastroTerritorial('talhao')} disabled={!propId || !propriedadeSelecionada?.geometria}>
                <span aria-hidden="true">{isMobile ? <FaDrawPolygon /> : '▱'}</span><strong>Novo talhão</strong><small>{!propId ? 'Selecione a propriedade' : propriedadeSelecionada?.geometria ? 'Medir pelo desenho' : 'Mapeie a propriedade primeiro'}</small>
              </button>
              {pecuariaEnabled && propriedadeSelecionada && <button type="button" aria-pressed={mostrarPastagens} onClick={() => setMostrarPastagens(value => !value)} title={pecuariaMap.error || 'Mostrar lotes, pastagens e cultivos nos talhões'}>
                <span aria-hidden="true">{isMobile ? <FaLayerGroup /> : '♧'}</span><strong>{!isMobile && mostrarPastagens ? 'Atividades visíveis' : 'Atividades'}</strong><small>{pecuariaMap.loading ? 'Carregando atividades…' : pecuariaMap.error ? 'Consulta indisponível' : 'Lotes, pastagens e cultivos'}</small>
              </button>}
              {droneHabilitado && propriedadeSelecionada && (
                <button
                  type="button"
                  aria-pressed={painelDroneAberto}
                  onClick={() => setPainelDroneAberto(aberto => !aberto)}
                  title="Histórico de mapeamentos de drone da propriedade"
                >
                  <span aria-hidden="true">{isMobile ? <TbDrone /> : '✈'}</span>
                  <strong>Mapeamento</strong>
                  <small>{camadaDrone ? 'Camada ativa no mapa' : 'Ortomosaicos de drone'}</small>
                </button>
              )}
              {propriedadeSelecionada && (
                <button
                  type="button"
                  className="remote-map-limits-toggle"
                  onClick={() => setMostrarPoligono(visivel => !visivel)}
                  aria-pressed={mostrarPoligono}
                  title={mostrarPoligono ? 'Ocultar os perímetros para visualizar a imagem sem interferências' : 'Mostrar novamente os perímetros da propriedade e dos talhões'}
                >
                  <span aria-hidden="true">{isMobile ? (mostrarPoligono ? <FaEye /> : <FaEyeSlash />) : (mostrarPoligono ? '◉' : '○')}</span>
                  <strong>{isMobile ? 'Limites' : (mostrarPoligono ? 'Ocultar limites' : 'Mostrar limites')}</strong>
                  <small>{mostrarPoligono ? 'Ver imagem sem polígonos' : 'Exibir propriedade e talhões'}</small>
                </button>
              )}
              <button
                type="button"
                aria-pressed={mostrarInmet}
                disabled={previewMode}
                onClick={() => setMostrarInmet(visible => !visible)}
                title={previewMode ? 'A consulta INMET não está disponível na prévia local' : 'Estações automáticas e observações públicas do INMET'}
              >
                <span aria-hidden="true"><FaMapMarkedAlt /></span>
                <strong>Estações INMET</strong>
                <small>{mostrarInmet ? 'Ocultar estações públicas' : 'Medições e histórico de 48 h'}</small>
              </button>
            </div>
          )}

          {/* Erro de iniciarCadastroTerritorial: cadastroTerritorial ainda não existe nesse ponto, então precisa de um lugar próprio para aparecer. */}
          {!cadastroTerritorial && cadastroTerritorialErro && (
            <p className="remote-territorial-inline-error" role="alert">{cadastroTerritorialErro}</p>
          )}

          <div className={`remote-map-operation-panels${compactMapLayout && !cadastroTerritorial ? ' is-compact' : ''}`}>
          {contextoSelecionado && !cadastroTerritorial && (
            <section className={`remote-map-context-actions${compactMapLayout && !moreMapActionsOpen ? ' is-condensed' : ''}`} aria-label={`Ações para ${contextoSelecionado.nome}`}>
              <header>
                <span>{contextoSelecaoLabel}</span>
                <strong>{contextoSelecionado.nome}</strong>
                <small>{contextoSelecionado.area ? `${contextoSelecionado.area} ha` : (poligono ? formatarAreaHectares(calcularAreaHectares(poligono)) : 'Sem perímetro desenhado')}</small>
              </header>
              {mostrarPastagens && pecuariaEnabled && <p className="pec-map-legend">{Object.entries(PASTURE_STATES).filter(([key]) => key !== 'unconfigured').map(([key, state]) => <span key={key}><i style={{ background: state.color }} />{state.label}</span>)}</p>}
              {mostrarPastagens && pecuariaMap.error && <p className="remote-property-access-note" role="status">{pecuariaMap.error} O mapa agrícola permanece disponível.</p>}
              {escopoSelecionado?.tipo === 'propriedade' && propriedadeSelecionada?._accessRole === 'viewer' && (
                <p className="remote-property-access-note">
                  Propriedade compartilhada em modo de visualização: solicite acesso de editor para alterar o perímetro.
                </p>
              )}
              {!poligono && (
                <p className="remote-property-access-note">
                  Este cadastro ainda não possui perímetro. Imagens, NDVI e terreno serão liberados após o georreferenciamento.
                  {escopoSelecionado?.tipo === 'propriedade' && propriedadeSelecionada?._accessRole !== 'viewer' && (
                    <button type="button" onClick={iniciarAtualizacaoPerimetroPropriedade}>Adicionar perímetro agora</button>
                  )}
                </p>
              )}
              <div id="remote-context-action-grid">
                <button type="button" className="is-images" onClick={() => {
                  setAbaResultados('imagens');
                  if (tipoImagem === 'ndvi') setTipoImagem('rgb');
                  if (isMobile) setPainelAberto(true);
                  else setPainelTerritorialRecolhido(false);
                }} disabled={!poligono} title={`Abrir imagens de satélite ${escopoSelecionado?.tipo === 'propriedade' ? 'da propriedade' : 'do talhão'}`}>
                  <span aria-hidden="true">🛰️</span><strong>Imagens</strong>
                </button>
                <button type="button" className="is-terrain" onClick={gerarCurvasDoEscopoSelecionado} disabled={!poligono || analisandoTerreno} title="Abrir análise do terreno">
                  <span aria-hidden="true">⌁</span><strong>{analisandoTerreno ? 'Processando…' : 'Terreno'}</strong>
                </button>
                {pecuariaEnabled && <button type="button" className="remote-secondary-map-action" onClick={() => onNavigate('pecuaria', {
                  propertyId: propId,
                  plotId: talhao?._physicalPlotId || null,
                  // Quem entrou pela Central de Operações deve conseguir voltar direto
                  // para a mesma fazenda e visão de mapa, sem depender do Dashboard.
                  returnTarget: 'sensoriamento',
                  returnContext: {
                    propertyId: propId,
                    plotId: talhaoId || null,
                    scopeType: escopoSelecionado?.tipo || (talhaoId ? 'talhao' : 'propriedade'),
                    mapView: mapViewRef.current,
                    analysisTab: abaResultados,
                    imageType: tipoImagem,
                  },
                })} title="Abrir pastagens, lotes e histórico desta propriedade">
                  <span aria-hidden="true">♧</span><strong>Pecuária</strong>
                </button>}
                <button type="button" className="remote-secondary-map-action" onClick={() => abrirAcaoTerritorial('new-order', `Nova ordem vinculada à ${contextoTipoLabel.toLowerCase()} selecionada.`)} title="Criar uma ordem de serviço neste contexto">
                  <span aria-hidden="true">✓</span><strong>Nova ordem</strong>
                </button>
                <button type="button" className="remote-secondary-map-action" onClick={() => abrirAcaoTerritorial('new-expense', `Nova despesa vinculada à ${contextoTipoLabel.toLowerCase()} selecionada.`)} title="Registrar uma despesa neste contexto">
                  <span aria-hidden="true">R$</span><strong>Despesa</strong>
                </button>
                {escopoSelecionado?.tipo === 'talhao' && (
                  <>
                    {talhao?.ciclo_atual && propriedadeSelecionada?._accessRole !== 'viewer' && (
                      <button type="button" className="remote-secondary-map-action" onClick={() => abrirGestaoCultivo('edit')} title="Editar as informações do plantio atual">
                        <span aria-hidden="true">✎</span><strong>Editar plantio</strong>
                      </button>
                    )}
                    <button type="button" className="remote-secondary-map-action" onClick={() => abrirGestaoCultivo(talhao?.ciclo_atual ? 'harvest' : 'create')} title={talhao?.ciclo_atual ? 'Registrar a colheita do cultivo atual' : 'Cadastrar um novo plantio'} disabled={propriedadeSelecionada?._accessRole === 'viewer'}>
                      <span aria-hidden="true">{talhao?.ciclo_atual ? '🌾' : '↗'}</span><strong>{talhao?.ciclo_atual ? 'Colheita' : 'Novo plantio'}</strong>
                    </button>
                    <button
                      type="button"
                      className="remote-secondary-map-action"
                      onClick={() => abrirAcaoTerritorial('history', 'O Caderno será filtrado por este talhão.')}
                      title="Ir para o Caderno de Campo do talhão"
                      aria-label="Ir para o Caderno de Campo do talhão"
                    >
                      <span aria-hidden="true">≡</span><strong>Caderno</strong>
                    </button>
                    <button
                      type="button"
                      className="remote-secondary-map-action"
                      onClick={() => abrirAcaoTerritorial('plot-info', 'Abrindo as informações completas deste talhão.')}
                      title="Abrir este talhão em Propriedades e Talhões"
                      aria-label="Abrir informações do talhão em Propriedades e Talhões"
                    >
                      <span aria-hidden="true">ⓘ</span><strong>Info talhão</strong>
                    </button>
                  </>
                )}
                {compactMapLayout && <>
                  <button type="button" aria-expanded={moreMapActionsOpen} aria-controls="remote-context-action-grid" onClick={() => setMoreMapActionsOpen(open => !open)}>
                    <span aria-hidden="true">⋯</span><strong>{moreMapActionsOpen ? 'Menos ações' : 'Mais ações'}</strong>
                  </button>
                  {talhao && !sensorMapaSelecionado && <button type="button" aria-expanded={plotDetailsVisible} aria-controls="remote-selected-plot-details" disabled={plotDetailsRequired} onClick={() => setPlotDetailsOpen(open => !open)}>
                    <span aria-hidden="true">ⓘ</span><strong>{plotDetailsVisible ? 'Recolher detalhes' : 'Detalhes'}</strong>
                  </button>}
                </>}
              </div>
            </section>
          )}

          {cadastroTerritorial && (
            <section className="remote-territorial-create-panel" aria-label={`Cadastro de ${cadastroTerritorial.tipo}`}>
              <header>
                <div>
                  <span>{cadastroTerritorial.modo === 'atualizacao' ? 'Atualização segura do cadastro' : 'Cadastro direto no mapa'}</span>
                  <h2>{cadastroTerritorial.modo === 'atualizacao'
                    ? `Perímetro de ${cadastroTerritorial.form.nome}`
                    : cadastroTerritorial.tipo === 'propriedade' ? 'Nova propriedade' : 'Novo talhão'}</h2>
                </div>
                <button type="button" onClick={cancelarCadastroTerritorial} aria-label="Cancelar cadastro">×</button>
              </header>

              <div className="remote-territorial-steps" aria-label="Etapas do cadastro">
                <span className={cadastroTerritorial.etapa === 'desenho' ? 'is-active' : 'is-complete'}><i>1</i> Desenhar</span>
                <b aria-hidden="true" />
                <span className={cadastroTerritorial.etapa === 'dados' ? 'is-active' : ''}><i>2</i> {cadastroTerritorial.modo === 'atualizacao' ? 'Conferir' : 'Identificar'}</span>
                <b aria-hidden="true" />
                <span><i>3</i> Salvar</span>
              </div>

              {cadastroTerritorial.etapa === 'desenho' ? (
                <div className="remote-territorial-drawing-guide">
                  <div className="remote-territorial-measure">
                    <span>Área medida em tempo real</span>
                    <strong>{formatarAreaHectares(areaEmDesenho)}</strong>
                    <small>O valor será definido pelo perímetro, sem preenchimento manual.</small>
                  </div>
                  {cadastroTerritorial.tipo === 'talhao' && (
                    <div className="remote-snap-assistant" aria-live="polite">
                      <i aria-hidden="true">⌁</i>
                      <span>
                        <strong>Aderência automática ativa</strong>
                        <small>{totalPontosAderidos
                          ? `${totalPontosAderidos} ponto(s) alinhado(s) com limites existentes.`
                          : 'Aproxime o cursor de outro talhão ou do limite da propriedade para encaixar.'}</small>
                      </span>
                    </div>
                  )}
                  <ol>
                    <li>Clique nos cantos do perímetro no mapa.</li>
                    <li>Use pelo menos três pontos para formar a área.</li>
                    <li>Arraste para ajustar. Use os botões + nas arestas para acrescentar vértices.</li>
                    <li>Selecione um vértice para excluí-lo ou use Ctrl+Z para desfazer.</li>
                    {cadastroTerritorial.tipo === 'talhao' && <li>O sistema bloqueia sobreposição e áreas fora da propriedade.</li>}
                  </ol>
                  {erroGeometriaVisivel && <p className="remote-geometry-error" role="alert">{erroGeometriaVisivel}</p>}
                  <div className="remote-territorial-drawing-actions">
                    <button type="button" onClick={desfazerUltimoPonto} disabled={!pontosDesenho.length} title="Atalho: Ctrl+Z">↶ Desfazer ponto</button>
                    {verticeSelecionado !== null && (
                      <button type="button" className="is-danger" onClick={() => removerPontoDesenho(verticeSelecionado)} disabled={pontosDesenho.length <= 3}>
                        Excluir vértice {verticeSelecionado + 1}
                      </button>
                    )}
                    <button type="button" className="is-primary" onClick={encerrarPoligono} disabled={!desenhoPodeConcluir}>Concluir perímetro</button>
                  </div>
                </div>
              ) : (
                <div className="remote-territorial-form">
                  <div className="remote-territorial-measure is-complete">
                    <span>{cadastroTerritorial.modo === 'atualizacao' ? 'Área medida do novo perímetro' : 'Área calculada pelo mapa'}</span>
                    <strong>{formatarAreaHectares(areaTerritorialAtual)}</strong>
                    <small>{cadastroTerritorial.modo === 'atualizacao'
                      ? 'A área já informada no cadastro será preservada.'
                      : 'Este será o tamanho oficial do cadastro.'}</small>
                  </div>

                  {cadastroTerritorial.modo === 'atualizacao' ? (
                    <div className="remote-territorial-existing-summary">
                      <span>Propriedade existente preservada</span>
                      <strong>{cadastroTerritorial.form.nome}</strong>
                      <small>{[cadastroTerritorial.form.municipio, normalizarUf(cadastroTerritorial.form.estado)].filter(Boolean).join(' · ') || 'Localização cadastral mantida'}</small>
                      <p>Somente o novo polígono será acrescentado. Nome, proprietário, área informada e demais dados continuarão como estão.</p>
                    </div>
                  ) : (
                    <label>
                      <span>{cadastroTerritorial.tipo === 'propriedade' ? 'Nome da propriedade' : 'Nome do talhão'}</span>
                      <input
                        value={cadastroTerritorial.form.nome}
                        onChange={event => atualizarCadastroTerritorial('nome', event.target.value)}
                        placeholder={cadastroTerritorial.tipo === 'propriedade' ? 'Ex.: Fazenda Rio Preto' : 'Ex.: Pivô Central'}
                        maxLength="80"
                        autoFocus
                      />
                    </label>
                  )}

                  {cadastroTerritorial.tipo === 'propriedade' && cadastroTerritorial.modo !== 'atualizacao' ? (
                    <div className="remote-territorial-form-row">
                      <div className="remote-territorial-municipality-field">
                        <label htmlFor="municipio-cadastro-territorial"><span>Município</span></label>
                        <input
                          id="municipio-cadastro-territorial"
                          value={cadastroTerritorial.form.municipio}
                          onChange={event => {
                            atualizarCadastroTerritorial('municipio', event.target.value);
                            atualizarCadastroTerritorial('estado', '');
                            setMunicipioCadastroAberto(event.target.value.trim().length >= 2);
                          }}
                          onFocus={() => setMunicipioCadastroAberto(cadastroTerritorial.form.municipio.trim().length >= 2)}
                          onBlur={() => window.setTimeout(() => setMunicipioCadastroAberto(false), 120)}
                          placeholder="Digite para buscar…"
                          role="combobox"
                          aria-autocomplete="list"
                          aria-expanded={municipioCadastroAberto}
                          aria-controls="municipios-cadastro-territorial"
                          autoComplete="off"
                        />
                        {municipioCadastroAberto && (
                          <div id="municipios-cadastro-territorial" className="remote-territorial-municipality-results" role="listbox">
                            {municipiosCadastroLoading && <p role="status">Buscando municípios…</p>}
                            {!municipiosCadastroLoading && municipiosCadastroError && <p role="alert">Não foi possível consultar os municípios.</p>}
                            {!municipiosCadastroLoading && !municipiosCadastroError && municipiosCadastro.map(municipio => {
                              const nome = municipio.name_municipio || municipio.name_muni;
                              const uf = normalizarUf(municipio.sigla_estado || municipio.state || municipio.code_state);
                              return (
                                <button
                                  type="button"
                                  role="option"
                                  key={`${nome}-${municipio.code_state}`}
                                  onMouseDown={event => event.preventDefault()}
                                  onClick={() => selecionarMunicipioCadastro(municipio)}
                                >
                                  <strong>{nome}</strong>
                                  <small>{uf || municipio.code_state}</small>
                                </button>
                              );
                            })}
                            {!municipiosCadastroLoading && !municipiosCadastroError && municipioCadastroTermo.trim().length >= 2 && municipiosCadastro.length === 0 && (
                              <p>Nenhum município encontrado.</p>
                            )}
                          </div>
                        )}
                      </div>
                      <label className="is-state">
                        <span>UF</span>
                        <input value={cadastroTerritorial.form.estado} placeholder="UF" maxLength="2" readOnly aria-label="UF preenchida pelo município" />
                      </label>
                    </div>
                  ) : cadastroTerritorial.tipo === 'talhao' ? (
                    <p className="remote-territorial-fixed-area-note">O talhão será salvo como uma área física permanente. O plantio será cadastrado depois, ao selecionar o talhão no mapa.</p>
                  ) : null}

                  {cadastroTerritorialErro && <p role="alert">{cadastroTerritorialErro}</p>}
                  <div className="remote-territorial-form-actions">
                    <button type="button" onClick={() => {
                      const points = poligono?.geometry?.coordinates?.[0]?.slice(0, -1).map(([lng, lat]) => ({ lat, lng })) || [];
                      setPontosDesenho(points);
                      setPoligono(null);
                      setModoDesenho(true);
                      setCadastroTerritorial(current => current ? { ...current, etapa: 'desenho' } : current);
                    }}>Ajustar desenho</button>
                    <button type="button" className="is-primary" onClick={salvarCadastroTerritorial} disabled={salvandoCadastroTerritorial || !cadastroTerritorial.form.nome.trim()}>
                      {salvandoCadastroTerritorial
                        ? 'Salvando…'
                        : cadastroTerritorial.modo === 'atualizacao' ? 'Salvar perímetro' : `Salvar ${cadastroTerritorial.tipo}`}
                    </button>
                  </div>
                  {!TERRITORIAL_WRITES_ENABLED && <small className="remote-territorial-preview-lock">Prévia protegida: o banco de produção não será alterado.</small>}
                </div>
              )}
            </section>
          )}

          {talhao && !isMobile && !cadastroTerritorial && !sensorMapaSelecionado && (!compactMapLayout || plotDetailsVisible) && (
            <section id="remote-selected-plot-details" className="remote-selected-plot-card" aria-label={`Informações de ${talhao.nome}`}>
              <header>
                <div>
                  <span>{talhao.isDraft ? 'Novo desenho' : 'Talhão selecionado'}</span>
                  {renamingPlot ? (
                    <div className="remote-plot-name-editor">
                      <input value={plotNameDraft} onChange={event => setPlotNameDraft(event.target.value)} autoFocus maxLength="60" />
                      <button type="button" onClick={salvarNomeTalhao} disabled={!plotNameDraft.trim() || salvandoNomeTalhao}>{salvandoNomeTalhao ? 'Salvando…' : 'Salvar nome'}</button>
                    </div>
                  ) : <h3>{talhao.nome}</h3>}
                </div>
                <span className={poligono ? 'is-mapped' : 'is-pending'}>{poligono ? 'Georreferenciado' : 'Aguardando desenho'}</span>
              </header>
              <div className="remote-selected-plot-facts">
                <span><small>Cultivo atual</small><strong>{talhao.ciclo_atual?.cultura || 'Sem cultivo ativo'}</strong></span>
                <span><small>Área</small><strong>{talhao.area ? `${talhao.area} ha` : 'Calcular após salvar'}</strong></span>
                <span><small>Plantio</small><strong>{talhao.ciclo_atual?.data_plantio ? new Date(`${talhao.ciclo_atual.data_plantio}T00:00:00`).toLocaleDateString('pt-BR') : 'Área disponível'}</strong></span>
                {talhao.ciclo_atual?.data_colheita_prevista && (
                  <span><small>Colheita prevista</small><strong>{new Date(`${talhao.ciclo_atual.data_colheita_prevista}T00:00:00`).toLocaleDateString('pt-BR')}</strong></span>
                )}
              </div>
              {modoDesenho ? (
                <>
                  <div className="remote-drawing-progress">
                    <i /> <span><strong>Desenhando com aderência</strong><small>{pontosDesenho.length} ponto(s) · {totalPontosAderidos} encaixe(s) automático(s)</small></span>
                    <button type="button" className="is-secondary" onClick={desfazerUltimoPonto} disabled={!pontosDesenho.length} title="Atalho: Ctrl+Z">Desfazer</button>
                    <button type="button" className="is-secondary" onClick={cancelarEdicaoPoligono}>Cancelar edição</button>
                    <button type="button" onClick={encerrarPoligono} disabled={!desenhoPodeConcluir}>Encerrar desenho</button>
                  </div>
                  <div className="remote-drawing-shortcuts">
                    <span><kbd>Ctrl</kbd> + <kbd>Z</kbd> desfaz o último ponto</span>
                    <span>Clique em um vértice e use “Excluir ponto”</span>
                    <span>Duplo clique também exclui diretamente</span>
                    {verticeSelecionado !== null && (
                      <button type="button" onClick={() => removerPontoDesenho(verticeSelecionado)} disabled={pontosDesenho.length <= 3}>
                        🗑 Excluir ponto {verticeSelecionado + 1}
                      </button>
                    )}
                  </div>
                  {erroGeometriaVisivel && <p className="remote-geometry-error" role="alert">{erroGeometriaVisivel}</p>}
                </>
              ) : (
                <div className="remote-selected-plot-actions">
                  {!renamingPlot && <button type="button" onClick={iniciarEdicaoPoligono} disabled={!poligono}>Editar desenho</button>}
                  <button type="button" onClick={() => {
                    if (renamingPlot) setPlotNameDraft(talhao.nome || '');
                    setRenamingPlot(current => !current);
                  }}>{renamingPlot ? 'Cancelar' : 'Renomear'}</button>
                  {talhao.ciclo_atual && propriedadeSelecionada?._accessRole !== 'viewer' && !renamingPlot && (
                    <button type="button" onClick={() => abrirGestaoCultivo('edit')}>Editar plantio</button>
                  )}
                  {alteracaoPoligonoPendente && <button type="button" className="is-primary" onClick={salvarPoligono} disabled={!poligono || salvando}>{salvando ? 'Salvando…' : 'Salvar alterações'}</button>}
                  {propriedadeSelecionada?._accessRole === 'owner' && !renamingPlot && (
                    <button
                      type="button"
                      className="is-danger"
                      onClick={excluirTalhaoAtual}
                      disabled={excluindoTalhao || Boolean(motivoBloqueioExclusaoTalhao(talhao))}
                      title={motivoBloqueioExclusaoTalhao(talhao) || 'Excluir talhão vazio'}
                    >
                      {excluindoTalhao ? 'Excluindo…' : 'Excluir talhão'}
                    </button>
                  )}
                </div>
              )}
              {propriedadeSelecionada?._accessRole === 'owner' && !renamingPlot && motivoBloqueioExclusaoTalhao(talhao) && (
                <p className="remote-selected-plot-protection-note">Exclua o plantio primeiro. Talhões com qualquer histórico permanecem protegidos.</p>
              )}
              {msgSalvo && <p>{msgSalvo}</p>}
            </section>
          )}
          </div>

          {isMobile && !cadastroTerritorial && (
            <>
              {talhaoId && (
                <div className="remote-mobile-controls" style={s.controlesMobile}>
                  {!modoDesenho && !poligono && (
                    <button style={{ ...s.btnPrimary, whiteSpace: 'nowrap' }} onClick={() => setModoDesenho(true)}>✏️ Desenhar Área</button>
                  )}
                  {modoDesenho && (
                    <>
                      <span style={s.hint}>{poligono ? 'Arraste ou toque num ponto para excluir' : 'Toque no mapa'} ({pontosDesenho.length} marcados)</span>
                      <button style={{ ...s.btnToggleOff, whiteSpace: 'nowrap' }} onClick={desfazerUltimoPonto} disabled={!pontosDesenho.length}>↶ Desfazer</button>
                      {verticeSelecionado !== null && (
                        <button style={{ ...s.btnDanger, whiteSpace: 'nowrap' }} onClick={() => removerPontoDesenho(verticeSelecionado)} disabled={pontosDesenho.length <= 3}>
                          🗑 Ponto {verticeSelecionado + 1}
                        </button>
                      )}
                      <button style={{ ...s.btnSuccess, whiteSpace: 'nowrap' }} onClick={encerrarPoligono} disabled={!desenhoPodeConcluir}>{poligono ? '💾 Concluir' : '✔ Encerrar'}</button>
                      <button style={{ ...s.btnDanger, whiteSpace: 'nowrap' }} onClick={cancelarEdicaoPoligono}>✕ Cancelar</button>
                      {erroGeometriaVisivel && <span style={s.erro}>{erroGeometriaVisivel}</span>}
                    </>
                  )}
                  {poligono && !modoDesenho && (
                    <>
                      {alteracaoPoligonoPendente && <button style={{ ...s.btnSave, whiteSpace: 'nowrap' }} onClick={salvarPoligono} disabled={salvando}>{salvando ? 'Salvando...' : '💾 Salvar alterações'}</button>}
                      <button style={{ ...s.btnToggleOn, whiteSpace: 'nowrap' }} onClick={iniciarEdicaoPoligono}>✏️ Editar</button>
                      <button style={{ ...(mostrarPoligono ? s.btnToggleOn : s.btnToggleOff), whiteSpace: 'nowrap' }} onClick={() => setMostrarPoligono(v => !v)}>{mostrarPoligono ? '👁 Ocultar' : '👁 Mostrar'}</button>
                      {(tileUrl || mapaNdviRecortado) && (
                        <button style={{ ...(mostrarImagemSatelite ? s.btnToggleOff : s.btnToggleOn), whiteSpace: 'nowrap' }} onClick={() => setMostrarImagemSatelite(v => !v)}>
                          {mostrarImagemSatelite ? '🗺️ Base' : '🛰️ Satélite'}
                        </button>
                      )}
                      <button style={{ ...s.btnDanger, whiteSpace: 'nowrap' }} onClick={limparPoligono}>🗑 Limpar</button>
                      {msgSalvo && <span style={s.msgOk}>{msgSalvo}</span>}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          <div className="remote-map-container" style={{ ...s.mapaContainer, cursor: modoDesenho || modoMedicao ? 'crosshair' : 'grab' }}>
            <MapContainer
              center={initialContext?.mapView?.center
                ? [initialContext.mapView.center.lat, initialContext.mapView.center.lng]
                : CENTRO_BRASIL}
              zoom={initialContext?.mapView?.zoom || 5}
              zoomAnimation={false}
              fadeAnimation={false}
              style={{ width: '100%', height: '100%' }}
            >
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="Satélite">
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution="Esri World Imagery"
                    maxZoom={20}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Mapa">
                  <TileLayer
                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="© OpenStreetMap contributors"
                  />
                </LayersControl.BaseLayer>
              </LayersControl>

              {mostrarInmet && !previewMode && !modoDesenho && !modoMedicao && !cadastroTerritorial && (
                <InmetStationsLayer
                  onStatus={setInmetStatus}
                  referencePoint={calcularCentroGeometria(propriedadeSelecionada?.geometria)}
                />
              )}

              {tileUrl && tipoImagem !== 'ndvi' && mostrarImagemSatelite && abaResultados !== 'terreno' && (
                <TileLayer
                  key={tileUrl}
                  url={tileUrl}
                  opacity={1}
                  zIndex={500}
                  attribution="Sentinel-2 © ESA | TiTiler.xyz"
                  tileSize={256}
                  eventHandlers={{
                    tileerror: () => setErroTile(true),
                    tileload: () => setErroTile(false),
                  }}
                />
              )}

              {mapaNdviRecortado && tipoImagem === 'ndvi' && mostrarImagemSatelite && abaResultados !== 'terreno' && (
                <ImageOverlay
                  url={mapaNdviRecortado.dataUrl}
                  bounds={mapaNdviRecortado.bounds}
                  opacity={0.92}
                  zIndex={500}
                  interactive={false}
                />
              )}

              {dadosTerreno && camadaTerreno === 'altitude' && abaResultados === 'terreno' && <ImageOverlay url={dadosTerreno.mapaAltitude.dataUrl} bounds={dadosTerreno.mapaAltitude.bounds} opacity={0.82} zIndex={480} />}
              {dadosTerreno && camadaTerreno === 'declividade' && abaResultados === 'terreno' && <ImageOverlay url={dadosTerreno.mapaDeclividade.dataUrl} bounds={dadosTerreno.mapaDeclividade.bounds} opacity={0.86} zIndex={480} />}
              {dadosTerreno && mostrarCurvasNivel && abaResultados === 'terreno' && curvasNivelAtivas.map((curva, indice) => {
                const curvaMestra = niveisCurvasMestras.has(Number(curva.nivel));
                return (
                  <React.Fragment key={`${curva.nivel}-${indice}`}>
                    <Polyline positions={curva.positions} pathOptions={{ color: '#ffffff', weight: curvaMestra ? 4.6 : 3.5, opacity: 0.92, interactive: false }} />
                    <Polyline positions={curva.positions} pathOptions={{ color: '#4b2e1d', weight: curvaMestra ? 2.2 : 1.4, opacity: 1, interactive: false }} />
                  </React.Fragment>
                );
              })}
              {dadosTerreno && mostrarCurvasNivel && mostrarRotulosCurvas && abaResultados === 'terreno' && rotulosCurvasNivel.map(rotulo => (
                <Marker
                  key={`rotulo-curva-${rotulo.id}`}
                  position={rotulo.position}
                  icon={criarIconeRotuloCurva(rotulo)}
                  interactive={false}
                  zIndexOffset={950}
                />
              ))}

              {propertyPositions && limitesTerritoriaisVisiveis && cadastroTerritorial?.tipo !== 'propriedade' && (
                <Polygon
                  positions={propertyPositions}
                  interactive={!modoDesenho && !modoMedicao}
                  pathOptions={escopoSelecionado?.tipo === 'propriedade'
                    ? { color: '#f59b23', weight: 3.5, dashArray: '9,6', fillColor: '#f5b942', fillOpacity: 0.18 }
                    : { color: '#f5b942', weight: 3, dashArray: '9,7', fillOpacity: 0.025 }}
                  eventHandlers={modoDesenho || modoMedicao ? undefined : { click: () => selecionarPropriedadeNoMapa(propId) }}
                >
                  <Tooltip sticky direction="top">Limite · {propriedadeSelecionada?.nome || 'Propriedade'} · Clique para operar</Tooltip>
                </Polygon>
              )}

              {limitesTerritoriaisVisiveis && portfolioPolygons.map(({ item, positions }) => (
                <React.Fragment key={`portfolio-${item.id}`}>
                <Polygon
                  positions={positions}
                  interactive={!modoDesenho && !modoMedicao}
                  pathOptions={{ color: pecuariaMap.getState(item)?.color || '#20b797', weight: 2, fillColor: pecuariaMap.getState(item)?.color || '#28c39f', fillOpacity: 0.16 }}
                  eventHandlers={modoDesenho || modoMedicao ? undefined : { click: () => selecionarTalhaoNoMapa(String(item.id)) }}
                >
                  <Tooltip sticky direction="top" className="remote-plot-map-tooltip">
                    <div><strong>{item.nome}</strong><span>{item.ciclo_atual?.cultura || 'Sem cultivo ativo'}</span><small>{item.area ? `${item.area} ha` : 'Área não informada'} · Clique para abrir</small></div>
                    {pecuariaMap.getState(item) && <div><strong>{pecuariaMap.getState(item).label}</strong><span>{pecuariaMap.getState(item).current.map(o => o.nome_lote).join(' + ') || 'Sem lote no momento'}</span><small>{pecuariaMap.getState(item).restDays != null ? `${pecuariaMap.getState(item).restDays} dias de descanso · conferir campo` : `${formatNumber(pecuariaMap.getState(item).uaPerHa)} UA/ha`}</small></div>}
                  </Tooltip>
                </Polygon>
                {mostrarPastagens && <OperationalPlotIndicator plot={item} geometry={item.geometria} pecuariaMap={pecuariaMap} zoom={mapZoom} interactive={!modoDesenho && !modoMedicao} onClick={() => selecionarTalhaoNoMapa(String(item.id))} />}
                </React.Fragment>
              ))}

              {!modoDesenho && !cadastroTerritorial && sensorGroups.map((group, groupIndex) => {
                if (group.sensors.length > 1) {
                  const groupAccessibleName = `Grupo com ${group.sensors.length} dispositivos de campo`;
                  return (
                    <Marker
                      key={`sensor-group-${groupIndex}-${group.sensors.map(item => item.id || item.device_id).join('-')}`}
                      position={[group.lat, group.lng]}
                      icon={makeSensorClusterIcon(group.sensors.length, mapZoom)}
                      title={groupAccessibleName}
                      alt={groupAccessibleName}
                      zIndexOffset={1100}
                      interactive={!modoMedicao}
                      eventHandlers={{
                        add: event => applyMarkerAccessibleName(event, groupAccessibleName),
                        ...(!modoMedicao ? { click: () => {
                          setVoo({ lat: group.lat, lng: group.lng, zoom: Math.min(mapZoom + 3, 18) });
                          setFitPoly(null);
                        } } : {}),
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -16]} className="remote-sensor-map-tooltip">
                        <div>
                          <strong>{group.sensors.length} dispositivos próximos</strong>
                          <span>{group.sensors.slice(0, 3).map(item => item.nome_sensor || item.device_id).join(' · ')}</span>
                          <small>Clique para aproximar{group.sensors.length > 3 ? ` · +${group.sensors.length - 3} estações` : ''}</small>
                        </div>
                      </Tooltip>
                    </Marker>
                  );
                }
                const sensor = group.sensors[0];
                const type = SENSOR_TYPE_INFO[sensor.tipo_sensor] || SENSOR_TYPE_INFO.meteorologico;
                const connection = sensorConnection(sensor);
                const weather = sensor.tipo_sensor === 'meteorologico' ? sensorWeatherStatus(sensor) : null;
                const markerSize = mapZoom <= 7 ? 24 : mapZoom <= 11 ? 32 : 44;
                const accessibleName = `${type.label} ${sensor.nome_sensor || sensor.device_id}${weather ? ` · ${weather.label}` : ''}`;
                return (
                  <Marker
                    key={`sensor-${sensor.id || sensor.device_id}`}
                    position={[group.lat, group.lng]}
                    icon={makeSensorMapIcon(sensor, mapZoom)}
                    title={accessibleName}
                    alt={accessibleName}
                    zIndexOffset={1200}
                    interactive={!modoMedicao}
                    eventHandlers={{
                      add: event => applyMarkerAccessibleName(event, accessibleName),
                      ...(!modoMedicao ? { click: () => setSensorMapaSelecionado(sensor) } : {}),
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -(markerSize - 6)]} className="remote-sensor-map-tooltip">
                      <div>
                        <strong>{sensor.nome_sensor || sensor.device_id}</strong>
                        <span>{type.label} · {connection.label}</span>
                        <small>{weather ? `${weather.label} · ` : ''}{connection.detail} · Clique para abrir o resumo</small>
                      </div>
                    </Tooltip>
                  </Marker>
                );
              })}

              {polyPositions && limitesTerritoriaisVisiveis && !modoDesenho
                && (cadastroTerritorial || escopoSelecionado?.tipo !== 'propriedade') && (
                <>
                <Polygon
                  positions={polyPositions}
                  interactive={!cadastroTerritorial && !modoMedicao}
                  eventHandlers={cadastroTerritorial || modoMedicao ? undefined : {
                    click: () => escopoSelecionado?.tipo === 'propriedade'
                      ? selecionarPropriedadeNoMapa(propId)
                      : selecionarTalhaoNoMapa(talhaoId),
                    contextmenu: event => {
                      event.originalEvent?.preventDefault();
                      setPolygonContextMenu({ lat: event.latlng.lat, lng: event.latlng.lng });
                    },
                  }}
                  pathOptions={cadastroTerritorial?.tipo === 'propriedade' || escopoSelecionado?.tipo === 'propriedade'
                    ? { color: '#f59b23', weight: 3.5, dashArray: '9,6', fillColor: '#f5b942', fillOpacity: 0.18 }
                    : { color: '#0b78c5', weight: 3.5, fillColor: pecuariaMap.getState(talhao)?.color || '#17a3c7', fillOpacity: abaResultados === 'terreno' && camadaTerreno === 'curvas' ? 0.025 : 0.22 }}
                >
                  <Tooltip sticky direction="top" className="remote-plot-map-tooltip">
                    <div>
                      <strong>{cadastroTerritorial?.form?.nome || contextoSelecionado?.nome || (cadastroTerritorial?.tipo === 'propriedade' ? 'Nova propriedade' : 'Área selecionada')}</strong>
                      <span>{cadastroTerritorial ? (cadastroTerritorial.tipo === 'propriedade' ? 'Perímetro da propriedade' : 'Nova área permanente') : (escopoSelecionado?.tipo === 'propriedade' ? 'Propriedade completa' : talhao?.ciclo_atual?.cultura || 'Sem cultivo ativo')}</span>
                      <small>{formatarAreaHectares(areaTerritorialAtual)} · Área calculada pelo mapa</small>
                      {pecuariaMap.getState(talhao) && <><strong>{pecuariaMap.getState(talhao).label}</strong><span>{pecuariaMap.getState(talhao).current.map(o => o.nome_lote).join(' + ') || 'Sem lote no momento'}</span><small>{pecuariaMap.getState(talhao).restDays != null ? `${pecuariaMap.getState(talhao).restDays} dias de descanso · conferir campo` : `${formatNumber(pecuariaMap.getState(talhao).uaPerHa)} UA/ha`}</small></>}
                    </div>
                  </Tooltip>
                  {cadastroTerritorial && areaTerritorialAtual && (
                    <Tooltip permanent direction="center" className="remote-area-map-label">
                      {formatarAreaHectares(areaTerritorialAtual)}
                    </Tooltip>
                  )}
                </Polygon>
                {!cadastroTerritorial && mostrarPastagens && escopoSelecionado?.tipo !== 'propriedade' && <OperationalPlotIndicator plot={talhao} geometry={poligono} pecuariaMap={pecuariaMap} zoom={mapZoom} interactive={!modoMedicao} onClick={() => selecionarTalhaoNoMapa(talhaoId)} />}
                </>
              )}

              {polygonContextMenu && !cadastroTerritorial && (
                <Popup
                  position={[polygonContextMenu.lat, polygonContextMenu.lng]}
                  closeButton
                  className="remote-polygon-context-menu"
                  eventHandlers={{ remove: () => setPolygonContextMenu(null) }}
                >
                  <button
                    type="button"
                    onClick={editarPerimetroPeloMapa}
                    onMouseDown={event => event.stopPropagation()}
                    disabled={escopoSelecionado?.tipo === 'propriedade' && propriedadeSelecionada?._accessRole === 'viewer'}
                    title={escopoSelecionado?.tipo === 'propriedade' && propriedadeSelecionada?._accessRole === 'viewer'
                      ? 'Solicite acesso de editor para alterar o perímetro'
                      : 'Editar os pontos deste perímetro'}
                  >
                    <span aria-hidden="true">▱</span>
                    <strong>Editar perímetro</strong>
                  </button>
                </Popup>
              )}

              {zonasManejo && mostrarZonas && abaResultados === 'analise' && (
                <ImageOverlay url={zonasManejo.dataUrl} bounds={zonasManejo.bounds} opacity={0.85} zIndex={600} />
              )}

              {desenhoPositions.length > 0 && (
                <>
                  {desenhoPositions.length >= 3 ? (
                    <Polygon
                      positions={desenhoPositions}
                      pathOptions={validacaoDesenhoEmTempoReal.valido
                        ? { color: '#16866f', weight: 2.5, dashArray: '6,4', fillColor: '#18a68b', fillOpacity: 0.12 }
                        : { color: '#d54444', weight: 3, dashArray: '6,4', fillColor: '#e34d4d', fillOpacity: 0.16 }}
                    >
                      {areaEmDesenho && (
                        <Tooltip permanent direction="center" className="remote-area-map-label is-drawing">
                          {formatarAreaHectares(areaEmDesenho)}
                        </Tooltip>
                      )}
                    </Polygon>
                  ) : (
                    <Polyline
                      positions={desenhoPositions}
                      pathOptions={{ color: '#ff6d00', weight: 2, dashArray: '6,4' }}
                    />
                  )}
                  {pontosDesenho.map((pos, i) => (
                    <VerticeDesenho
                      key={`${i}-${pos.snapSourceId || 'livre'}`}
                      index={i}
                      position={pos}
                      selecionado={verticeSelecionado === i}
                      limitesAderencia={limitesAderencia}
                      onMover={moverPontoDesenho}
                      onRemover={removerPontoDesenho}
                      onSelecionar={setVerticeSelecionado}
                      onArrasteStart={() => protecaoCliqueAposArrasteRef.current.iniciar()}
                      onArrasteEnd={() => protecaoCliqueAposArrasteRef.current.encerrar()}
                    />
                  ))}
                  {pontosIntermediarios.map(point => (
                    <VerticeIntermediario
                      key={`midpoint-${point.insertIndex}`}
                      position={point}
                      insertIndex={point.insertIndex}
                      limitesAderencia={limitesAderencia}
                      onAdicionar={adicionarVerticeNaAresta}
                      onArrasteStart={() => protecaoCliqueAposArrasteRef.current.iniciar()}
                      onArrasteEnd={() => protecaoCliqueAposArrasteRef.current.encerrar()}
                    />
                  ))}
                </>
              )}

              {modoDesenho && sugestaoAderencia && (
                <Marker
                  position={[sugestaoAderencia.lat, sugestaoAderencia.lng]}
                  icon={sugestaoAderenciaIcon}
                  interactive={false}
                  zIndexOffset={1600}
                >
                  <Tooltip permanent direction="top" offset={[0, -13]} className="remote-snap-tooltip">
                    Encaixar na {sugestaoAderencia.tipo === 'vertice' ? 'quina' : 'borda'} de {sugestaoAderencia.limiteNome || 'área existente'}
                  </Tooltip>
                </Marker>
              )}

              {modoMedicao === 'distancia' && posicoesMedicao.length >= 2 && (
                <Polyline
                  positions={posicoesMedicao}
                  interactive={false}
                  pathOptions={{ color: '#f7b32b', weight: 4, dashArray: '8,6', opacity: 0.95 }}
                />
              )}

              {modoMedicao === 'area' && posicoesMedicao.length >= 2 && (
                posicoesMedicao.length >= 3 ? (
                  <Polygon
                    positions={posicoesMedicao}
                    interactive={false}
                    pathOptions={{ color: '#f7b32b', weight: 3.5, dashArray: '8,6', fillColor: '#f7b32b', fillOpacity: 0.2 }}
                  />
                ) : (
                  <Polyline
                    positions={posicoesMedicao}
                    interactive={false}
                    pathOptions={{ color: '#f7b32b', weight: 4, dashArray: '8,6' }}
                  />
                )
              )}

              {modoMedicao === 'circulo' && pontosMedicaoPreview.length >= 2 && raioMedicao > 0 && (
                <Circle
                  center={[pontosMedicaoPreview[0].lat, pontosMedicaoPreview[0].lng]}
                  radius={raioMedicao}
                  interactive={false}
                  pathOptions={{ color: '#f7b32b', weight: 3.5, dashArray: '8,6', fillColor: '#f7b32b', fillOpacity: 0.2 }}
                />
              )}

              {modoMedicao && pontosMedicao.map((ponto, index) => (
                <CircleMarker
                  key={`medicao-${modoMedicao}-${index}`}
                  center={[ponto.lat, ponto.lng]}
                  radius={5}
                  interactive={false}
                  pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#f7a600', fillOpacity: 1 }}
                />
              ))}

              {modoMedicao && resultadoMedicao && pontosMedicaoPreview.length > 1 && (
                <CircleMarker
                  center={[pontosMedicaoPreview[pontosMedicaoPreview.length - 1].lat, pontosMedicaoPreview[pontosMedicaoPreview.length - 1].lng]}
                  radius={1}
                  interactive={false}
                  pathOptions={{ opacity: 0, fillOpacity: 0 }}
                >
                  <Tooltip permanent direction="top" offset={[0, -10]} className="remote-measure-map-label">
                    {resultadoMedicao}
                  </Tooltip>
                </CircleMarker>
              )}

              <ColetorPontos
                ativo={permiteAdicionarPontoNoMapa(modoDesenho, poligono)}
                onAdicionar={adicionarPonto}
                onSugestao={atualizarSugestaoAderencia}
                limitesAderencia={limitesAderencia}
                ignorarClique={() => protecaoCliqueAposArrasteRef.current.deveIgnorarClique()}
              />
              <ColetorMedicao
                ativo={Boolean(modoMedicao) && !modoDesenho && !cadastroTerritorial}
                onAdicionar={adicionarPontoMedicao}
                onMover={setCursorMedicao}
              />
              {camadaDrone && (
                <MapeamentoDroneLayer
                  key={camadaDrone.id}
                  url={camadaDrone.url}
                  opacity={opacidadeDrone}
                  onStatus={setStatusCamadaDrone}
                />
              )}
              <MapViewObserver onChange={handleMapViewChange} />
              <FitPortfolioBounds positions={portfolioPositions} enabled={shouldFitPortfolio} />
              {voo && <CentrarMapa lat={voo.lat} lng={voo.lng} zoom={voo.zoom} />}
              {fitPoly && <FitBounds positions={fitPoly} />}
            </MapContainer>

            {mostrarInmet && !previewMode && !modoDesenho && !modoMedicao && !cadastroTerritorial && (
              <p className="inmet-layer-notice" role="status">
                {inmetStatus.loading ? 'Carregando estações INMET…'
                  : inmetStatus.error || `INMET · ${inmetStatus.count} estações · Clique em um ponto para consultar medições.`}
              </p>
            )}

            {!modoDesenho && !cadastroTerritorial && (
              <div
                className={`remote-measure-control ${medicaoAberta ? 'is-open' : ''}`}
                onMouseDown={event => event.stopPropagation()}
                onClick={event => event.stopPropagation()}
              >
                <button
                  type="button"
                  className="remote-measure-control__toggle"
                  onClick={() => {
                    if (medicaoAberta) fecharMedicao();
                    else setMedicaoAberta(true);
                  }}
                  aria-label={medicaoAberta ? 'Fechar ferramentas de medição' : 'Medir no mapa'}
                  aria-expanded={medicaoAberta}
                  title="Medir distância, área ou círculo"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M5.2 15.7 15.7 5.2l3.1 3.1L8.3 18.8z" />
                    <path d="m9 12 1.7 1.7m1-5.5 1.7 1.7m-7.8 5.5 1.7 1.7" />
                  </svg>
                </button>

                {medicaoAberta && (
                  <section className="remote-measure-panel" aria-label="Medição no mapa">
                    <header>
                      <div><span>Ferramenta de mapa</span><strong>Medir</strong></div>
                      <button type="button" onClick={fecharMedicao} aria-label="Fechar medição">×</button>
                    </header>
                    <div className="remote-measure-panel__modes" role="group" aria-label="Tipo de medição">
                      <button type="button" className={modoMedicao === 'distancia' ? 'is-active' : ''} onClick={() => selecionarModoMedicao('distancia')}>
                        <span aria-hidden="true">↔</span><strong>Distância</strong>
                      </button>
                      <button type="button" className={modoMedicao === 'area' ? 'is-active' : ''} onClick={() => selecionarModoMedicao('area')}>
                        <span aria-hidden="true">▱</span><strong>Área</strong>
                      </button>
                      <button type="button" className={modoMedicao === 'circulo' ? 'is-active' : ''} onClick={() => selecionarModoMedicao('circulo')}>
                        <span aria-hidden="true">○</span><strong>Círculo</strong>
                      </button>
                    </div>

                    {modoMedicao ? (
                      <div className="remote-measure-panel__result" aria-live="polite">
                        <span>{modoMedicao === 'distancia' ? 'Distância total' : modoMedicao === 'area' ? 'Área medida' : 'Área do círculo'}</span>
                        <strong>{resultadoMedicao}</strong>
                        {modoMedicao === 'circulo' && raioMedicao > 0 && (
                          <small>Raio {formatarDistanciaMedicao(raioMedicao)} · Perímetro {formatarDistanciaMedicao(2 * Math.PI * raioMedicao)}</small>
                        )}
                        {modoMedicao !== 'circulo' && (
                          <small>{pontosMedicao.length === 0 ? 'Clique no mapa para iniciar.' : 'Continue clicando para adicionar pontos.'}</small>
                        )}
                        {modoMedicao === 'circulo' && (
                          <small>{pontosMedicao.length === 0 ? 'Clique para definir o centro.' : pontosMedicao.length === 1 ? 'Clique novamente para definir o raio.' : 'Clique em outro ponto para iniciar um novo círculo.'}</small>
                        )}
                      </div>
                    ) : (
                      <p className="remote-measure-panel__hint">Escolha uma medição e marque os pontos diretamente no mapa.</p>
                    )}

                    <footer>
                      <button type="button" onClick={() => setPontosMedicao(atuais => atuais.slice(0, -1))} disabled={pontosMedicao.length === 0}>Desfazer</button>
                      <button type="button" onClick={limparMedicao} disabled={pontosMedicao.length === 0}>Limpar</button>
                    </footer>
                  </section>
                )}
              </div>
            )}

            {droneHabilitado && painelDroneAberto && propriedadeSelecionada && !cadastroTerritorial && (
              <MapeamentoDronePanel
                propriedade={propriedadeSelecionada}
                user={user}
                onFechar={() => setPainelDroneAberto(false)}
                camadaAtiva={camadaDrone}
                onAtivar={camada => { setCamadaDrone(camada); if (!camada) setStatusCamadaDrone(null); }}
                opacidade={opacidadeDrone}
                onOpacidade={setOpacidadeDrone}
                statusCamada={statusCamadaDrone}
              />
            )}

            {sensorMapaSelecionado && sensorCoordinates(sensorMapaSelecionado) && (
              <section
                className={`remote-selected-sensor-card ${contextoSelecionado ? 'is-with-map-actions' : ''}`}
                role={sensorMapaSelecionado.tipo_sensor === 'camera' ? 'region' : 'button'}
                tabIndex={sensorMapaSelecionado.tipo_sensor === 'camera' ? undefined : '0'}
                aria-label={`${sensorMapaSelecionado.tipo_sensor === 'camera' ? 'Câmera' : 'Abrir detalhes de'} ${sensorMapaSelecionado.nome_sensor || sensorMapaSelecionado.device_id}`}
                onClick={() => sensorMapaSelecionado.tipo_sensor !== 'camera' && abrirSensorNoModulo(sensorMapaSelecionado)}
                onKeyDown={event => {
                  if (sensorMapaSelecionado.tipo_sensor !== 'camera' && (event.key === 'Enter' || event.key === ' ')) abrirSensorNoModulo(sensorMapaSelecionado);
                }}
              >
                <header>
                  <span className="remote-selected-sensor-card__icon" aria-hidden="true">{selectedSensorWeather?.icon || selectedSensorType?.icon}</span>
                  <div>
                    <span>Sensor instalado</span>
                    <h3>{sensorMapaSelecionado.nome_sensor || sensorMapaSelecionado.device_id}</h3>
                    <small>{selectedSensorType?.label} · {sensorMapaSelecionado.device_id}</small>
                  </div>
                  <button
                    type="button"
                    aria-label="Fechar resumo do sensor"
                    onClick={event => {
                      event.stopPropagation();
                      setSensorMapaSelecionado(null);
                    }}
                  >×</button>
                </header>
                <div className={`remote-selected-sensor-card__status is-${selectedSensorConnection?.tone || 'offline'}`}>
                  <i /> <strong>{selectedSensorConnection?.label}</strong><span>{selectedSensorConnection?.detail}</span>
                </div>
                <div className="remote-selected-sensor-card__metrics">
                  {selectedSensorMetrics.map(metric => (
                    <span key={metric.label}><small>{metric.label}</small><strong>{metric.value}</strong></span>
                  ))}
                </div>
                {sensorMapaSelecionado.tipo_sensor === 'camera' && (
                  <CameraStreamPlayer camera={sensorMapaSelecionado} className="remote-camera-stream" />
                )}
                <footer><span><b>📍</b> {sensorCoordinates(sensorMapaSelecionado).lat.toFixed(5)}, {sensorCoordinates(sensorMapaSelecionado).lng.toFixed(5)}</span><strong>{sensorMapaSelecionado.tipo_sensor === 'camera' ? 'Vídeo ao vivo' : 'Abrir dados e gráficos →'}</strong></footer>
              </section>
            )}
          </div>

          {isMobile && talhao && !cadastroTerritorial && (
            <div className="remote-mobile-quick-actions" aria-label={`Ações rápidas em ${talhao.nome}`}>
              <button type="button" onClick={() => abrirAcaoTerritorial('new-order', 'A nova ordem será aberta já vinculada a este talhão.')}>✓<span>Ordem</span></button>
              <button type="button" onClick={() => abrirAcaoTerritorial('new-expense', 'A despesa será aberta com propriedade e talhão preenchidos.')}>R$<span>Despesa</span></button>
              {talhao.ciclo_atual && propriedadeSelecionada?._accessRole !== 'viewer' && <button type="button" onClick={() => abrirGestaoCultivo('edit')}>✎<span>Editar plantio</span></button>}
              <button type="button" onClick={() => abrirGestaoCultivo(talhao.ciclo_atual ? 'harvest' : 'create')} disabled={propriedadeSelecionada?._accessRole === 'viewer'}>{talhao.ciclo_atual ? '🌾' : '↗'}<span>{talhao.ciclo_atual ? 'Colheita' : 'Plantio'}</span></button>
              <button
                type="button"
                onClick={() => abrirAcaoTerritorial('history', 'O Caderno será filtrado por este talhão.')}
                title="Ir para o Caderno de Campo do talhão"
                aria-label="Ir para o Caderno de Campo do talhão"
              >≡<span>Caderno</span></button>
              <button
                type="button"
                onClick={() => abrirAcaoTerritorial('plot-info', 'Abrindo as informações completas deste talhão.')}
                title="Abrir este talhão em Propriedades e Talhões"
                aria-label="Abrir informações do talhão em Propriedades e Talhões"
              >ⓘ<span>Info talhão</span></button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile: painel aberto diretamente pelas ações Imagens, NDVI e Terreno */}
      {isMobile && poligono && !cadastroTerritorial && painelAberto && (
        <>
          <div style={s.sheetBackdrop} onClick={() => setPainelAberto(false)} />
          <div
            style={s.bottomSheet}
            role="dialog"
            aria-modal="true"
            aria-label={escopoSelecionado?.tipo === 'propriedade' ? 'Mapas da propriedade' : 'Mapas do talhão'}
          >
            <div style={s.sheetHandleBar}>
              <span style={s.painelHandle} />
              <span style={s.sheetTitle}>
                ▧ {escopoSelecionado?.tipo === 'propriedade' ? 'Mapas da propriedade' : 'Mapas do talhão'}
              </span>
              <button
                type="button"
                style={s.sheetClose}
                onClick={() => setPainelAberto(false)}
                aria-label={escopoSelecionado?.tipo === 'propriedade' ? 'Fechar mapas da propriedade' : 'Fechar mapas do talhão'}
                title="Fechar"
              >
                ×
              </button>
            </div>
            <div style={s.sheetScroll}>
              <div style={s.sidebarTabbar}>
                <button style={abaResultados === 'imagens' ? s.sidebarTabBtnActive : s.sidebarTabBtn} onClick={() => setAbaResultados('imagens')}>Imagens</button>
                <button style={abaResultados === 'analise' ? s.sidebarTabBtnActive : s.sidebarTabBtn} onClick={abrirNdvi} disabled={!cenaUrl}>NDVI</button>
                <button style={abaResultados === 'terreno' ? s.sidebarTabBtnActive : s.sidebarTabBtn} onClick={() => setAbaResultados('terreno')}>Terreno</button>
              </div>
              {abaResultados === 'terreno' ? renderTerreno() : renderBuscaEResultados(false)}
            </div>
          </div>
        </>
      )}

      <GestaoCultivoModal
        open={gestaoCultivoAberta}
        onClose={() => setGestaoCultivoAberta(false)}
        talhao={talhao}
        cicloAtual={talhao?.ciclo_atual || null}
        user={user}
        previewMode={previewMode}
        mode={gestaoCultivoModo}
        onSaved={atualizarTalhaoComCiclo}
        darkMode={darkMode}
      />
    </div>
  );
}

export default function SensoriamentoRemoto({ onBack, onNavigate, user, previewMode = false, previewData = null, initialContext = null, pecuariaEnabled = false }) {
  const addonSateliteReal = useAddonSatelite(previewMode ? undefined : user?.id);
  const addonSatelite = previewMode
    ? {
        loading: false,
        temAcesso: true,
        trialUsado: false,
        status: 'ativo',
        ativarTrial: async () => ({ sucesso: true }),
      }
    : addonSateliteReal;

  if (addonSatelite.loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ fontSize: 14, color: '#888' }}>Verificando acesso...</div>
      </div>
    );
  }

  if (!addonSatelite.temAcesso) {
    return (
      <>
        <AddonSateliteModal
          show={true}
          onClose={onBack}
          usuario={user}
          trialUsado={addonSatelite.trialUsado}
          statusAtual={addonSatelite.status}
          onTrialAtivado={async () => {
            const resultado = await addonSatelite.ativarTrial();
            return resultado;
          }}
        />
        <div style={{ minHeight: '100vh' }} />
      </>
    );
  }

  return (
    <SensoriamentoRemotoInner
      onBack={onBack}
      onNavigate={onNavigate}
      user={user}
      previewMode={previewMode}
      previewData={previewData}
      initialContext={initialContext}
      pecuariaEnabled={pecuariaEnabled}
    />
  );
}

// ---------------------------------------------------------------------------
// Estilos — alinhados ao design system Climorax
// ---------------------------------------------------------------------------
function makeStyles(dm) {
const ACCENT = 'linear-gradient(135deg, #0f7ec8, #17a38e)';
const s = {
  // Layout raiz
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    position: 'relative',
    background: dm ? '#0f1722' : '#eef3f9',
    fontFamily: "'Inter', 'Segoe UI', sans-serif"
  },

  // Header
  header: {
    position: 'sticky', top: 0, zIndex: 20,
    background: dm ? 'rgba(15,23,34,0.96)' : 'rgba(255,255,255,0.97)',
    paddingLeft: dm ? 20 : 24, paddingRight: 24,
    paddingTop: 12, paddingBottom: 12,
    borderBottom: `1px solid ${dm ? '#1f2c3d' : '#e4eaf1'}`,
    boxShadow: dm ? '0 6px 18px rgba(0,0,0,0.3)' : '0 4px 14px rgba(20,60,100,0.06)',
    flexShrink: 0,
  },
  btnVoltar: {
    flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '6px 4px', color: dm ? '#7dd3c0' : '#0f7ec8',
    fontSize: 14, fontWeight: 600,
    borderRadius: 6, transition: 'opacity 0.15s', whiteSpace: 'nowrap',
  },
  headerIcon: {
    width: 38, height: 38, borderRadius: 10,
    background: ACCENT,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 18,
    boxShadow: '0 4px 12px rgba(15,126,200,0.35)',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 18, fontWeight: 800,
    color: dm ? '#e7eef7' : '#0f172a', margin: 0, lineHeight: 1.2,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  headerSubtitle: { margin: 0, fontSize: 11, color: dm ? '#7893ad' : '#94a3b8', marginTop: 2, whiteSpace: 'nowrap' },
  headerBreadcrumb: {
    marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 13, fontWeight: 700, color: dm ? '#cfe4ff' : '#0f4c7c',
    background: dm ? 'rgba(23,163,142,0.12)' : 'rgba(15,126,200,0.08)',
    border: `1px solid ${dm ? 'rgba(23,163,142,0.3)' : 'rgba(15,126,200,0.18)'}`,
    padding: '6px 14px', borderRadius: 99,
  },
  headerBreadcrumbDot: { width: 7, height: 7, borderRadius: '50%', background: '#17a38e', flexShrink: 0 },

  // Barra de abas de contexto (topo, desktop)
  topTabbar: {
    display: 'flex', gap: 6, padding: '8px 24px',
    background: dm ? '#141f2c' : '#ffffff',
    borderBottom: `1px solid ${dm ? '#1f2c3d' : '#e4eaf1'}`,
    flexShrink: 0,
  },
  topTabBtn: {
    minHeight: 36, padding: '0 18px', border: 'none', borderRadius: 8, cursor: 'pointer',
    background: 'transparent', color: dm ? '#7893ad' : '#8095ac',
    fontSize: 13, fontWeight: 700, touchAction: 'manipulation', transition: 'all 0.15s',
  },
  topTabBtnActive: {
    minHeight: 36, padding: '0 18px', border: 'none', borderRadius: 8, cursor: 'pointer',
    background: dm ? 'rgba(23,163,142,0.14)' : 'rgba(15,126,200,0.09)',
    color: dm ? '#7dd3c0' : '#0f7ec8',
    fontSize: 13, fontWeight: 800, touchAction: 'manipulation', transition: 'all 0.15s',
    boxShadow: `inset 0 -2px 0 ${dm ? '#17a38e' : '#0f7ec8'}`,
  },

  // Corpo: sidebar + mapa
  body: { display: 'flex', flex: 1, minHeight: 0, position: 'relative' },
  mapWrap: { flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' },

  sidebar: {
    width: 380, flexShrink: 0,
    background: dm ? '#141f2c' : '#ffffff',
    borderRight: `1px solid ${dm ? '#1f2c3d' : '#e4eaf1'}`,
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  sidebarScroll: { flex: 1, overflowY: 'auto', padding: '16px 14px' },
  sidebarBlock: { marginBottom: 4 },
  sidebarLabel: {
    fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8,
    color: dm ? '#5f7a97' : '#8095ac', marginBottom: 10,
  },
  sidebarDivider: { height: 1, background: dm ? '#1f2c3d' : '#eef1f6', margin: '16px 0' },

  controlesMobile: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
    background: dm ? '#17222f' : '#f6f9fc',
    borderBottom: `1px solid ${dm ? '#1f2c3d' : '#e4eaf1'}`,
    flexShrink: 0, flexWrap: 'nowrap', overflowX: 'auto',
  },
  hint: { fontSize: 12, color: dm ? '#9fb3c8' : '#5f7186', flex: 1, whiteSpace: 'nowrap' },
  hintBox: {
    fontSize: 12, fontWeight: 600, color: dm ? '#ffcf8a' : '#8a5a00',
    background: dm ? 'rgba(255,180,60,0.1)' : '#fff3e0',
    border: `1px solid ${dm ? 'rgba(255,180,60,0.28)' : '#ffe0a3'}`,
    borderRadius: 9, padding: '8px 10px',
  },

  // Controles de polígono (sidebar)
  polyControlStack: { display: 'flex', flexDirection: 'column', gap: 8 },
  polyControlRow: { display: 'flex', gap: 8 },
  polySummary: {
    minHeight: 48, padding: '7px 8px 7px 10px',
    border: `1px solid ${dm ? 'rgba(23,163,142,0.28)' : 'rgba(23,163,142,0.2)'}`,
    borderRadius: 10, background: dm ? 'rgba(23,163,142,0.09)' : 'rgba(23,163,142,0.06)',
    display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr) auto', alignItems: 'center', gap: 8,
  },
  polySummaryIcon: {
    width: 26, height: 26, borderRadius: 8, color: '#fff', background: '#17a38e',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900,
  },
  polySummaryCopy: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  polySummaryTitle: { color: dm ? '#dcebf3' : '#183f55', fontSize: 12, lineHeight: 1.2 },
  polySummaryMeta: { color: dm ? '#7898a8' : '#718b9a', fontSize: 10.5, lineHeight: 1.2 },
  polySummaryButton: {
    minHeight: 32, padding: '0 9px', border: `1px solid ${dm ? '#2f5667' : '#c8dce7'}`,
    borderRadius: 8, color: dm ? '#8ddbc9' : '#0f7386', background: dm ? '#193342' : '#fff',
    cursor: 'pointer', fontSize: 11, fontWeight: 750,
  },
  polyTools: {
    padding: 8, border: `1px solid ${dm ? '#26394b' : '#e2eaf0'}`, borderRadius: 10,
    background: dm ? '#111c28' : '#f8fbfd', display: 'flex', flexDirection: 'column', gap: 7,
  },

  // Botões
  btnPrimary: { minHeight: 42, padding: '0 16px', background: ACCENT, color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13.5, fontWeight: 700, boxShadow: '0 4px 12px rgba(15,126,200,0.28)', touchAction: 'manipulation', width: '100%' },
  btnSuccess: { minHeight: 42, padding: '0 14px', background: 'linear-gradient(135deg, #34a853, #1e7e34)', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13.5, fontWeight: 700, boxShadow: '0 3px 10px rgba(30,126,52,0.3)', touchAction: 'manipulation', flex: 1 },
  btnDanger: { minHeight: 42, padding: '0 14px', background: dm ? '#241417' : '#fff', color: '#e5484d', border: '1.5px solid #e5484d', borderRadius: 9, cursor: 'pointer', fontSize: 13.5, fontWeight: 700, touchAction: 'manipulation', flex: 1 },
  btnDangerGhost: { minHeight: 40, padding: '0 14px', background: 'transparent', color: dm ? '#f38b8e' : '#c0392b', border: `1.5px dashed ${dm ? '#5a2c2f' : '#f0c6c2'}`, borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 600, touchAction: 'manipulation' },
  btnSave: { minHeight: 42, padding: '0 16px', background: ACCENT, color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13.5, fontWeight: 700, boxShadow: '0 4px 12px rgba(15,126,200,0.28)', touchAction: 'manipulation', width: '100%' },
  btnToggleOn: { minHeight: 40, padding: '0 12px', background: dm ? '#152a38' : '#eaf6ff', color: dm ? '#5fd0ff' : '#0f7ec8', border: `1.5px solid ${dm ? '#1c5b7a' : '#bfe3fb'}`, borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700, touchAction: 'manipulation', flex: 1 },
  btnToggleOff: { minHeight: 40, padding: '0 12px', background: dm ? '#182430' : '#eef1f5', color: dm ? '#7b8ea3' : '#8896a6', border: `1.5px solid ${dm ? '#263647' : '#e2e7ee'}`, borderRadius: 9, cursor: 'pointer', fontSize: 13, touchAction: 'manipulation', flex: 1 },
  btnBuscar: { minHeight: 42, padding: '0 16px', background: 'linear-gradient(135deg, #34a853, #1e7e34)', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13.5, fontWeight: 700, boxShadow: '0 3px 10px rgba(30,126,52,0.28)', touchAction: 'manipulation', width: '100%', marginTop: 4 },
  btnMini: { minHeight: 32, padding: '0 10px', background: dm ? '#152a38' : '#eaf6ff', color: dm ? '#5fd0ff' : '#0f7ec8', border: `1px solid ${dm ? '#1c5b7a' : '#bfe3fb'}`, borderRadius: 7, cursor: 'pointer', fontSize: 11.5, fontWeight: 700, touchAction: 'manipulation' },
  btnPlantio: {
    alignSelf: 'stretch', marginTop: 2,
    padding: '7px 10px', borderRadius: 8, fontSize: 11.5, fontWeight: 700,
    border: `1px solid ${dm ? '#2c4a30' : '#a5d6a7'}`, background: dm ? 'rgba(76,175,80,0.1)' : '#f1f8e9',
    color: dm ? '#8bd68e' : '#2e7d32', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  msgOk: { fontSize: 12.5, color: '#2e7d32', fontWeight: 700 },

  // Mapa
  mapaContainer: { position: 'relative', flex: 1, minHeight: 260, overflow: 'hidden' },

  // Filtros (card único usado em sidebar e bottom sheet)
  filtroCard: {
    background: dm ? '#182430' : '#f6f9fc',
    border: `1px solid ${dm ? '#243547' : '#e4eaf1'}`,
    borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 10,
  },
  filtroGridRow: { display: 'flex', gap: 8 },
  campo: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 },
  label: { fontSize: 10.5, color: dm ? '#7893ad' : '#78909c', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 },
  secLabel: { fontSize: 12, color: dm ? '#7dd3c0' : '#0f4c7c', fontWeight: 800 },
  input: {
    minHeight: 38, padding: '8px 10px',
    border: `1px solid ${dm ? '#2b3d51' : '#d6e1ec'}`,
    borderRadius: 8, fontSize: 13.5,
    color: dm ? '#e0e0e0' : '#1a2a4a', outline: 'none',
    background: dm ? '#101a26' : '#fff', width: '100%', boxSizing: 'border-box',
  },
  slider: { width: '100%', accentColor: '#17a38e', cursor: 'pointer' },

  // Erros
  erro: { color: '#e5484d', fontSize: 12.5, margin: '10px 0 0', padding: '8px 10px', background: dm ? 'rgba(229,72,77,0.1)' : '#fdecec', borderRadius: 8 },
  erroTile: { color: dm ? '#ffb74d' : '#a15c00', fontSize: 12.5, margin: '10px 0 0', background: dm ? 'rgba(255,180,60,0.08)' : '#fff3e0', padding: '8px 10px', borderRadius: 8, border: `1px solid ${dm ? 'rgba(255,180,60,0.25)' : '#ffe0a3'}` },

  // Resultados (cenas)
  resultHeader: { marginTop: 14, marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  sidebarTabbar: {
    display: 'flex', gap: 4, marginTop: 12, marginBottom: 10,
    background: dm ? '#101a26' : '#eef1f5', borderRadius: 9, padding: 3,
  },
  sidebarTabBtn: {
    flex: 1, minHeight: 34, border: 'none', borderRadius: 7, cursor: 'pointer',
    background: 'transparent', color: dm ? '#7893ad' : '#8095ac',
    fontSize: 12.5, fontWeight: 700, touchAction: 'manipulation', transition: 'all 0.15s',
  },
  sidebarTabBtnActive: {
    flex: 1, minHeight: 34, border: 'none', borderRadius: 7, cursor: 'pointer',
    background: dm ? '#1b2735' : '#ffffff', color: dm ? '#e7eef7' : '#0f172a',
    fontSize: 12.5, fontWeight: 800, boxShadow: dm ? '0 2px 6px rgba(0,0,0,0.3)' : '0 2px 6px rgba(15,23,42,0.12)',
    touchAction: 'manipulation', transition: 'all 0.15s',
  },
  listaCenas: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 },
  cenaRowWrap: { position: 'relative', display: 'flex' },
  cenaItem: {
    flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '9px 30px 9px 12px', background: dm ? '#182430' : '#f6f9fc',
    border: `1px solid ${dm ? '#243547' : '#e4eaf1'}`, borderRadius: 9,
    cursor: 'pointer', fontSize: 12.5, color: dm ? '#dbe6f1' : '#1a2a4a', touchAction: 'manipulation',
  },
  cenaAtiva: {
    flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '9px 30px 9px 12px', background: ACCENT,
    border: 'none', borderRadius: 9,
    cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#fff',
    boxShadow: '0 3px 10px rgba(15,126,200,0.3)', touchAction: 'manipulation',
  },
  cenaNuvem: { opacity: 0.85, fontWeight: 600 },
  cenaRemoveBtn: {
    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
    border: 'none', borderRadius: '50%', width: 18, height: 18, padding: 0,
    cursor: 'pointer', fontSize: 12, lineHeight: '18px', textAlign: 'center', touchAction: 'manipulation',
  },

  // Camadas (RGB/CIR/NDVI)
  camadas: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap', marginTop: 4 },
  camadaWrap: { position: 'relative', display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 },
  camadaBtnBase: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
    minHeight: 36, width: '100%', paddingLeft: 10, paddingRight: 22,
    borderRadius: 9, cursor: 'pointer', fontSize: 12,
    touchAction: 'manipulation', transition: 'all 0.15s',
  },
  camadaToggle: {
    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
    border: 'none', borderRadius: '50%', width: 15, height: 15, padding: 0,
    cursor: 'pointer', fontSize: 10.5, lineHeight: '15px', textAlign: 'center', touchAction: 'manipulation',
  },

  // Relatório PDF
  btnRelatorio: { minHeight: 42, marginTop: 12, padding: '0 14px', background: 'linear-gradient(135deg, #0f4c7c, #0f7ec8)', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, display: 'block', width: '100%', boxShadow: '0 4px 12px rgba(15,76,124,0.3)', touchAction: 'manipulation' },

  // Legenda NDVI
  legendaBox: { marginTop: 12, padding: '10px 12px', background: dm ? 'rgba(76,175,80,0.08)' : '#f1f8e9', border: `1px solid ${dm ? '#2c4a30' : '#c5e1a5'}`, borderRadius: 10 },
  legendaLabel: { display: 'block', fontSize: 10.5, fontWeight: 800, color: dm ? '#8bd68e' : '#33691e', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 },
  legendaBar: { height: 12, borderRadius: 6, background: 'linear-gradient(to right, #d73027, #fee08b, #1a9850)', marginBottom: 6 },
  legendaTextos: { display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: dm ? '#9fb3c8' : '#5f7186' },

  // Variabilidade / zonas
  varBox: { marginTop: 14, padding: '12px', background: dm ? '#182430' : '#f6f9fc', border: `1px solid ${dm ? '#243547' : '#e4eaf1'}`, borderRadius: 12 },
  varBoxHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' },
  mutedHint: { fontSize: 11.5, color: dm ? '#7893ad' : '#78909c', margin: '8px 0 0', lineHeight: 1.5 },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 10 },
  statCard: { background: dm ? '#101a26' : '#fff', border: `1px solid ${dm ? '#243547' : '#e4eaf1'}`, borderRadius: 8, padding: '8px 10px' },
  statLabel: { fontSize: 9.5, color: dm ? '#7893ad' : '#78909c', fontWeight: 700, textTransform: 'uppercase' },
  statValor: { fontSize: 14, fontWeight: 800, color: dm ? '#e0e0e0' : '#0f172a', marginTop: 2 },
  temporalBox: { marginBottom: 12, padding: '11px', border: `1px solid ${dm ? '#28505b' : '#cfe5e5'}`, borderRadius: 10, background: dm ? 'rgba(15,126,126,0.08)' : '#f4fbfa' },
  temporalHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 },
  temporalTitle: { display: 'block', marginTop: 2, color: dm ? '#e0eef0' : '#173b44', fontSize: 12.5 },
  temporalBadge: { flexShrink: 0, padding: '5px 8px', borderRadius: 999, fontSize: 11.5, fontWeight: 800 },
  temporalPeriod: { margin: '8px 0', color: dm ? '#9fc6cc' : '#52777d', fontSize: 10.5, fontWeight: 700 },
  temporalGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 7 },
  temporalMetric: { display: 'flex', minWidth: 0, flexDirection: 'column', padding: '7px 8px', border: `1px solid ${dm ? '#25434b' : '#dbeaea'}`, borderRadius: 8, background: dm ? 'rgba(9,25,34,0.7)' : '#fff' },
  temporalNote: { margin: '8px 0 0', color: dm ? '#9bb2bd' : '#637d86', fontSize: 10.5, lineHeight: 1.45 },
  vigorBar: { display: 'flex', height: 18, borderRadius: 6, overflow: 'hidden' },
  vigorLegend: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: dm ? '#b7c4d1' : '#555', marginTop: 6, flexWrap: 'wrap', gap: 6 },
  interpretacaoBox: { marginTop: 10, padding: '8px 10px', borderRadius: 8, background: dm ? 'rgba(255,180,60,0.08)' : '#fff8e1' },
  interpretacaoTexto: { fontSize: 11.5, color: dm ? '#c3cedb' : '#546e7a', margin: '4px 0 0' },
  dotSwatch: { width: 10, height: 10, borderRadius: 3, display: 'inline-block', marginRight: 5, verticalAlign: 'middle' },

  // Mobile: bottom sheet acionado pelas ferramentas do contexto selecionado
  sheetBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9998 },
  bottomSheet: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 9999,
    maxHeight: '75vh', display: 'flex', flexDirection: 'column',
    background: dm ? '#141f2c' : '#ffffff',
    borderTopLeftRadius: 18, borderTopRightRadius: 18,
    boxShadow: '0 -12px 32px rgba(0,0,0,0.28)', overflow: 'hidden',
  },
  sheetHandleBar: { position: 'relative', padding: '10px 52px 6px', textAlign: 'center' },
  painelHandle: { display: 'block', width: 36, height: 4, borderRadius: 99, background: dm ? '#334357' : '#d5dee7', margin: '0 auto 8px' },
  sheetTitle: { fontSize: 14, fontWeight: 800, color: dm ? '#e7eef7' : '#0f172a' },
  sheetClose: {
    position: 'absolute', top: 8, right: 12, width: 36, height: 36,
    display: 'grid', placeItems: 'center', border: 0, borderRadius: 10,
    background: dm ? '#223043' : '#edf3f7', color: dm ? '#e7eef7' : '#183246',
    fontSize: 24, lineHeight: 1, fontWeight: 500, cursor: 'pointer',
  },
  sheetScroll: { flex: 1, overflowY: 'auto', padding: '4px 16px 20px' },
};
return s;
}
